/* eslint-disable no-template-curly-in-string */
// ════════════════════════════════════════════════════════════════
// MYRA_MESSAGES.js
// All Myra-facing text, organized by flow step.
// Reading top-to-bottom mirrors the GSA flow.
//
// NODE SHAPES (resolver dispatches on shape):
//   Shape 1 — Plain string. Mode/flow ignored by resolver.
//       key: "text"
//
//   Shape 2 — Voice/chat split only.
//       key: { voice: "...", chat: "..." }
//
//   Shape 3 — Full flow split (used widely in announcement keys).
//       key: {
//         app: { voice: "...", chat: "..." },
//         gsa: {
//           test1: { voice: "...", chat: "..." },
//           test2: { voice: "...", chat: "..." }
//         }
//       }
//
// Empty string "" is the signal for "use fallback" — voice and chat
// resolve independently. Resolver order for flow keys:
//   gsa.test2[mode] → gsa.test1[mode] → app[mode]
//
// DOCTRINE:
//   - GSA and in-app are kept as separate strings even when identical
//     today, so future divergence doesn't require restructuring.
//   - Voice/chat split is used only where the source actually has
//     different copy. Where source is one string for both modes,
//     the same string is duplicated into voice and chat with a
//     ⚑ FLAG comment.
// ════════════════════════════════════════════════════════════════

export const MYRA_MESSAGES = {

  // ── 1. INTRO ──────────────────────────────────────────────────
  intro: {
    welcome: window.twelcome ? window.twelcome : "Myra, your AI recovery coach, uses physical therapy principles to create a plan designed specifically for you.", // batch: 1 (pageLoad)
    readyPrompt: window.tready ? window.tready : "She asks a few questions and guides you through a simple movement. To meet Myra and get started, click the button below that best matches your pain area.", // batch: 1 (pageLoad)

    // CTA response: "Who is Myra?"
    whoIsMyra: "I’m Myra, your recovery coach from Virdio Health. I’m powered by AI and designed to guide you through a recovery process based on best practices in physical therapy. My goal is to help you feel better by creating a plan tailored to your needs.\n\nI work alongside doctors of physical therapy and evidence-based research to support your recovery at home. While I’m not a doctor or clinician, I’m here to make this process easier and answer any questions you have.", // batch: 1 (pageLoad)

    // CTA response: "What's the Assessment?"
    whatsAssessment: "Here's what we'll do together — about 10 minutes total. Tap any step to learn more.", // batch: 1 (pageLoad)

    // Accordion step descriptions (for "What's the Assessment?" expanded view)
    assessmentSteps: [
      { title: "A few questions about your pain", body: "I'll ask where your pain started and a few targeted questions about what it feels like — sharp, dull, radiating. This helps me understand what's going on so I can pick the right movement checks for you." },
      { title: "A quick safety check", body: "Before any movements, I'll run through a short checklist to make sure it's safe to continue. If anything comes up, I'll recommend you see a provider first — your safety always comes first." },
      { title: "1–2 simple movement checks", body: "I'll pick 1 or 2 gentle movements based on your answers. I'll show you a demo video first, then guide you through it. The goal is never to push through pain — it's to help me understand your starting point." },
      { title: "A few optional personal details", body: "After the movement checks, you can share details like your age, activity level, and goals. The more I know, the more accurate your plan. If you skip this, I'll still build a plan with conservative assumptions." },
      { title: "Your personalized recovery plan", body: "Based on everything I've learned, I'll create a recovery plan with exercises tailored to you — including what to do, how often, and why each exercise was chosen." },
    ], // batch: 1 (pageLoad)

    // ── Intro CTA/button labels ────────────────────────────────
    ctaLabels: {
      start: "Let's Build My Free Exercise Plan",
      startMyAssessment: "Start My Assessment",
    },

    // Inline intent responses (when user types freely at intro)
    areaDetected: "Thanks for sharing that. I'll keep that in mind as we go. Let me start by confirming where your pain began.",
    noAreaDetected: "Thanks for sharing that — I'll keep it in mind as we go through the assessment. Let me know when you're ready to start.",
    introOpenReminder: "This assessment helps me understand your pain so I can build a plan specific to you. It takes about 10 minutes. Let me know when you're ready.",
  },

  // ── 2. PROBLEM AREA ───────────────────────────────────────────
  problemArea: {
    prompt: "Can you start by telling me where you are feeling pain? <span class=\"promptPhrase\" data-phrase=\"start\">First, tap where the pain started.</span>",
    selectionConfirm: "{area} —  got it. Now let’s pinpoint where exactly you’re feeling this.",
    noSelectionPrompt: "Please make your selection before we can move to the next screen.", // batch: 1 (pageLoad)
    whereHurtsLabel: "Where are you feeling pain?",
    optionsById: {
      LBP: { label: "Lower back" },
      HIP: { label: "Hip" },
      THIGH: { label: "Thigh" },
      KNEE: { label: "Knee" },
    },

    // ── Area-aware acknowledgements (added) ──────────────────────
    // Spoken right after the user picks their primary problem area.
    // Provides a per-area "you're not alone / this is well understood"
    // beat before transitioning into the next question.
    ackByArea: {
      LBP: "Lower back pain is something so many people deal with, and finding the right kind of exercises can make all the difference.", // batch: 1 (pageLoad)
      HIP: "Hip pain often gets brushed off, but once we see the pattern, it’s usually clear what your hip needs to feel better.", // batch: 1 (pageLoad)
      THIGH: "Thigh pain can be tricky because where you feel it and where it’s coming from aren’t always the same—but we’ll figure it out together.", // batch: 1 (pageLoad)
      KNEE: "Knee pain is one of the most well-studied areas in recovery, so there’s strong evidence to guide what your knee needs.", // batch: 1 (pageLoad)
    },

    autoResponse: {
      LBP: "Hi, I'm Myra. I'm sorry your lower back hurts. Let's figure this out together.\n\nI work from a library of more than 100 stretches and exercises, and I use physical therapy principles to find the ones that actually fit your back.\n\nTo start, tell me a little about what's been going on with your lower back lately.", // batch: 1 (pageLoad)
      HIP: "Hi, I'm Myra. I'm sorry your hip hurts. Let's figure this out together.\n\nI work from a library of more than 65 stretches and exercises, and I use physical therapy principles to find the ones that actually fit your back.\n\nTo start, tell me a little about what's been going on with your lower back lately.", // batch: 1 (pageLoad)
      THIGH: "Hi, I'm Myra. I'm sorry your thigh hurts. Let's figure this out together.\n\nI work from a library of more than 50 stretches and exercises, and I use physical therapy principles to find the ones that actually fit your back.\n\nTo start, tell me a little about what's been going on with your lower back lately.", // batch: 1 (pageLoad)
      KNEE: "Hi, I'm Myra. I'm sorry your knee hurts. Let's figure this out together.\n\nI work from a library of more than 60 stretches and exercises, and I use physical therapy principles to find the ones that actually fit your back.\n\nTo start, tell me a little about what's been going on with your lower back lately.", // batch: 1 (pageLoad)
    },

    // ── Setup beat into pain-location step (added) ───────────────
    // Bridges from problemArea selection into the painLocation prompt,
    // framing why we need a more specific tap.
    setupBeatByArea: {
      LBP: "To help find the right kind of exercises for your back, let’s start by seeing where you are feeling it.", // batch: 1 (pageLoad)
      HIP: "To figure out what will help your hip feel better, let’s first see exactly where you are feeling it.", // batch: 1 (pageLoad)
      THIGH: "With thigh pain, where you feel it can tell me a lot about where it’s coming from. Let’s take a closer look.", // batch: 1 (pageLoad)
      KNEE: "To figure out what will help your knee, it’s important to see where you are feeling it. Where it lives on the knee tells me a lot about what’s going on.", // batch: 1 (pageLoad)
    },
    PROBLEM_AREA_NUGGET: "Even if it's spread, the spot it began is the one that tells me the most.", // batch: 1 (pageLoad)
    PROBLEM_AREA_QUESTION: "Where did your pain initially start?", // batch: 1 (pageLoad)
    PROBLEM_AREA_SETUP_BEAT: "Glad you decided to take the assessment with me.", // batch: 1 (pageLoad)

    // ── "Coming soon" body-area labels (moved from CONSTANTS.COMING_SOON_OPTIONS) ─
    // Shown as disabled tiles on the problem-area screen for areas not yet supported.
    // Strings live here as a flat array because they have no id/value coupling.
    comingSoonLabels: ["Shoulder", "Elbow", "Ankle", "Wrist", "Neck", "Flexibility"],
  },

  // ── 3. PINPOINT PAIN ──────────────────────────────────────────
  painLocation: {
    prompt: "Show me where you're feeling it.",
    confirmFew: "{zones} — got it. Now I need to do a quick safety check before we continue.",
    confirmMany: "Got it — {count} areas noted. Now I need to do a quick safety check before we continue.",
    noSelectionPrompt: "Please select at least one area before we can continue.", // batch: 1 (pageLoad)

    // ── Area-aware question + nugget (added) ─────────────────────
    // The `question` mirrors painLocation.prompt today, but the `nugget`
    // adds a brief educational tip per area. Caller picks based on the
    // area selected at problemArea.
    questionByArea: {
      LBP: {
        question: "Back pain often spreads from where it starts, so tell me everywhere you feel it.", // batch: 1 (pageLoad)
        nugget: "Tell me everywhere you feel pain, from a slight ache to the worst pain.", // batch: 1 (pageLoad)
      },
      HIP: {
        question: "Hip pain can spread from where it starts, so tell me everywhere you feel it.", // batch: 1 (pageLoad)
        nugget: "Tell me everywhere you feel pain, from a slight ache to the worst pain.", // batch: 1 (pageLoad)
      },
      THIGH: {
        question: "Thigh pain can spread from where it starts, so tell me everywhere you feel it.", // batch: 1 (pageLoad)
        nugget: "Tell me everywhere you feel pain, from a slight ache to the worst pain.", // batch: 1 (pageLoad)
      },
      KNEE: {
        question: "Knee pain often spreads from where it starts, so tell me everywhere you feel it.", // batch: 1 (pageLoad)
        nugget: "Tell me everywhere you feel pain, from a slight ache to the worst pain.", // batch: 1 (pageLoad)
      },
    },

    // ── Pattern-validation nuggets (added) ───────────────────────
    // Spoken after the user finishes tapping locations, keyed by the
    // resolved pattern (area + single/multi or specific sub-zones).
    // Reassures the user that their pattern is recognized and signals
    // what the next questions will narrow down.
    validation: {
      LBP_LEG: "Insight: About 6 in 10 people with low back pain feel it in the buttock or leg too, which helps me understand what your back needs.", // batch: 1 (pageLoad)
      LBP_BACK_ONLY: "Insight: When pain stays in the back, recovery often moves a bit faster, which helps me find the right kind of exercises for you.", // batch: 1 (pageLoad)
      HIP_SINGLE: "Insight: Where you feel it tells me a lot about which part of the hip is involved and what your hip needs.", // batch: 1 (pageLoad)
      HIP_MULTI: "Insight: Hip pain in multiple spots often points to the joint itself, helping me sort what your hip needs.", // batch: 1 (pageLoad)
      THIGH_A: "Insight: Thigh pain has a few possible sources, and this helps me narrow down which one.", // batch: 1 (pageLoad)
      THIGH_B1: "Insight: When thigh pain appears with back or buttock pain, the source often isn’t where it hurts. This helps me find the real cause.", // batch: 1 (pageLoad)
      THIGH_B2: "Insight: Thigh pain that stays in the thigh usually points to a muscular source, and this helps me find which one.", // batch: 1 (pageLoad)
      KNEE_SINGLE: "Insight: Where you feel it tells me a lot about what's going on in the knee and what it needs.", // batch: 1 (pageLoad)
      KNEE_MULTI: "Insight: Knee pain that shows up across more than one spot is its own recognized pattern, which helps me sort what your knee needs.", // batch: 1 (pageLoad)
    },
  },

  // ── 4. SAFETY SCREEN ──────────────────────────────────────────
  safety: {
    prompt: "This guided assessment helps identify a starting point for your recovery. It is not a medical diagnosis and does not replace in-person care from a licensed healthcare provider. If you are experiencing a medical emergency, please call 911 or visit your nearest emergency room.",
    followUpIntro: "Thanks for letting me know. I have a couple of follow-up questions to make sure it's safe to continue.", // batch: 1 (pageLoad)
    followUpNext: "One more thing I need to check.",
    clearSeatedOnly: "Good news — we can continue, and I'll keep things to seated movements to be safe.",
    clearNormal: "Good — we're clear to continue.", // batch: 1 (pageLoad)
    SAFETY_SETUP_BEAT: "Before we begin, I need to ask one quick safety question to make sure I picked a safe movement check for you.", // batch: 1 (pageLoad)

    // Question + nugget combined bubble (§F.10).
    // Universal question, body-area-aware italic nugget.
    SAFETY_QUESTION: 'Do you have any pre-existing conditions or symptoms that would make it unsafe to perform at-home exercises?', // batch: 1 (pageLoad)
    SAFETY_NUGGET_BY_AREA: {
      LBP: "e.g. changes in bowel or bladder control, sharp or shooting pain down your legs, numbness or tingling in your legs or feet, or leg weakness or 'giving out.' Let me know if any apply, or if none do.", // batch: 1 (pageLoad)
      HIP: "e.g. changes in bowel or bladder control, shooting leg pain, numbness, or tingling, a childhood hip condition (such as Perthes disease or a slipped hip), avascular necrosis or bone death in your hip, a hip replacement or major hip surgery in the past 6 months, or hip pain that started after a fall or significant injury. Let me know if any apply, or if none do.", // batch: 1 (pageLoad)
      THIGH: "e.g. changes in bowel or bladder control, shooting leg pain, numbness, or tingling, thigh pain that started after a fall, direct hit, or other injury, or leg or thigh surgery in the past 3 months. Let me know if any apply, or if none do.", // batch: 1 (pageLoad)
      KNEE: "e.g. changes in bowel or bladder control, numbness or tingling in your legs or feet, leg weakness, instability, or your leg giving out, knee surgery in the past 3 months, knee pain that started after a fall, twist, or direct injury, or a torn ligament (ACL, PCL, MCL) that has not been treated. Let me know if any apply, or if none do." // batch: 1 (pageLoad)
    },
    placeholder: "Share conditions or symptoms, or reply none",
  },

  // ── 4b. SIDE (conversational only) ─────────────
  // Per-area side question for the conversational landing flow. The
  // structured screens capture side from a left/right body-diagram tap and
  // need no copy; this block exists so the conversational bank has a
  // question to ask when location is given without a side. Weighted per
  // area: light for LBP (side does not change the work), heavier for
  // HIP/THIGH/KNEE (side is a direct input to the plan).
  side: {
    questionByArea: {
      LBP: "And is this on your left side, your right, or both?", // batch: 1 (pageLoad)
      HIP: "Is your hip pain on the left, the right, or both? Since the work trains both sides while we focus on the one that hurts, knowing which side matters here.", // batch: 1 (pageLoad)
      THIGH: "Which side is this on, left, right, or both? It helps me target the right leg as we build your plan.", // batch: 1 (pageLoad)
      KNEE: "Is this your left knee, your right, or both? Knowing the side lets me aim the work where you need it.", // batch: 1 (pageLoad)
    },
  },
  safetyOutcome: {
    advisoryHeading: "Something to consider before continuing", // batch: 1 (pageLoad)
    stoppedFollowUp: "I know this isn't the news you were hoping for. If you have any questions, I'm right here.", // batch: 1 (pageLoad)
    advisoryLeave: "No problem at all. When you're ready to give it a try, I'll be right here.", // batch: 1 (pageLoad)
  },

  // ── HARD STOP ─────────────────────────────────────────────────
  hardStop: {
    safety: {
      empathy: "Your safety is my top priority.", // batch: 1 (pageLoad)
      detail: "Based on what you've shared, I'd strongly recommend seeing a healthcare provider before starting an exercise program. They can evaluate your symptoms and let you know when it's safe to proceed.", // batch: 1 (pageLoad)
    },
    test1Pain: {
      empathy: "I'm glad you told me about the pain.", // batch: 1 (pageLoad)
      detail: "Experiencing pain during that movement tells me we should take a different approach. I'd recommend seeing a healthcare provider who can evaluate things in person and guide your recovery safely.", // batch: 1 (pageLoad)
    },
    couldntFinish: {
      empathy: "No worries at all — that's important information.", // batch: 1 (pageLoad)
      detail: "Not being able to complete the movement tells me this might need a more hands-on approach. I'd recommend seeing a healthcare provider who can work with you directly.", // batch: 1 (pageLoad)
    },
    followUp: "I know this isn't the outcome you were hoping for. If you have any questions about what this means or what to expect from an in-person visit, I'm here — just ask.", // batch: 1 (pageLoad)
  },

  // ── 5. CPG QUESTION / PAIN CHARACTER ──────────────────────────
  cpg: {
    // LBP CPG prompts
    lbpLegQuestion: "When the pain travels into your leg, what does it feel like?", // batch: 1 (pageLoad)
    lbpBackQuestion: "When you move your back around, bend, twist, stand up, what does it feel like?", // batch: 1 (pageLoad)
    // HKT CPG prompts come from CLINICAL_PATHS[area].cpgQuestion.prompt

    // Transition messages after CPG selection
    transitionToSubtype: "Thanks. As I continue to narrow things down, I have another question for you.",
    transitionToSubtypeLbp: "Thanks. Now let me narrow things down a bit more.",
    transitionToPreNprs: "Thanks. Let me ask about your pain level.",
    transitionToPreNprsFromBackOnly: "Thanks. Let me ask about your pain level.",

    // Validation: spoken when user tries to advance with no selection (voice mode).
    noSelectionPrompt: "Please make your selection before we can move to the next screen",

    // ── Area-aware setup beat into the CPG question (added) ──────
    // Spoken right before the CPG question is shown, framing why the
    // "how does it feel" question matters for this specific area.
    setupByArea: {
      LBP: "Let’s talk about how the pain feels. What you’re noticing helps me understand the pattern your back is showing.", // batch: 1 (pageLoad)
      HIP: "Moving on to how the pain feels. The way hip pain shows up often points to where the work needs to start.", // batch: 1 (pageLoad)
      THIGH: "Moving on to how the pain feels. With thigh pain, the quality of what you’re noticing usually points to the source.", // batch: 1 (pageLoad)
      KNEE: "Moving on to how the pain feels. With knee pain, what you’re noticing helps me sort which pattern is in play.", // batch: 1 (pageLoad)
    },

    // ── Educational nuggets shown alongside the CPG question (added) ──
    // For LBP, the leg / back question paths get the same nugget today
    // (kept as separate keys so they can diverge if copy direction changes).
    lbpLegNugget: "In most cases, back pain improves within the first few weeks of the right kind of stretches and exercises.", // batch: 1 (pageLoad)
    lbpBackNugget: "In most cases, back pain improves within the first few weeks of the right kind of stretches and exercises.", // batch: 1 (pageLoad)

    // For non-LBP areas, the nugget is keyed by area.
    nuggetByArea: {
      HIP: "Research shows that targeted exercise often works as well as, or better than, other treatments for hip pain.", // batch: 1 (pageLoad)
      THIGH: "Research shows that thigh pain responds well to targeted recovery, and finding the source is key.", // batch: 1 (pageLoad)
      KNEE: "Research shows that exercise often matches or outperforms surgery or injections for knee pain.", // batch: 1 (pageLoad)
    },

    // For non-LBP areas, the question stem is also keyed by area.
    questionByArea: {
      HIP: "Which of these sounds most like what you're feeling?", // batch: 1 (pageLoad)
      THIGH: "Which of these sounds most like what you're feeling?", // batch: 1 (pageLoad)
      KNEE: "Which of these sounds most like what you're feeling?", // batch: 1 (pageLoad)
    },

    // ── Post-tap educational follow-ups (added) ──────────────────
    // Spoken after the user picks a CPG option. Each key matches the
    // option id from CLINICAL_PATHS — caller looks up by selected option.
    postTap: {
      "cpg-radiating": "This pain often comes from an irritated nerve in your lower back. About 6 in 10 people feel it travel into a leg, and most improve with the right exercises in a week or two.", // batch: 1 (pageLoad)
      "cpg-referred": "That hard-to-pinpoint ache is a real pattern, even if scans don't show much. The work we're doing is built exactly for this kind of pain.", // batch: 1 (pageLoad)
      "cpg-mobility": "That kind of stiffness can be structural, or your body protecting itself from expected pain. It often improves by finding the right movement direction.", // batch: 1 (pageLoad)
      "cpg-coordination": "That give-out feeling is a real pattern that rarely shows on imaging. The work ahead retrains the muscles around your back to stabilize you.", // batch: 1 (pageLoad)
      "hip-cpg-mobility": "This stiffness often makes things like sitting cross-legged or putting on socks tough. It's a recognized pattern, and targeted work usually brings meaningful change in 6 to 12 weeks.", // batch: 1 (pageLoad)
      "hip-cpg-power": "This pattern is often mistakenly called 'bursitis.' Research clearly shows targeted exercise outperforms cortisone injections at both 8 weeks and a full year.", // batch: 1 (pageLoad)
      "hip-cpg-motor": "What you're describing is a recognized pattern that imaging often misses. The good news is it responds well to conservative care.", // batch: 1 (pageLoad)
      "thigh-cpg-muscular": "This ache often points to a specific muscle group. Targeted recovery cuts return-to-activity time by about three weeks compared to rest alone.", // batch: 1 (pageLoad)
      "thigh-cpg-neural": "This nerve-compression pattern is often missed by clinicians. But once the source is named, most cases respond well to conservative care.", // batch: 1 (pageLoad)
      "thigh-cpg-referred": "Pain that seems to come from somewhere else often does. About 1 in 3 people diagnosed with a back issue actually have hip pain, and naming the real source drives recovery.", // batch: 1 (pageLoad)
      "knee-cpg-pfp": "What you're feeling around the kneecap is a well-studied pattern. We'll work on the hip and knee together, which is the most effective combination.", // batch: 1 (pageLoad)
      "knee-cpg-mobility": "For a stiff or unstable joint, we focus on rebuilding controlled motion in stages. Rehab is the first-line treatment, bringing progress in steady increments.", // batch: 1 (pageLoad)
      "knee-cpg-lateral": "What you're feeling on the outside of your knee often traces back up to the hip. The path forward combines hip strengthening with load management.", // batch: 1 (pageLoad)
      "knee-cpg-posterior": "Where you're feeling it has a few possible sources. Our movement check will narrow it down, and recovery usually centers on progressive hamstring loading.", // batch: 1 (pageLoad)
      "knee-cpg-medial": "Research strongly backs this pattern. For inner-knee wear, exercise matches surgical outcomes at one and two years, with none of the surgical risk.", // batch: 1 (pageLoad)
    },

    // ── HKT CPG-question prompts (moved from CONSTANTS.CLINICAL_PATHS[area].cpgQuestion.prompt) ─
    // For LBP, the prompts already live in `lbpLegQuestion` and `lbpBackQuestion` above.
    areaPrompts: {
      HIP: "Which of these best describes your primary hip complaint?",
      THIGH: "Which of these best describes the feeling in your thigh?",
      KNEE: "Which of these best describes where you feel your knee pain?",
    },

    // ── CPG option labels & info (moved from CONSTANTS.CPG_OPTIONS_LEG, CPG_OPTIONS_BACK,
    //     and CONSTANTS.CLINICAL_PATHS[area].cpgQuestion.options) ─
    // Pattern A lookup: components iterate the option array from CONSTANTS
    // (carries id + value + cpgForBackend + routesTo + ...) and look up
    // display copy here by id.
    options: {
      // LBP — CPG_OPTIONS_LEG
      "cpg-radiating": { label: "Sharp and shooting", info: "A narrow line traveling down the leg, like a wire running through it." },
      "cpg-referred": { label: "Dull and spread out", info: "A deep ache spread through the leg, hard to point to one spot." },
      // LBP — CPG_OPTIONS_BACK
      "cpg-mobility": { label: "Stiff and restricted", info: "A deep ache and restricted movement, about half of all back-only pain." },
      "cpg-coordination": { label: "Unstable or weak", info: "Like the back might give out, weakness more than stiffness." },

      // HIP — CLINICAL_PATHS.HIP.cpgQuestion.options
      "hip-cpg-mobility": { label: "Stiff or tight", info: "Hard to put on socks, get out of low chairs, sit cross-legged. Movement-pattern, not weakness." },
      "hip-cpg-power": { label: "Weak or unsteady", info: "Often dismissed as bursitis for years. Targeted exercise outperforms cortisone injections at one year for this." },
      "hip-cpg-motor": { label: "Pinching or catching", info: "Up to 60% of these are misdiagnosed at first because imaging often looks normal. The pattern itself is well-recognized." },

      // THIGH — CLINICAL_PATHS.THIGH.cpgQuestion.options
      "thigh-cpg-muscular": { label: "A muscular ache or pull", info: "Maybe a pop moment you remember, or a slow ache from training. The body suddenly not cooperating." },
      "thigh-cpg-neural": { label: "Burning or tingling", info: "Often called the elusive diagnosis. Many clinicians do not recognize this nerve-compression pattern." },
      "thigh-cpg-referred": { label: "A deep ache from somewhere else", info: "About 20% of people first told they had a lumbar issue actually had hip-related pain referring down." },

      // KNEE — CLINICAL_PATHS.KNEE.cpgQuestion.options
      "knee-cpg-pfp": { label: "Around or behind the kneecap", info: "If you've been told just rest, it'll pass, that advice does not match the evidence for this pattern." },
      "knee-cpg-mobility": { label: "Stiff, blocked, or unsteady", info: "When the joint feels untrustworthy, slipping, locking, or just not steady, even between bad moments." },
      "knee-cpg-lateral": { label: "On the outer side", info: "Most cases recover within about 6 weeks, usually through hip strengthening and modified loading." },
      "knee-cpg-posterior": { label: "Behind the knee", info: "Has multiple possible sources. Most respond to conservative care." },
      "knee-cpg-medial": { label: "On the inner side", info: "Multiple landmark trials show exercise therapy matches arthroscopic surgery for degenerative tears." },
    },
  },

  // ── 6. SUBTYPE Q1 / PAIN PATTERN ──────────────────────────────
  subtypeQ1: {
    prompt: "Which position best describes your lower back pain?", // batch: 1 (pageLoad)
    promptWithPreselect: "Based on what you shared earlier, it sounds like this might apply — does it still sound right?", // batch: 1 (pageLoad)
    transitionToQ2: "One final question about this.",

    // Validation: spoken when user tries to advance with no selection (voice mode).
    noSelectionPrompt: "Please make your selection before we can move to the next screen",

    // ── Area-aware setup beat into the subtype question (added) ──
    setupByArea: {
      LBP: "Continuing on how you feel, let’s see what changes your pain. How you move helps me find the right exercises for you.", // batch: 1 (pageLoad)
      HIP: "Continuing on how you feel, let’s see how this shows up in your day. Knowing what triggers it helps me narrow down the exercises.", // batch: 1 (pageLoad)
      THIGH: "Continuing on how you feel, let’s find exactly where your thigh hurts. The exact spot helps me target the right exercises.", // batch: 1 (pageLoad)
      KNEE: "Continuing on how you feel, let’s see when this pain flares up. Your knee’s patterns help me narrow down the right exercises.", // batch: 1 (pageLoad)
    },

    // Reference template — quotes back the user's earlier pain-location
    // selection inside the subtype prompt. {referencePhrase} is built by
    // the caller from the painLocation taps.
    referenceTemplate: "You told me you feel it in {referencePhrase}.", // batch: 1 (pageLoad)

    // LBP-specific question + nugget (the area-aware sets cover HIP/THIGH/KNEE).
    lbpQuestion: "What tends to make it worse?", // batch: 1 (pageLoad)
    lbpNugget: "Pain often gets worse with certain movements. We want to avoid those.", // batch: 1 (pageLoad)

    // ── Educational nuggets keyed by area + question index (added) ──
    // Some areas have multiple subtype questions; index disambiguates.
    nuggetByAreaAndIndex: {
      HIP_0: "Where the tightness lives tells me which muscles to focus on.", // batch: 1 (pageLoad)
      HIP_1: "Which kind of movement reproduces the weakness tells me where to start.", // batch: 1 (pageLoad)
      THIGH_0: "Where in your thigh you feel it tells me which muscles are involved.", // batch: 1 (pageLoad)
      KNEE_0: "When the pain shows up tells me how the joint is being loaded.", // batch: 1 (pageLoad)
      KNEE_1: "Whether the knee feels stuck or untrustworthy points to different things to work on.", // batch: 1 (pageLoad)
    },

    // ── Post-tap educational follow-ups (added) ──────────────────
    // Spoken after the user picks a subtype option. Keys match the
    // option ids from CLINICAL_PATHS / Q1_MAP. The q1a/q1b/q1c/q1d/q1e
    // keys are LBP; the rest are HIP / THIGH / KNEE sub-patterns.
    postTap: {
      // LBP subtypes
      q1a: "This well-recognized pattern usually responds to exercises in your preferred direction, plus small tweaks to how you sit, drive, and bend.", // batch: 1 (pageLoad)
      q1b: "Feeling better when sitting or leaning forward is a key clue. The exercises that work best for this pattern use that same direction.", // batch: 1 (pageLoad)
      q1e: "Twisting pain affects about 1 in 5 people with back pain. It's a well-understood pattern that requires its own unique exercises.", // batch: 1 (pageLoad)
      q1c: "When pain lacks a clear direction, we focus on gently rebuilding your tolerance for movement itself. Same destination, different path.", // batch: 1 (pageLoad)
      q1d: "A lot of people land here. The upcoming movement checks will give us a clear picture so we can learn what your back needs together.", // batch: 1 (pageLoad)

      // HIP subtypes
      "hip-sub-lateral": "The things that aggravate it, like side-lying or low chairs, are exactly where we start strengthening. Research shows exercise outperforms cortisone at one year.", // batch: 1 (pageLoad)
      "hip-sub-anterior": "What you're feeling at the front of your hip is a recognized pattern that imaging often misses. We'll work on reducing load in painful positions and strengthening the muscles that protect the joint.", // batch: 1 (pageLoad)
      "hip-sub-posterior": "Tightness here usually means the muscle is tensing from weakness, not asking for a stretch. That is good news—strength is something we can build together.", // batch: 1 (pageLoad)
      "hip-sub-rotational": "This rotational pattern is a known clinical signature. Improving capsule mobility and rotational strength is the clear path forward.", // batch: 1 (pageLoad)
      "hip-sub-neuro": "Pain when sitting is the main signal for this pattern. It is well-known and responds very well to progressive loading and slight sitting tweaks.", // batch: 1 (pageLoad)
      "hip-sub-global": "When pain spreads across the whole joint, exercise reduces symptoms just as well as NSAIDs. We will work with your body, not against it.", // batch: 1 (pageLoad)
      "hip-sub-adductor": "This pattern has incredible research backing. A specific routine done over 6 to 8 weeks cuts adductor injury rates by over 75%.", // batch: 1 (pageLoad)
      "hip-sub-abduction": "Nothing is broken here. The muscle that keeps your pelvis level just needs to get stronger, which we can fix with targeted strengthening.", // batch: 1 (pageLoad)

      // THIGH subtypes
      "thigh-sub-anterior": "This responds well to a structured plan: pain control first, then rebuilding your quadriceps' tolerance. Most people return to activity in about 6 weeks.", // batch: 1 (pageLoad)
      "thigh-sub-medial": "Inner-thigh pain has a clear signature, especially pain when squeezing your heels together. It responds very well to specific progressive loading.", // batch: 1 (pageLoad)
      "thigh-sub-posterior": "Whatever triggered this hamstring pain, the path forward is the same: progressive loading built up over a few weeks.", // batch: 1 (pageLoad)
      "thigh-sub-lateral": "What you're feeling on the outside of your thigh is connected to the hip muscles in the same chain. We'll work there, and most cases respond well to strengthening and load management.", // batch: 1 (pageLoad)

      // KNEE subtypes
      "knee-sub-valgus": "When squats and stairs are the trigger, it's usually about how the load travels through your hip and knee. We'll work on hip and quad strengthening while gradually rebuilding range of motion.", // batch: 1 (pageLoad)
      "knee-sub-maltrack": "About half of knee-pain users have this pattern, triggered by sitting with bent knees. Hip and knee strengthening is highly effective here.", // batch: 1 (pageLoad)
      "knee-sub-tendon": "You do not have to be an athlete to have this. There is strong evidence that combined hip and quad strengthening makes a big difference.", // batch: 1 (pageLoad)
      "knee-sub-stiff": "This stiffness responds well to gentle, progressive movement. We will work with the rhythms your body already shows us, not against them.", // batch: 1 (pageLoad)
      "knee-sub-unstable": "That giving-way feeling is a real pattern. Quad strength and balance work will help you get steadier by gradually doing more of what feels unsafe.", // batch: 1 (pageLoad)
    },

    // ── Option labels & info (moved from CONSTANTS.Q1_OPTIONS and CONSTANTS.CLINICAL_PATHS.*.subtypeQuestions[].options) ─
    // Pattern A lookup: components iterate the option array from CONSTANTS
    // (which carries id + value + routing fields) and look up display copy
    // here by id. Keys match exactly the option ids in CONSTANTS.
    options: {
      // LBP — Q1_OPTIONS
      q1a: { label: "It hurts when I sit", info: "Probably already shifting in your chair, getting up more, dreading long meetings. That's the pattern." },
      q1b: { label: "It hurts to stand for long", info: "The kind where leaning forward on a counter, cart, or chair feels like the only thing that helps." },
      q1e: { label: "It hurts to twist", info: "Rolling over in bed, getting in or out of the car, looking over your shoulder backing up." },
      q1c: { label: "It hurts no matter what", info: "When no position consistently helps, you're carrying something harder than the others, not failing at it." },
      q1d: { label: "Honestly, I'm not sure", info: "Pain at this depth doesn't always follow neat rules, and noticing that is its own kind of clarity." },

      // HIP — CLINICAL_PATHS.HIP.subtypeQuestions[0].options ("Where do you feel it the most?")
      "hip-sub-lateral": { label: "On the side of my hip, especially when lying on that side or walking.", info: "Pillows under the leg at night, picking which side to sleep on, taking the elevator more. Sound familiar?" },
      "hip-sub-anterior": { label: "In the front of my hip or groin, especially when extending my leg behind me.", info: "Worse with deep chairs, deep squats, getting out of the car. The front of the joint complaining about positions." },
      "hip-sub-posterior": { label: "In my buttock or the back of my hip, especially when bringing my knee towards my chest.", info: "Stretching the hamstring sometimes makes it worse instead of better, and that's not your fault." },
      "hip-sub-rotational": { label: "In my groin or the crease where my leg meets my body, especially with twisting or crossing my legs.", info: "Turning at the kitchen counter, planting your foot to change direction, twisting to load the dishwasher." },
      "hip-sub-neuro": { label: "A specific, deep pain in my buttock, sometimes with tingling down my leg.", info: "Builds with sitting, often hitting hard around the 20 to 30 minute mark. That specific." },
      "hip-sub-global": { label: "A general, deep ache and stiffness all around the joint, worse in the morning.", info: "Stiff in the morning, eases as you start moving, returns through the day, and you've probably reorganized more than you realize." },
      // HIP — CLINICAL_PATHS.HIP.subtypeQuestions[1].options ("Which is most difficult?")
      "hip-sub-adductor": { label: "Moving my legs apart", info: "When you try to squeeze your heels together hard, there's weakness too, not just pain." },
      "hip-sub-abduction": { label: "Standing on one leg or stairs", info: "A sense of the hip dropping or dipping when you walk. This is mostly fixable with strength." },

      // THIGH — CLINICAL_PATHS.THIGH.subtypeQuestions[0].options ("Where do you feel it the most?")
      "thigh-sub-anterior": { label: "Front of my thigh", info: "Either a sharp moment you remember, or a slower loading ache with stairs and standing up. Both count." },
      "thigh-sub-medial": { label: "Inner thigh or groin", info: "Sometimes confused with a hernia, hip flexor strain, or pelvic causes, but it has its own specific signature." },
      "thigh-sub-posterior": { label: "Back of my thigh", info: "Hamstring strain isn't only an athlete's injury. The peak count happens in adults 60 to 64." },
      "thigh-sub-lateral": { label: "Outer side of my thigh", info: "Outer-thigh pain has a few possible sources, the side muscles, the hip chain, or load from nearby joints." },

      // KNEE — CLINICAL_PATHS.KNEE.subtypeQuestions[0].options ("When do you notice it most?")
      "knee-sub-valgus": { label: "Squatting or going downstairs", info: "If you've started taking stairs slower or holding the rail more, that itself is the pattern." },
      "knee-sub-maltrack": { label: "After sitting a while", info: "This pattern has a name. Clinicians often call it the movie sign. That alone tells you it's recognized." },
      "knee-sub-tendon": { label: "Jumping or running", info: "Often called runner's knee, but you do not have to run to get it." },
      // KNEE — CLINICAL_PATHS.KNEE.subtypeQuestions[1].options ("Which describes your knee best?")
      "knee-sub-stiff": { label: "Stiff and limited", info: "Yoyos through the day, better with movement, worse after rest. That fluctuation is the pattern." },
      "knee-sub-unstable": { label: "Unsteady or giving way", info: "27% of people feel this without their knee ever actually buckling. More common than buckling itself." },
    },
  },

  // ── 7. SELF-ASSESSMENT Q2 (LBP) ──────────────────────────────
  q2: {
    // Q2 question text: see `titleByQ1Answer` below (moved from CONSTANTS.Q2_MAP)
    transitionToPreNprs: "Thanks — that helps narrow things down. Now let me ask about your pain level.",

    // ── Q2 prompts keyed by Q1 answer (moved from CONSTANTS.Q2_MAP[*].title) ─
    // Caller uses the Q1 answer as the key. The `name` routing field
    // (q2a, q2b, q2e, q3) stays in CONSTANTS.Q2_MAP.
    titleByQ1Answer: {
      a: "Does standing up straight or gently arching your back help?",
      b: "Does sitting down help?",
      e: "Does avoiding twisting help?",
      c: "Does moving around or changing position help?",
      d: "Does moving around or changing position help?",
    },

    // Validation: spoken when user tries to advance with no selection (voice mode).
    noSelectionPrompt: "Please make your selection before we can move to the next screen",

    // ── Setup beat into Q2 (added) ───────────────────────────────
    transition: "One last question on your pain patterns, and I’ll have a clear idea of which movement check to pick for you.", // batch: 1 (pageLoad)

    // ── Nuggets keyed by Q1 answer (added) ───────────────────────
    // a/b/e share copy today (kept as separate keys so they can diverge);
    // c/d share a different message tuned for "no clear direction" patterns.
    nuggetByQ1: {
      a: "What relieves pain matters as much as what triggers it. Recognizing this pattern helps me choose exercises that match your body's needs.", // batch: 1 (pageLoad)
      b: "What relieves pain matters as much as what triggers it. Recognizing this pattern helps me choose exercises that match your body's needs.", // batch: 1 (pageLoad)
      e: "What relieves pain matters as much as what triggers it. Recognizing this pattern helps me choose exercises that match your body's needs.", // batch: 1 (pageLoad)
      c: "Even when no clear direction stands out, a small relief signal can still point the way. This question helps me find one if it's there.", // batch: 1 (pageLoad)
      d: "Even when no clear direction stands out, a small relief signal can still point the way. This question helps me find one if it's there.", // batch: 1 (pageLoad)
    },

    // ── Post-tap acknowledgement (added) ─────────────────────────
    // Spoken right after the user picks Q2. Generic for now (no per-option
    // branching), but lives in postTap to mirror the pattern in cpg/subtypeQ1.
    postTap: "Got it. Helps me understand your pain better. Now I’m ready to choose the best movement check for you.", // batch: 1 (pageLoad)
  },

  // ── 7b. HKT SUBTYPE QUESTION ─────────────────────────────────
  hktSubtype: {
    // Prompts (moved from CONSTANTS.CLINICAL_PATHS[area].subtypeQuestions[index].prompt).
    // Pattern A lookup: caller computes "{AREA}_{index}" key.
    // ⚑ FLAG: emptied per New file — copy moved to handoff.bridgeToNprs.
    //   Code that still reads this key will get '' until call sites migrate.
    transitionToPreNprs: "",

    // ── Subtype-question prompts (moved from CONSTANTS.CLINICAL_PATHS) ─
    promptByAreaAndIndex: {
      HIP_0: "Where do you feel it the most?",
      HIP_1: "Which is most difficult?",
      THIGH_0: "Where do you feel it the most?",
      KNEE_0: "When do you notice it most?",
      KNEE_1: "Which describes your knee best?",
    },
  },

  // ── 7c. HANDOFF — bridge from classification into movement check ──
  // Spoken after the last subtype/Q2 answer, just before the NPRS pain
  // questions. Wraps up the classification phase with a trust-close
  // beat, optionally a long-term educational beat, and the test-pick.
  //
  // Caller wiring:
  //   1. trustCloseByFlow[`${area}_${q1Fired ? 'q1Fired' : 'q1Skipped'}`]
  //   2. (optional) longTermBeats[area][subPattern]
  //   3. testPickTemplate filled with {testName}
  //   4. bridgeToNprs
  handoff: {
    // ── Trust-close ─────────────────────────────────────────────
    // Per-area reassurance + privacy beat after the classification
    // questions land. Keys are "{AREA}_{q1Fired|q1Skipped}".
    trustCloseByFlow: {
      LBP_q1Fired: "That tells me a lot about what your back needs. Thanks for sharing with me.", // batch: 1 (pageLoad)
      HIP_q1Fired: "That helps me understand what your hip is doing. Thanks for sharing with me.", // batch: 1 (pageLoad)
      HIP_q1Skipped: "That pattern is well-recognized, and what you've shared gives me a clear picture. Thanks for sharing with me.", // batch: 1 (pageLoad)
      THIGH_q1Fired: "That tells me which muscles are involved. Thanks for thinking through these with me.", // batch: 1 (pageLoad)
      THIGH_q1Skipped: "With thigh pain, naming the source is most of the work, and what you've shared has given me a clear starting point. Thanks for sharing with me.", // batch: 1 (pageLoad)
      KNEE_q1Fired: "That tells me a lot about what your knee is doing. Thanks for thinking through these with me.", // batch: 1 (pageLoad)
      KNEE_q1Skipped: "That pattern has a known shape, and what you've shared gives me a clear starting point. Thanks for sharing with me.", // batch: 1 (pageLoad)
    },

    // ── Long-term educational beats ─────────────────────────────
    // Optional long-form copy spoken between the trust-close and the
    // test pick. Keyed by area, then by sub-pattern. THIGH currently
    // shares one default across all sub-patterns; the others split.
    longTermBeats: {
      LBP: {
        leg: "Since your back pain travels to your leg, our goal is to prevent flare-ups and keep you moving comfortably. Let's set up a plan to help you do just that.", // batch: 1 (pageLoad)
        back: "Because your pain stays in your back, the main focus is preventing flare-ups and helping your body move naturally again. We will build a plan to interrupt that pain cycle.", // batch: 1 (pageLoad)
      },
      HIP: {
        mobility: "Since you mentioned hip stiffness, targeted movement is the best way to create lasting relief. Let's take a few more steps to match the right recovery plan exactly to what your body needs.", // batch: 1 (pageLoad)
        power: "Based on your hip pattern, targeted exercise is a highly effective way to build lasting strength. We have a few more steps to match the plan to what your body needs.", // batch: 1 (pageLoad)
        motor: "Because this specific hip pattern can be tricky, finding the exact right starting point is key. Let's build a plan tailored to what you are actually feeling.", // batch: 1 (pageLoad)
      },
      THIGH: {
        default: "To get lasting relief for your thigh pain, we need to pinpoint the exact source. Your plan will target your specific pattern so we do not have to guess.", // batch: 1 (pageLoad)
      },
      KNEE: {
        pfp: "Since you are feeling pain around your kneecap, targeted movement is vital to stop it from lingering. Let's build a plan that creates lasting results.", // batch: 1 (pageLoad)
        stability: "Based on what you shared, rebuilding your controlled motion is the key to recovery. We will use steady, structured steps to help you get there.", // batch: 1 (pageLoad)
        posterior: "For the pain behind your knee, steady work on your hamstrings is the best approach. Next, we will match a recovery plan to what your body needs.", // batch: 1 (pageLoad)
        medial: "Since you have inner-knee pain, targeted exercise is incredibly effective for managing normal wear and tear. Let's build the right recovery plan for you.", // batch: 1 (pageLoad)
        lateral: "Because your outer-knee pain likely traces up to your hip muscles, we need to focus on both areas. We will set up a plan to strengthen your hip and manage the load.", // batch: 1 (pageLoad)
      },
    },

    // ── Test-pick + bridge into NPRS ────────────────────────────
    // testPickTemplate uses {testName} for the chosen movement check.
    // bridgeToNprs is the connector before the pain-rating screen
    // (replaces the old preNprs.transitionToTest).
    testPickTemplate: "I'd like you to try the {testName}.", // batch: 1 (pageLoad)

    // ── Movement reveal — "any questions before we move?" interstitial ─
    // Spoken after the test pick is announced and before the bridge into NPRS.
    // Gives the user a chance to ask questions or tap "I am good for now".
    movementRevealQuestion: "A good time to check if you have any questions or let me know if you're ready for the next step?",
    movementRevealReadyLabel: "I am good for now",
    movementRevealReadyEcho: "I'm good for now.",
    movementRevealGuide: "Or type your questions below",

    bridgeToNprs: "Before we start the movement check, I have a couple of quick questions for you.", // batch: 1 (pageLoad)
  },

  // ── 7d. POST-TAP GENERIC FALLBACKS ───────────────────────────
  // Generic per-field strings used when option-specific postTap lookup
  // fails (e.g., Beacon returned low confidence and no option id matched).
  // One string per captureable bucket field. Frontend resolver order:
  //   option-specific postTap → postTapGeneric[fieldName] → silent
  // Wording is intentionally light and non-specific — these fill the
  // beat without making claims that wouldn't generalize.
  postTapGeneric: {
    cpg: "Okay, I am starting to get a better sense of the pattern now.", // batch: 1 (pageLoad)
    subtypeQ1: "Good. That adds to what I'm seeing.", // batch: 1 (pageLoad)
    subtypeQ2: "Good. The picture is clear now.", // batch: 1 (pageLoad)
    subtypeAnswer: "Okay, that gives me a clearer picture of the pattern.", // batch: 1 (pageLoad)
    painLevel: "Okay, that helps me know where to start your exercises and stretches.", // batch: 1 (pageLoad)
  },

  // ── 8. PRE-NPRS (Pain + Duration) ────────────────────────────
  preNprs: {
    painPrompt: "On a scale of 0 to 10, how would you rate your pain right now?", // batch: 1 (pageLoad)
    durationPromptDefault: "And how long have you been experiencing these symptoms?",
    durationPromptPreselect: "You mentioned how long this has been going on — does this match?",
    // ⚑ FLAG: emptied per New file — copy moved to handoff.testPickTemplate
    //   (uses {testName} the same way). Call sites should migrate.
    transitionToTest: "", // batch: 1 (pageLoad)
    // Voice-only message per spec decision — caller skips in chat mode.
    voiceHeadsUp: "Next, watch this quick demo video for the {testName}. This is just a demo—you will be doing the test in a few minutes, I will guide you.", // batch: 1 (pageLoad)

    noDurationSelectedPrompt: "Please select how long you've been experiencing symptoms.",
    nugget: "0 is no pain at all, 10 is the worst you can imagine. Your number helps me set the right starting point.", // batch: 1 (pageLoad)
    NPRS_SETUP_BEAT: "Your pain level helps me find the right starting point for your recovery.", // batch: 1 (pageLoad)
    NPRS_POST_TAP_NUGGET: "Got it. That’s the piece I needed. We’re just one step away from starting the movement check process.",

    placeholder: "Type a number from 0 to 10",

    // ── Duration option labels (moved from CONSTANTS.DURATION_OPTIONS) ─
    // Pattern A lookup: components iterate DURATION_OPTIONS from CONSTANTS
    // (carries id + value routing: ACUTE / SUBACUTE / CHRONIC) and look up
    // the label here by value. Note: PERSONALIZE_GROUPS[0].fields.duration
    // uses the same option structure with the same labels — same lookup applies.
    durationLabelByValue: {
      ACUTE: "Less than 6 weeks",
      SUBACUTE: "6 weeks to 3 months",
      CHRONIC: "More than 3 months",
    },
  },

  // ── 9. MODE SELECTION (test-time communication preference) ──
  modeSelection: {
    prompt: "Want to go hands-free? You can talk to me while you move",
    talkLabel: "Yes, talk",
    talkDesc: /* PLACEHOLDER COPY */ "Speak your responses out loud for a smoother experience. (Microphone permission required.)",
    chatLabel: "No thanks",
    chatDesc: /* PLACEHOLDER COPY */ "Type your responses directly on your device. Keep in mind, you'll need to walk back to your device each time.",
    talkHelper: "Recommended for this part",
    chatHelper: "Same Plan, Same Flow",
    MODE_SELECTION_CHAT_VALIDITY: "Chat works completely too, same plan, same flow, just a different way to talk. You can also switch later if you change your mind.", // batch: 1 (pageLoad)
    MODE_SELECTION_SETUP_BEAT: "During the movement check, your phone will be a few feet away from you so the camera can see you. That changes how we communicate. I want to give you a choice. Talk is the easier option here, just because your hands are busy with the movement and your phone is a few feet away. You don't have to walk back to type.", // batch: 1 (pageLoad)
    MODE_SELECTION_POST_DECISION: {
      talk: "Got it. The phone will ask for microphone access in a second so we can talk through the test.", // batch: 1 (pageLoad)
      chat: "Got it. We'll keep things in chat." // batch: 1 (pageLoad)
    },
  },

  // ── 9a. DEMO VIDEO / TEST INTRO ──────────────────────────────
  // Plays right before the demo video rolls.
  // GSA: distinguishes first vs second movement check.
  // App: single variant — plan exercises have no "1 and 2" framing.
  demoVideo: {
    intro: {
      app: {
        voice: /* PLACEHOLDER COPY */ "Here’s a quick demo of the {testName}. Watch how it’s done, then you’ll give it a try.",
        chat:  /* PLACEHOLDER COPY */ "Here’s a quick demo of the {testName}. Watch how it’s done, then you’ll give it a try.",
      },
      gsa: {
        test1: {
          voice: /* PLACEHOLDER COPY */ "Let’s start with the {testName}. I’ll show you a quick demo first, then you’ll give it a try.",
          chat:  /* PLACEHOLDER COPY */ "Let’s start with the {testName}. I’ll show you a quick demo first, then you’ll give it a try.",
        },
        test2: {
          // ⚑ FLAG: test2 intro could read differently ("one more movement to try —
          // here's the demo"). Leaving empty to fall back to test1 until you decide.
          voice: "",
          chat: "",
        },
      },
    },
    // Demo-video user-facing labels.
    watchInstructionPrefix: "Watch how to do the",
    watchInstructionSuffix: ".",
    buttonLabels: {
      playDemoVideo: "Play Demo Video",
      continue: "Continue",
      startMovementTest: "Start Movement Check",
      startMovementTest2: "Start Movement Check 2",
      watchedDemoEcho: "Watched {testName} demo video",
      frozenDemoLabel: "Demo:",
    },

    // Voice acknowledgements spoken in response to video-control voice commands.
    // Two surfaces, two slightly different copy sets:
    //   - gsa: short acks used inside the GSA chat shell (SelfAssessment.jsx
    //          TestIntroScreen / TestIntro2Screen — test1 + test2 demo).
    //   - app: longer acks used by the global Myra voice agent that runs on
    //          the main app player (MyraAgent.jsx flow_demo_video listener).
    // Strings are intentionally kept verbatim from each source — including
    // the phonetic "re-zume" / "pauze" spellings, which are TTS hints for
    // the voice synth.
    controls: {
      gsa: {
        skipAck: "Got it! Skipping the video.",
        alreadyPaused: "The video is already paused.",
        pauseAck: "Sure! I'll pause the video...",
        resumeAck: "Sure! I'll resume the video...",
        expandAck: "Sure! I'll expand the video...",
        unrecognized: "I didn't catch that one. Here's what I can help with: Skip, Replay, Pause, Expand",
      },
      app: {
        skipAck: "Got it! Skipping the video.",
        replayAck: "Sure! I'll replay the video for you.",
        pauseAck: "Sure! I’ll pause the video for you. When you’re ready to continue, just say Myra, resume video.",
        resumeAck: "Sure! I'll re-zume the video for you.",
        // INVALID_COMMAND handler — user said something unrecognized.
        unrecognized: "I didn't catch that one. Here's what I can help with: Skip the video, Replay the video, pauze the video, and Expand the video",
        // VALID_COMMAND handler — user asked "what can I do here / help".
        helpMenu: "On this screen I can help you with the following : Skip the video, Replay the video, pauze the video, and Expand the video",
      },
    },

    // ── Demo-video setup beat (per area) ────────────────────────
    // Spoken right before the test1 demo video plays. Bridges from the
    // questions/classification phase into the movement, framing why
    // the chosen movement was picked.
    DEMO_VIDEO_1_SETUP_BEAT_BY_AREA: {
      LBP: "For our first step, let's look at a quick demo of the {testName}. Take your time to get comfortable. I am ready when you are.",
      HIP: "For our first step, let's look at a quick demo of the {testName}. Take your time to get comfortable. I am ready when you are.",
      THIGH: "For our first step, let's look at a quick demo of the {testName}. Take your time to get comfortable. I am ready when you are.",
      KNEE: "For our first step, let's look at a quick demo of the {testName}. Take your time to get comfortable. I am ready when you are.",
    },

    DEMO_VIDEO_1_POST_VIDEO: "Got it. Let's keep going.",

    // ── Demo-video "why I picked this movement" rationale (per subtype) ─
    // Long-form per-subtype copy explaining why test1 was chosen, given
    // the user's chip-flow / classification result. Keys are subtype IDs.
    DEMO_VIDEO_1_WHY_BY_SUBTYPE: {
      // LBP — locked from extract §3.2
      FLEXION_INTOLERANT: "You told me sitting and bending forward make this worse. The movement coming up goes the opposite direction, gently, so I can see how your back responds. When backs respond well to going one direction over another, that's the strongest signal I can get this early.",
      EXTENSION_INTOLERANT: "You told me standing and walking for a while make this worse. The movement coming up goes in the opposite direction, gently folding your back forward, so I can see how it responds. When backs respond well to going one direction over another, that's the strongest signal I can get this early.",
      LIMITED_LUMBAR_MOBILITY: "You told me your back feels stiff and restricted, more than tied to one direction. The movement coming up is a side-to-side bend, which lets me see how your back moves across multiple directions. When the pattern is stiffness rather than one bad direction, what your body shows me side-to-side is what tells me the most.",
      ROTATIONAL_INTOLERANT: "You told me twisting and turning make this worse. Most of the twisting movements that bother backs are also bending movements. The movement coming up gently goes backward, which is the cleanest way for me to see how your back wants to move. Your body's response is what tells me the most.",
      NEUTRAL_SENSITIVE: "What you shared in the questions tells me your back doesn't fit a single direction cleanly, and that's something the assessment is built to handle. The movement coming up is a gentle seated rotation. What your body shows me there is what reveals the pattern self-report alone couldn't.",

      // HIP — locked from extract §3.3
      ANTERIOR_CHAIN_MOBILITY_DEFICIT: "Your chip flow pointed to a hip pattern that shows up as positional limits, particularly in the front. The movement coming up tests how that range is right now. What I see there is what tells me the most about how to start the work.",
      POSTERIOR_CHAIN_MOBILITY_DEFICIT: "Your chip flow pointed to a hip pattern that shows up as positional limits, especially toward the back. The movement coming up tests how that range is right now. What I see there is what tells me the most about how to start the work.",
      NEUROMUSCULAR_MOBILITY_DEFICIT: "Your chip flow pointed to a hip pattern that shows up as positional limits, especially toward the back. The movement coming up tests how that range is right now. What I see there is what tells me the most about how to start the work.",
      ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "You shared a pattern that points to your inner-thigh muscles being part of the picture, with the hip needing strengthening work to support them. The movement coming up is a seated hip hinge, which lets me see how your hip moves when it folds, without putting load directly on the inner thigh yet. What I see is what tells me where to start.",
      HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: "Your chip flow pointed to a hip pattern where the right work has been studied unusually well. The movement coming up is a seated hip abduction, a controlled probe of how the side-of-hip muscles are working. What I see is what tells me how to build the work.",
      PELVIC_CONTROL_IMPAIRMENT: "Your chip flow pointed to a hip pattern where the work centers on how your pelvis controls movement under load. The movement coming up is a seated hip hinge, a controlled probe of that control. What I see is what tells me where to start.",

      // THIGH — locked from extract §3.4
      QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "What you shared points to the muscles around the front of your thigh. The movement coming up is a seated isometric knee extension, a controlled probe of how that muscle handles load right now. What I see there is what tells me where to start.",
      HAMSTRING_LOADING_LENGTHENING_DEFICIT: "What you shared points to the muscles at the back of your thigh. The movement coming up is a toe touch, a low-load probe of how those structures tolerate lengthening. What I see there is what tells me where the work should start.",
      ITB_FRICTION_SYNDROME: "What you shared points to a pattern along the outer side of your thigh. The movement coming up is a standing stretch with a side bend, a controlled probe of how the outer side of your leg tolerates lengthening. What I see there is what tells me where to start.",

      // KNEE — locked from extract §3.5
      KNEE_VALGUS_COLLAPSE: "Your chip flow pointed to a knee pattern where the hip side of the chain is part of the picture. The movement coming up is a seated hip abduction, a controlled probe of the muscles that support your knee from the side. What I see there is what tells me where to start the work.",
      PATELLOFEMORAL_PAIN_WITH_MALTRACKING: "Your chip flow pointed to a knee pattern where the work comes in steady increments rather than all at once. The movement coming up is a seated isometric knee extension, a controlled probe of the muscles that hold your knee steady. What I see there is what tells me where the steady work begins.",
      LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: "Your chip flow pointed to a knee pattern where stiffness is part of the picture. The movement coming up is a seated isometric knee extension, a controlled probe of how the muscles around your knee respond. What I see there is what tells me where to start.",
      TIBIAL_TRANSLATION_INSTABILITY: "Your chip flow pointed to a knee pattern where the work comes in steady increments rather than all at once. The movement coming up is a seated isometric knee extension, a controlled probe of the muscles that hold your knee steady. What I see there is what tells me where the steady work begins.",
    },
  },

  // ── 9b. EXERCISE INTRO ANNOUNCEMENT ──────────────────────────
  // Plays after the demo video, when the user is about to perform the movement.
  // Distinct from movement.cameraGuide / selfReportMovement.guide, which
  // reference the camera/self-report context. This is the pure "let's begin" line.
  //
  // Vars: {name}, {displayName}, {totalReps}, {holdText}
  //   {holdText} is pre-composed by the caller — either ' and hold for N seconds.' or '.'.
  exerciseIntro: {
    announce: {
      app: {
        // ⚑ FLAG: identical to gsa.test1 today — kept separate per doctrine
        // so future in-app vs GSA wording can diverge without restructuring.
        voice: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
        chat: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
      },
      gsa: {
        test1: {
          voice: "{name} | Thanks for continuing with your assessment! Alright, let's get started with the {displayName}. You'll do {totalReps} reps—take your time, and I'll follow your pace.",
          chat: "{name} | Thanks for continuing with your assessment! Alright, let's get started with the {displayName}. You'll do {totalReps} reps—take your time, and I'll follow your pace.",
        },
        test2: {
          // ⚑ FLAG: same as test1 today; will diverge when GSA test2-specific
          // wording is written (e.g. "one more to try — the {displayName}").
          voice: "{name} | Thanks for continuing with your assessment! Alright, let's get started with the {displayName}. You'll do {totalReps} reps—take your time, and I'll follow your pace.",
          chat: "{name} | Thanks for continuing with your assessment! Alright, let's get started with the {displayName}. You'll do {totalReps} reps—take your time, and I'll follow your pace.",
        },
      },
    },

    // GSA second-movement-test variant. Caller picks this key when
    // _isSecondMovementTest is true (GSA-specific concept).
    // Vars: {name}, {displayName}, {totalReps}, {holdText}
    announceSecondMovement: {
      app: {
        // ⚑ FLAG: _isSecondMovementTest is GSA-only by design; this app slot
        // exists for schema symmetry. Resolver will only fall back here if
        // flow=app, which shouldn't happen for this key.
        voice: "{name} | Okay, for your second movement check you'll be doing the {displayName} — this helps me confirm a few things. You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
        chat: "{name} | Okay, for your second movement check you'll be doing the {displayName} — this helps me confirm a few things. You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
      },
      gsa: {
        test1: {
          // ⚑ FLAG: "second movement check" wording on test1 is unusual — caller
          // gates this key on _isSecondMovementTest, so test1 should rarely fire.
          voice: "{name} | Okay, for your second movement check you'll be doing the {displayName} — this helps me confirm a few things. You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
          chat: "{name} | Okay, for your second movement check you'll be doing the {displayName} — this helps me confirm a few things. You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
        },
        test2: {
          voice: "{name} | Okay, for your second movement check you'll be doing the {displayName} — this helps me confirm a few things. You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
          chat: "{name} | Okay, for your second movement check you'll be doing the {displayName} — this helps me confirm a few things. You'll be doing {totalReps} reps{holdText} I'll count each rep for you. When you hear, 3, 2, 1, Start Exercise... Begin your first rep and try to follow my pace as closely as you can... Let's do this!",
        },
      },
    },

    // Legacy non-GSA camera-path announcements (the long branching
    // `announcementText` block in check_start_stage.dart line 1775+).
    // The non-GSA codepath chooses one of these based on flags:
    //   isSet2, isUnilateral, isSideways, isFirstExercise,
    //   switchedMainPosition, anchorChanged, anchorMovedCloser/Farther.
    //
    // The selfLevel == '1' / '2' branches from the source are intentionally
    // dropped per direction — those were old long-form intros now superseded
    // by the GSA Phase-1 step flow. Call site should be cleaned up to remove
    // them from prepareUpNextAudio.
    //
    // ⚑ NOTE on fragments: these templates contain pre-composed fragments
    // that the dart caller assembles ({proppingTip}, {placementText},
    // {anchorTransition}, {grabChair}, {feetNotVisible}, {justModifier},
    // {seatedPrefix}, {repositionGuidance}, {screenInstructionsWithPunct}).
    // Those fragments are themselves user-facing copy that lives in dart,
    // not here. Future cleanup should lift them into this file.
    cameraInApp: {

      // First exercise of the session, full setup guidance.
      // Vars: {name}, {displayName}, {totalReps}, {holdText}, {placementText},
      //       {proppingTip}, {standOrSit}, {distance}, {justModifier}, {anchor},
      //       {feetNotVisible}, {screenInstructionsWithPunct}
      firstExercise: {
        app: {
          voice: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} {placementText}{proppingTip} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} If you want to skip or pause this exercise, just let me know.",
          chat: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} {placementText}{proppingTip} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} If you want to skip or pause this exercise, just let me know.",
        },
        gsa: {
          // ⚑ FLAG: legacy non-GSA path; gsa slots exist for schema symmetry
          // but should not fire — the GSA gate at check_start_stage:1614
          // returns before reaching this code.
          test1: {
            voice: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} {placementText}{proppingTip} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} If you want to skip or pause this exercise, just let me know.",
            chat: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} {placementText}{proppingTip} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} If you want to skip or pause this exercise, just let me know.",
          },
          test2: {
            voice: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} {placementText}{proppingTip} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} If you want to skip or pause this exercise, just let me know.",
            chat: "{name} | Your first exercise today is the {displayName}! You'll be doing {totalReps} reps{holdText} {placementText}{proppingTip} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} If you want to skip or pause this exercise, just let me know.",
          },
        },
      },

      // Continuation set (same exercise, additional set), bilateral.
      // Vars: {totalReps}, {displayName}, {holdText}
      continuationSet: {
        app: {
          voice: "Let's keep it going with another {totalReps} reps {displayName}{holdText}",
          chat: "Let's keep it going with another {totalReps} reps {displayName}{holdText}",
        },
        gsa: {
          test1: {
            voice: "Let's keep it going with another {totalReps} reps {displayName}{holdText}",
            chat: "Let's keep it going with another {totalReps} reps {displayName}{holdText}",
          },
          test2: {
            voice: "Let's keep it going with another {totalReps} reps {displayName}{holdText}",
            chat: "Let's keep it going with another {totalReps} reps {displayName}{holdText}",
          },
        },
      },

      // Continuation set, unilateral (one-sided), facing forward.
      // Vars: {displayName}, {limbSide}, {totalReps}, {holdText}
      continuationSetUnilateral: {
        app: {
          voice: "Continuing with {displayName} — this time use your {limbSide} leg, do {totalReps} reps{holdText}",
          chat: "Continuing with {displayName} — this time use your {limbSide} leg, do {totalReps} reps{holdText}",
        },
        gsa: {
          test1: {
            voice: "Continuing with {displayName} — this time use your {limbSide} leg, do {totalReps} reps{holdText}",
            chat: "Continuing with {displayName} — this time use your {limbSide} leg, do {totalReps} reps{holdText}",
          },
          test2: {
            voice: "Continuing with {displayName} — this time use your {limbSide} leg, do {totalReps} reps{holdText}",
            chat: "Continuing with {displayName} — this time use your {limbSide} leg, do {totalReps} reps{holdText}",
          },
        },
      },

      // Continuation set, unilateral, sideways orientation.
      // Vars: {displayName}, {totalReps}, {holdText}, {limbSide}
      continuationSetUnilateralSideways: {
        app: {
          voice: "Continuing with {displayName} and {totalReps} reps{holdText} This time you will be using your {limbSide} leg. Turn around so your {limbSide} shoulder is closest to the camera.",
          chat: "Continuing with {displayName} and {totalReps} reps{holdText} This time you will be using your {limbSide} leg. Turn around so your {limbSide} shoulder is closest to the camera.",
        },
        gsa: {
          test1: {
            voice: "Continuing with {displayName} and {totalReps} reps{holdText} This time you will be using your {limbSide} leg. Turn around so your {limbSide} shoulder is closest to the camera.",
            chat: "Continuing with {displayName} and {totalReps} reps{holdText} This time you will be using your {limbSide} leg. Turn around so your {limbSide} shoulder is closest to the camera.",
          },
          test2: {
            voice: "Continuing with {displayName} and {totalReps} reps{holdText} This time you will be using your {limbSide} leg. Turn around so your {limbSide} shoulder is closest to the camera.",
            chat: "Continuing with {displayName} and {totalReps} reps{holdText} This time you will be using your {limbSide} leg. Turn around so your {limbSide} shoulder is closest to the camera.",
          },
        },
      },

      // Next exercise, main position (seated/standing) switched from previous.
      // Vars: {displayName}, {totalReps}, {holdText}, {grabChair}, {placementText},
      //       {standOrSit}, {distance}, {justModifier}, {anchor}, {feetNotVisible},
      //       {screenInstructionsWithPunct}
      nextExerciseSwitchedPosition: {
        app: {
          voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {grabChair}{placementText} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {grabChair}{placementText} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
        },
        gsa: {
          test1: {
            voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {grabChair}{placementText} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
            chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {grabChair}{placementText} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          },
          test2: {
            voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {grabChair}{placementText} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
            chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {grabChair}{placementText} You should be able to {standOrSit} about {distance} away from your device as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          },
        },
      },

      // Next exercise, anchor changed (knees ↔ ankles visibility requirement).
      // Vars: {displayName}, {totalReps}, {holdText}, {anchorTransition},
      //       {screenInstructionsWithPunct}
      nextExerciseAnchorChanged: {
        app: {
          voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {anchorTransition}To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {anchorTransition}To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
        },
        gsa: {
          test1: {
            voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {anchorTransition}To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
            chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {anchorTransition}To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          },
          test2: {
            voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {anchorTransition}To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
            chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} {anchorTransition}To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          },
        },
      },

      // Next exercise, same setup as previous (most common case).
      // Vars: {displayName}, {totalReps}, {holdText}, {anchor},
      //       {screenInstructionsWithPunct}
      nextExerciseSameSetup: {
        app: {
          voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} Keep the same setup, with your {anchor} to your shoulders visible. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} Keep the same setup, with your {anchor} to your shoulders visible. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
        },
        gsa: {
          test1: {
            voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} Keep the same setup, with your {anchor} to your shoulders visible. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
            chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} Keep the same setup, with your {anchor} to your shoulders visible. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          },
          test2: {
            voice: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} Keep the same setup, with your {anchor} to your shoulders visible. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
            chat: "Your next exercise is the {displayName}. You'll be doing {totalReps} reps{holdText} Keep the same setup, with your {anchor} to your shoulders visible. To get into the starting position, {screenInstructionsWithPunct} As always, if you need anything, just ask.",
          },
        },
      },

      // Anchor-transition fragment: user needs to move CLOSER (ankles → knees).
      // Used as a {anchorTransition} substitution inside nextExerciseAnchorChanged.
      // No vars.
      anchorTransitionCloser: {
        app: {
          voice: "For this one, I only need to see you from your knees to your shoulders — your feet don't need to be visible on screen — so you can move a bit closer. ",
          chat: "For this one, I only need to see you from your knees to your shoulders — your feet don't need to be visible on screen — so you can move a bit closer. ",
        },
        gsa: {
          test1: {
            voice: "For this one, I only need to see you from your knees to your shoulders — your feet don't need to be visible on screen — so you can move a bit closer. ",
            chat: "For this one, I only need to see you from your knees to your shoulders — your feet don't need to be visible on screen — so you can move a bit closer. ",
          },
          test2: {
            voice: "For this one, I only need to see you from your knees to your shoulders — your feet don't need to be visible on screen — so you can move a bit closer. ",
            chat: "For this one, I only need to see you from your knees to your shoulders — your feet don't need to be visible on screen — so you can move a bit closer. ",
          },
        },
      },

      // Anchor-transition fragment: user needs to move FARTHER (knees → ankles).
      // No vars.
      anchorTransitionFarther: {
        app: {
          voice: "For this one, I need to see you from your ankles to your shoulders, so you may need to back up a step or two. ",
          chat: "For this one, I need to see you from your ankles to your shoulders, so you may need to back up a step or two. ",
        },
        gsa: {
          test1: {
            voice: "For this one, I need to see you from your ankles to your shoulders, so you may need to back up a step or two. ",
            chat: "For this one, I need to see you from your ankles to your shoulders, so you may need to back up a step or two. ",
          },
          test2: {
            voice: "For this one, I need to see you from your ankles to your shoulders, so you may need to back up a step or two. ",
            chat: "For this one, I need to see you from your ankles to your shoulders, so you may need to back up a step or two. ",
          },
        },
      },
    },
  },

  // ── 9c. MOVEMENT Check COMPLETION ─────────────────────────────
  movementTest: {
    completionSummary: "Past the {testName}.", // batch: 1 (pageLoad)
  },

  // When camamra Selected
  // completionSummary: "Done with the {testName}. That gives me what I need. Let me ask you a couple of follow-up questions.", // batch: 1 (pageLoad)

  // We need different ACK for 2nd movement check - Camera or no camera
  // completionSummary: "Thanks. No more tests.", // batch: 1 (pageLoad)

  // ── 10. CAMERA PERMISSION ────────────────────────────────────
  camera: {
    prompt: "To help me do the best job assessing your movement during the test, I can use your camera to track your movements in real time. Would you like to enable your camera or prefer not to? Your video stream stays on your device and is never recorded.",
    cameraAllowed: "Thanks, you'll be shortly asked for camera permission by your device.", // batch: 1 (pageLoad)
    cameraDeclined: "Got it. I'll ask you to self-report to me after the movement check.", // batch: 1 (pageLoad)
    CAMERA_PERMISSION_SETUP_BEAT: "You have two ways to complete the movement check: you can self-report, or I can monitor your movement using your camera. If you choose the camera, it simply tracks your movement patterns, speed, and range. It does not record you, and your video stays entirely on your device at all times. If you prefer to self-report, you will just answer two short questions about your movement afterward instead. Please pick whichever option makes you the most comfortable.", // batch: 1 (pageLoad)
    CAMERA_PERMISSION_DECLINE_LEGITIMIZATION: "If you'd rather not use the camera, the assessment continues with two short questions after the movement instead. They ask about the same things the camera would track, how your range and speed held up rep-to-rep, just from your perspective rather than mine.", // batch: 1 (pageLoad)
    buttonLabels: {
      continueWithoutCamera: "Continue Without Camera",
      allowCameraAccess: "Allow Camera Access",
    },
  },

  // ── 10b. CAMERA SESSION (on-demand camera live runtime) ──────
  // Voice acknowledgements spoken by the on-demand camera controller
  // (ondemand-camera.js) during the camera-on test flow. These run
  // outside the GSA chat shell — they're voice-command responses while
  // the user is positioned in front of the camera.
  cameraSession: {
    // START_TEST command response.
    startTestAck: "Great! Let's start the test.",
    // PROCEED_WITHOUT_CAMERA command response.
    proceedWithoutCameraAck: "Sure! You can continue without the camera, camera is optional.",
    // EXPAND_VIDEO command response.
    expandVideoAck: "Sure! I'll expand the video for you.",
    // EXPAND_CAMERA command response.
    expandCameraAck: "Sure! I'll expand the camera for you.",
    // MOVE_ON / continue command response (handleContinue).
    moveOnAck: "Great! Let's move on to the next step.",

    // Spoken when the user re-enters the camera flow (resume) or starts it
    // for the first time. Vars: {name}, {startLabel}.
    //   {startLabel} is "Re-zoom" when isResume === 'resume', else "Start".
    //   The pipe character "|" is preserved verbatim — it's a TTS pause hint.
    welcomeBack: "{name}. | Nice to have you back! When you're ready, press {startLabel} and I'll be ready to guide you.",

    // Static suffix appended to a dynamic backend `data.message` when the
    // start-position check fails. The dynamic prefix is the API's instruction
    // (e.g. "Move a step to the left."); this suffix gives the user a voice
    // override path. Kept as a leading-space string so callers can do
    // `data.message + MYRA_MESSAGES.cameraSession.startPositionOverrideHint`.
    startPositionOverrideHint: " If you feel you are in the correct position, just say 'Myra I am in the correct starting position' and I'll start the exercise",
  },

  // ── 11. MOVEMENT (Camera path) ───────────────────────────────
  // Mode split: voice reads "I'll be watching and counting" (works in both modes
  // conceptually but phrased for audio); chat version drops the spoken framing.
  movement: {
    cameraGuide: {
      voice: "Go ahead and try the <strong>{testName}</strong> now. I'll be watching and counting your reps.",
      chat: "Go ahead and try the <strong>{testName}</strong> now. I'll be watching and counting your reps.",
    },
    // ⚑ FLAG: both variants are identical today. If you want different chat
    // phrasing (e.g. "Tap Done when you finish" instead of "I'll be watching"),
    // say the word and I'll split them.
  },

  // ── 12. SELF-REPORT MOVEMENT (No-camera path) ────────────────
  selfReportMovement: {
    guide: {
      voice: "Go ahead and try the <strong>{testName}</strong> now. Take your time — do about 5-6 repetitions at a comfortable pace. I'll ask you a couple of questions when you're done.",
      chat: "Go ahead and try the <strong>{testName}</strong> now. Take your time — do about 5-6 repetitions at a comfortable pace. I'll ask you a couple of questions when you're done.",
    },
    // ⚑ FLAG: same situation as movement.cameraGuide — identical voice/chat today.
  },


  movementQuestions: {
    helper: "Describe it however feels natural.",
    q1: {
      setupBeat: "Now I want to understand how that movement went for you, both the effort it took and what it did to your pain.",
      question: "How challenging did that movement feel, and did it leave your pain better, worse, or about the same?",
    },
    q2: {
      setupBeat: "Now think back across all your reps, from the first one to the last.",
      question: "Were you able to keep the same pace, and did your movements stay just as full by the end?",
    },
  },

  // ── 13. SELF-REPORT ROM ──────────────────────────────────────
  selfReportRom: {
    // Heading: see SELF_REPORT_QUESTION below (matches CONSTANTS.SELF_REPORT_ROM.heading)
    // Subheading & options: see `subheading` and `options` below
    //   (moved from CONSTANTS.SELF_REPORT_ROM)
    prompt: "Thinking about your range of motion during the movement, did it...", // batch: 1 (pageLoad)
    transitionToTempo: "Thanks for sharing. One more question.", // batch: 1 (pageLoad)

    // Validation: spoken when user tries to advance with no selection (voice mode).
    noSelectionPrompt: "Please make your selection before we can move to the next screen",

    // ── ROM question copy (setup beat → question → per-area nugget → post-tap) ─
    SELF_REPORT_ROM_SETUP_BEAT: "Take a moment to think about how far you were able to move during the exercise.",
    SELF_REPORT_QUESTION: 'How did your last few reps compare to your first few?',
    // ── Subheading (moved from CONSTANTS.SELF_REPORT_ROM.subheading) ─
    subheading: "Think about the first few reps you did and the last few reps. How did your movements change?",

    SELF_REPORT_ROM_NUGGET_BY_AREA: {
      LBP: "I am checking to see if the distance you moved stayed the same from start to finish. I want to know about the actual size of your reps, rather than how they felt.",
      HIP: "I am checking to see if the distance you moved stayed the same from start to finish. I want to know about the actual size of your reps, rather than how they felt.",
      THIGH: "I am checking to see if the distance you moved stayed the same from start to finish. I want to know about the actual size of your reps, rather than how they felt.",
      KNEE: "I am checking to see if the distance you moved stayed the same from start to finish. I want to know about the actual size of your reps, rather than how they felt.",
    },
    SELF_REPORT_ROM_POST_TAP: "Thanks for letting me know.",

    // ── Per-option voice replies (moved from CONSTANTS.getSelfReportReplyText.rom) ─
    // Keyed by option id from SELF_REPORT_ROM.options. The function in CONSTANTS
    // now reads from this table.
    replyByValue: {
      rom_same: "Stayed the same — that's good information.",
      rom_smaller: "Got Shorter — that's useful to know.",
      rom_bigger: "Got Longer — that's a positive sign.",
    },

    // ── Option labels (moved from CONSTANTS.SELF_REPORT_ROM.options) ─
    // Pattern A lookup: components iterate the options array from CONSTANTS
    // (carries id + points routing) and look up display copy here by id.
    options: {
      rom_smaller: { title: "Got Shorter", subtitle: "My movements became shorter as I kept going, I think I was extending less." },
      rom_bigger: { title: "Got Longer", subtitle: "My movements became longer as I kept going, I think I was extending more." },
      rom_same: { title: "Stayed the same", subtitle: "My last few reps and the first few were the same." },
    },
  },

  // ── 14. SELF-REPORT TEMPO ────────────────────────────────────
  selfReportTempo: {
    // Heading: see SELF_REPORT_QUESTION below (matches CONSTANTS.SELF_REPORT_TEMPO.heading)
    // Subheading & options: see `subheading` and `options` below
    //   (moved from CONSTANTS.SELF_REPORT_TEMPO)
    // HARD_STOP outcome handled by hardStop.couldntFinish
    prompt: "Thinking about your pace during the movement, did you...", // batch: 1 (pageLoad)
    transitionToRpe: /* PLACEHOLDER COPY */ "Thanks — one more question about the movement.", // batch: 1 (pageLoad)

    // Validation: spoken when user tries to advance with no selection (voice mode).
    noSelectionPrompt: "Please make your selection before we can move to the next screen",

    // ── Tempo question copy (setup beat → question → per-area nugget → post-tap) ─
    SELF_REPORT_QUESTION: 'Were you able to keep up with my counting for each rep?',
    // ── Subheading (moved from CONSTANTS.SELF_REPORT_TEMPO.subheading) ─
    subheading: "Compared to Myra's count, how would you describe your speed?",

    SELF_REPORT_TEMPO_SETUP_BEAT: "This time, I’m checking how your pace matched up with my counting in the video.",
    SELF_REPORT_TEMPO_NUGGET_BY_AREA: {
      LBP: "Matching my count for each rep helps me see how well your body kept up with the pace of the movement. It tells me if the speed felt manageable, got harder, or stayed steady throughout the test.",
      HIP: "Matching my count for each rep helps me see how well your body kept up with the pace of the movement. It tells me if the speed felt manageable, got harder, or stayed steady throughout the test.",
      THIGH: "Matching my count for each rep helps me see how well your body kept up with the pace of the movement. It tells me if the speed felt manageable, got harder, or stayed steady throughout the test.",
      KNEE: "Matching my count for each rep helps me see how well your body kept up with the pace of the movement. It tells me if the speed felt manageable, got harder, or stayed steady throughout the test.",
    },
    SELF_REPORT_TEMPO_POST_TAP_CONTINUE: "Got that. Now one more, about how the movement felt as work.",

    // ── Per-option voice replies (moved from CONSTANTS.getSelfReportReplyText.tempo) ─
    // Keyed by option id from SELF_REPORT_TEMPO.options. The function in CONSTANTS
    // now reads from this table.
    replyByValue: {
      tempo_kept_up: "You kept up — great.",
      tempo_slower: "You slowed down — that's completely okay.",
      tempo_not_finished: "You weren't able to finish — that's important for me to know.",
    },

    // ── Option labels (moved from CONSTANTS.SELF_REPORT_TEMPO.options) ─
    // Pattern A lookup: components iterate the options array from CONSTANTS
    // (carries id + points + outcome routing) and look up display copy here by id.
    options: {
      tempo_kept_up: { title: "Kept up", subtitle: "I matched Myra's speed or moved slightly faster." },
      tempo_slower: { title: "Slowed down", subtitle: "I finished all the reps but moved slower." },
      tempo_not_finished: { title: "Couldn't finish", subtitle: "I couldn't finish all the reps and had to stop." },
    },
  },

  // ── 15. RPE ──────────────────────────────────────────────────
  rpe: {
    prompt: "How challenging did that movement feel?",
    transitionToFeelAffect: /* PLACEHOLDER COPY */ "Got it. One last question about how that movement affected your symptoms.",

    // Validation: spoken when user tries to advance with no RPE rating (voice mode).
    noSelectionPrompt: "Please select how challenging the exercise was before we continue.",
    RPE_SETUP_BEAT_BY_PATH: {
      granted: "Your answer helps me make sure my initial recovery plan is on the right track for you.", // batch: 1 (pageLoad)
      declined: "You rated your pain at the start. You just told me about your range and your speed. Now I want to know how the movement felt as work, pure effort, separate from pain. All of it tunes what comes next."
    },
    RPE_QUESTION: 'How challenging did that movement feel?', // batch: 1 (pageLoad)
    RPE_NUGGET: "When you’re in pain, it can be hard to separate effort from discomfort. For my sake, try to focus on how much effort it took to complete the 10 reps.", // batch: 1 (pageLoad)
    RPE_POST_TAP: "This helps me build the right recovery plan for you. Let's finish these last few steps together.", // batch: 1 (pageLoad)

    // ── Per-RPE-value voice replies (moved from CONSTANTS.getRpeResponseText) ─
    // Keyed by RPE numeric value (1, 3, 5, 7, 9). The function in CONSTANTS
    // now reads from this table.
    responseByValue: {
      1: "That was easy for you — good to know.",
      3: "A little bit of effort — noted.",
      5: "Right in the middle — that's useful information.",
      7: "That was pretty challenging — I'll keep that in mind.",
      9: "That was tough — thanks for pushing through it.",
    },

    // ── Per-RPE-value option labels (moved from CONSTANTS.RPE_OPTIONS / RPE_OPTIONS2) ─
    // Pattern A lookup: components iterate the RPE_OPTIONS array from CONSTANTS
    // (carries value + emoji + color) and look up the label here by value.
    // The same labels work for both RPE_OPTIONS and RPE_OPTIONS2.
    labelByValue: {
      1: "Easy",
      3: "Kinda Easy",
      5: "Just Right",
      7: "Kinda Hard",
      9: "Hard",
    },

  },

  // ── 16. FEEL AFFECT ──────────────────────────────────────────
  feelAffect: {
    prompt: "How did that movement make your symptoms feel?",

    // Override messages
    worsenedOverride: "Thanks for telling me — that's actually very useful information. It helps me adjust your classification. Let's move on to personalizing your plan.", // batch: 1 (pageLoad)
    rotationalImprovedOverride: "That's great feedback — it tells me a lot about what direction works for you. Let's move on.", // batch: 1 (pageLoad)

    // Normal flow
    noTest2: "That's all the movement checking we need. You're doing great — let's wrap things up.",
    transitionToTest2: "There's one more movement I'd like you to try — <strong>{testName}</strong>. Let me show you.",

    // Validation: spoken when user tries to advance with no selection (voice mode).
    noSelectionPrompt: "Please select how the movement made your symptoms feel.",
    FEEL_AFFECT_SETUP_BEAT: "I’d like to understand if the movement affected your symptoms. This helps me see if the direction I’ve chosen aligns with what your body is telling us and guides the next steps in your plan.", // batch: 1 (pageLoad)
    FEEL_AFFECT_QUESTION: 'How did that movement make your symptoms feel?', // batch: 1 (pageLoad)
    FEEL_AFFECT_NUGGET: "Your answer helps me understand what your body needs. If the movement helped, I’ll explore further to confirm. If it didn’t, I’ll adjust the plan to better suit you.", // batch: 1 (pageLoad)
    FEEL_AFFECT_CLOSE_BY_OUTCOME: {
      IMPROVED_HAS_TEST_2: "That's a strong signal. To confirm it's the direction that helped and not just any movement, I want to test the opposite direction next. The contrast is what tells me the most.", // batch: 1 (pageLoad)
      TOLERABLE_HAS_TEST_2: "That's a useful signal. To get a clearer picture, I want to test the opposite direction next. The contrast is what tells me the most.", // batch: 1 (pageLoad)
      WORSENED: "That tells me a lot. The direction I picked isn't the one your body responds to right now. I'll set you up with a path that starts where your body is, not where I thought it would be.", // batch: 1 (pageLoad)
      IMPROVED_NO_TEST_2: "That's enough for me to set up the right starting point. Combining what your body did, your pain rating, and your effort level, the picture is clear enough. Moving on.",
      TOLERABLE_NO_TEST_2: "That's enough for me to set up the right starting point. Combining what your body did, your pain rating, and your effort level, the picture is clear enough. Moving on."
    },

    // ── Option labels (moved from CONSTANTS.FEEL_AFFECT_OPTIONS) ─
    // Pattern A lookup: components iterate the FEEL_AFFECT_OPTIONS array
    // (carries id + value routing) and look up the label here by id.
    options: {
      "symptom-worsened": { label: "Felt Worse" },
      "symptom-tolerable": { label: "Felt Same" },
      "symptom-improved": { label: "Felt Better" },
    },
  },

  // ── 17. TEST 2 SKIP ──────────────────────────────────────────
  test2: {
    skipConfirm: "No problem — we have plenty to work with from the first movement. Let's move on.",

    DEMO_VIDEO_2_POST_VIDEO: "Thanks for watching the video.",

    // ── Demo-video 2 "why this second movement" rationale (per area) ─
    // Spoken right before the test2 demo video plays. Frames the contrast
    // between the first and second movements per problem area.
    DEMO_VIDEO_2_WHY_BY_AREA: {
      LBP: "It's for your second movement check, the {testName}. This movement helps confirm the best exercises for you. If doing this movement makes you feel worse, it actually confirms the plan. If you feel better instead, I have a backup plan ready to go. Please listen to your body. If you cannot start the test, or if your pain gets worse after you begin, simply press the skip exercise button.",
      HIP: "It's for your second movement check, the {testName}. This movement helps confirm the best exercises for you. If doing this movement makes you feel worse, it actually confirms the plan. If you feel better instead, I have a backup plan ready to go. Please listen to your body. If you cannot start the test, or if your pain gets worse after you begin, simply press the skip exercise button.",
      THIGH: "It's for your second movement check, the {testName}. This movement helps confirm the best exercises for you. If doing this movement makes you feel worse, it actually confirms the plan. If you feel better instead, I have a backup plan ready to go. Please listen to your body. If you cannot start the test, or if your pain gets worse after you begin, simply press the skip exercise button.",
      KNEE: "It's for your second movement check, the {testName}. This movement helps confirm the best exercises for you. If doing this movement makes you feel worse, it actually confirms the plan. If you feel better instead, I have a backup plan ready to go. Please listen to your body. If you cannot start the test, or if your pain gets worse after you begin, simply press the skip exercise button.",
    },
  },

  // ── 18. FEEDBACK 2 ───────────────────────────────────────────
  feedback2: {
    prompt: "If I asked you to {test2Description} and compared that to the {test1Name} you did, would you say it feels better, worse, or can't even try?",

    FEEDBACK_2_SETUP_BEAT: "I have one final question before I'm sure about your plan and we get to personalizing it.",

    FEEDBACK_2_QUESTION: "If I asked you to {test2Description} and compared that to the {test1Name} you did, would you say it feels better, worse, or can't even try?",

    FEEDBACK_2_NUGGET: "", // intentionally empty — no nugget on this screen

    FEEDBACK_2_ACK: "That locks it in. Let's personalize your plan.",

    // DEPRECATED — no longer used, kept for reference until cleanup pass
    FEEDBACK_2_CLOSE_BY_OUTCOME: {
      WORSE: "That confirms my initial plan. We’re done with the movement check phase—just one more step before your plan is ready.", // batch: 1 (pageLoad)
      BETTER: "That’s helpful to know. Both directions made a difference, so I’ll start with a balanced approach that works either way. We’re done with the movement check phase—just one more step before your plan is ready." // batch: 1 (pageLoad)
    },

    noSelectionPrompt: "Please choose better, worse, or can't even try.",

    options: {
      "fb2-cant-try": { label: "Can't even try" },
      "fb2-worse": { label: "Felt Worse" },
      "fb2-better": { label: "Felt Better" },
    },
  },

  // ── 19. PERSONALIZE ──────────────────────────────────────────
  personalize: {
    // Group intros: see `groupIntroById` below (moved from PERSONALIZE_GROUPS[n].myraIntro)
    // Skip messages: see `skipMessages` below (moved here from CONSTANTS.PERSONALIZE_SKIP_MESSAGES)
    // Field labels & placeholders: see `fields` below (moved from PERSONALIZE_GROUPS[n].fields)
    skipAllConfirm: "No problem — I'll use conservative settings across the board. You can update these anytime.",
    finishTransition: "Alright — I'm calculating your Recovery Compass Score now.", // batch: 1 (pageLoad)

    // ── Skip messages (moved from CONSTANTS.PERSONALIZE_SKIP_MESSAGES) ─
    // Context-specific copy shown when the user skips a personalization group.
    // Keyed by group id (basics / capacity / preferences).
    skipMessages: {
      basics: "I'll use conservative settings for your plan. Providing your age, weight, and height later will help me make it more accurate.",
      capacity: "Without knowing your activity level and floor capability, I'll start you at a gentler intensity to be safe.",
      preferences: "I'll set you up with a standard schedule — you can adjust this anytime.",
    },

    // ── Group intros (moved from CONSTANTS.PERSONALIZE_GROUPS[n].myraIntro) ─
    // Pattern A lookup: components iterate PERSONALIZE_GROUPS from CONSTANTS
    // (carries group id + fields schema) and look up the intro here by id.
    groupIntroById: {
      personalize: "A few details about you so I can build the right plan. All of these are optional — if you skip them, I'll use conservative defaults.",
    },

    // ── Field labels, placeholders & inline option labels ─────────
    //   (moved from CONSTANTS.PERSONALIZE_GROUPS[*].fields[*])
    // Pattern A lookup: components iterate the fields array from CONSTANTS
    // (carries id + type + min/max + option ids + value routing) and look up
    // display copy here by field id and (where relevant) option id.
    fields: {
      age: { label: "Age", placeholder: "Your age" },
      weight: { label: "Weight (lbs)", placeholder: "Your weight" },
      heightFt: { label: "Height (ft)", placeholder: "Feet" },
      heightIn: { label: "Height (in)", placeholder: "Inches" },
      activity: {
        label: "How many days per week are you typically active?",
        optionsByValue: {
          SEDENTARY: "0",
          LIGHTLY_ACTIVE: "1-2",
          ACTIVE: "3-4",
          VERY_ACTIVE: "5+",
        },
      },
      goals: {
        label: "What are your goals?",
        optionsByValue: {
          reduce_pain: "Reduce Pain",
          less_stiff: "Reduce Stiffness",
          improve_strength: "Improve Strength",
          improve_flexibility: "Improve Flexibility",
          daily_tasks: "Make Daily Tasks Easier",
          balance: "Improve Balance & Stability",
          picking_up: "Picking Up Things",
          sleep: "Improve Sleep Quality",
          walking: "Improve Walking & Endurance",
          lifting: "Make Bending & Lifting Easier",
          posture: "Improve Sitting & Standing Posture",
          rotational_sports: "Return to Sports (Golf, Tennis, etc.)",
          overhead_sports: "Return to Sports (Basketball, Swimming, etc.)",
          running: "Running, Cycling, etc.",
          fitness: "Fitness: Gym, CrossFit, etc.",
        },
      },
      duration: {
        label: "How long have you been experiencing these symptoms?",
        // Option labels reuse `preNprs.durationLabelByValue` — same ACUTE/SUBACUTE/CHRONIC values.
      },
    },

    // Validation: spoken when a FORM_QUESTION command fires and required fields are still missing.
    // Vars: {missing} — caller injects via resolveMessage().
    missingFieldsPrompt: "Please enter {missing}.",
    PERSONALIZE_SETUP_BEAT: "Before I build your plan, I’d love to learn a little more about you. Everything here is optional—share what you feel comfortable with, and I’ll use a safe starting point for anything you skip.", // batch: 1 (pageLoad)
    PERSONALIZE_QUESTION: 'Share a few details to help me match your recovery plan to the right level of challenge.', // batch: 1 (pageLoad)
    PERSONALIZE_NUGGET: "Two people with the same pain often need very different starting points. Knowing your activity level and goals helps me build a timeline that actually fits your body.", // batch: 1 (pageLoad)
    PERSONALIZE_USER_ECHO_BY_STATE: {
      filledAll: "Done. Here's what I want to share.",
      filledSome: "Here's what I'll share. The rest, you can fill in for me.",
      filledNothing: "I'd rather skip this part. You can fill in conservative settings."
    },
    PERSONALIZE_CLOSE_BY_STATE: {
      filledAll: "Got it. I have a clear picture of where to start you from.", // batch: 1 (pageLoad)
      filledSome: "Got it. I'll use what you've shared, and fill in conservative defaults for the rest.", // batch: 1 (pageLoad)
      filledNothing: "That's a fair call. I'll use a conservative starting point across the board, which means a gentler beginning." // batch: 1 (pageLoad)
    },
  },

  // ── 20. RECOVERY SCORE ───────────────────────────────────────
  recoveryScore: {
    // Score screen is rendered as inline UI, no explicit Myra chat message
    // Score explanation text is inline in the component
  },

  // ── 20a. DIAGNOSIS / READINESS / PLAN FOCUS ──────────────────
  // User-facing copy bundle moved from CONSTANTS.MESSAGING.* and
  // CONSTANTS.SUBTYPE_DISPLAY_NAMES / READINESS_TIERS.
  // Consumed by the recovery-compass screen and the plan screen.
  //
  // ⚑ DEDUPE: CONSTANTS previously had two readiness-tier objects
  //   (READINESS_TIERS and MESSAGING.readinessTiers) with subtle
  //   wording differences. The MESSAGING.readinessTiers wording is
  //   used here as canonical (longer, more user-facing copy).

  // Subtype-keyed clinical-style diagnosis cards.
  // Each entry: { headline, clinicalTerm, implications, relief }.
  diagnosis: {
    // LBP
    FLEXION_INTOLERANT: {
      headline: "Your Back Dislikes Bending Forward",
      clinicalTerm: "Flexion Intolerance",
      implications: "Activities that involve rounding your lower back, like slouching or tying your shoes, are likely to increase your discomfort.",
      relief: "You May Find That Standing Or Arching Your Back Provides Relief",
    },
    EXTENSION_INTOLERANT: {
      headline: "Your Back Dislikes Arching or Extended Standing",
      clinicalTerm: "Extension Intolerance",
      implications: "You might find that prolonged standing or walking is uncomfortable, and that sitting or slouching provides relief.",
      relief: "You May Find That Sitting Or Slouching Provides Relief",
    },
    LIMITED_LUMBAR_MOBILITY: {
      headline: "Your Back Pain May Be Caused by Stiffness",
      clinicalTerm: "Limited Lumbar Mobility",
      implications: "Because your lower back isn't moving as freely as it could, you might find that activities like putting on your shoes or getting out of a car feel stiff and difficult.",
      relief: "Gentle Movement In All Directions May Provide Relief",
    },
    NEUTRAL_SENSITIVE: {
      headline: "Your Back Seems to Want More Support",
      clinicalTerm: "Movement Coordination Impairments",
      implications: 'You might notice that your back feels weak, unstable, or like it might "give out."',
      relief: "Changing Positions Frequently May Be Your Best Source Of Relief Right Now",
    },
    ROTATIONAL_INTOLERANT: {
      headline: "Your Back Dislikes Twisting",
      clinicalTerm: "Rotational Intolerance",
      implications: "You might find that movements involving twisting, turning, or reaching across your body increase your discomfort.",
      relief: "Minimizing Twisting Motions May Provide Relief",
    },
    // HIGH_FUNCTIONING: triggered when backend returns complexityLevel === 5
    // Cannot be determined client-side — requires Recovery Compass Score API.
    HIGH_FUNCTIONING: {
      headline: "Your Back Is Ready to Level Up",
      clinicalTerm: "High Functioning / Low Irritability",
      implications: "You've shown strong control and minimal symptoms during the assessment, so you are well-positioned to progress beyond basic exercises and build a stronger, more durable back.",
      relief: "You May Find That Standing Or Arching Your Back Provides Relief",
    },
    // HIP
    ANTERIOR_CHAIN_MOBILITY_DEFICIT: {
      headline: "Your Hip Pain May Be Related to Tight Hip Flexors",
      clinicalTerm: "Anterior Chain Mobility Deficit",
      implications: "You might find that extending your leg behind you, standing up after prolonged sitting, or walking uphill increases your discomfort.",
      relief: "Hip Flexor Stretching May Provide Relief",
    },
    POSTERIOR_CHAIN_MOBILITY_DEFICIT: {
      headline: "Your Hip Pain May Be Related to Tight Posterior Muscles",
      clinicalTerm: "Posterior Chain Mobility Deficit",
      implications: "You might find that bringing your knee toward your chest, sitting for long periods, or certain rotational movements increase your discomfort.",
      relief: "Glute and Hip Mobility Work May Provide Relief",
    },
    NEUROMUSCULAR_MOBILITY_DEFICIT: {
      headline: "Your Hip Pain May Have a Neural Component",
      clinicalTerm: "Neuromuscular Mobility Deficit",
      implications: "You might notice a deep, specific pain in your buttock that sometimes travels down your leg, especially with certain sitting positions.",
      relief: "Gentle Neural Mobilization May Provide Relief",
    },
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: {
      headline: "Your Pain May Be Related to Inner Thigh Muscles",
      clinicalTerm: "Adductor Overload/Control Deficit",
      implications: "You might find that moving your legs apart, side-stepping, or activities that stress your inner thigh increase your discomfort.",
      relief: "Inner Thigh Strengthening May Provide Relief",
    },
    HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: {
      headline: "Your Hip May Need Better Side Support",
      clinicalTerm: "Hip Abduction/External Rotation Control Deficit",
      implications: "You might notice your hip drops when walking, or you feel unsteady when standing on one leg or climbing stairs.",
      relief: "Hip Abductor Strengthening May Provide Relief",
    },
    PELVIC_CONTROL_IMPAIRMENT: {
      headline: "Your Hip Needs Better Pelvic Control",
      clinicalTerm: "Pelvic Control Impairment",
      implications: "You might notice a pinching, clicking, or catching feeling in the front of your hip during certain movements, or feel like your pelvis isn't stable.",
      relief: "Pelvic Stability Exercises May Provide Relief",
    },
    // THIGH
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: {
      headline: "Your Thigh Pain May Be Related to Quadriceps Loading",
      clinicalTerm: "Quadriceps Loading/Impact Intolerance",
      implications: "You might find that activities like running, jumping, going down stairs, or kicking increase your front thigh discomfort.",
      relief: "Progressive Quadriceps Loading May Provide Relief",
    },
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: {
      headline: "Your Thigh Pain May Be Related to Hamstring or Posterior Chain",
      clinicalTerm: "Hamstring Loading/Lengthening Deficit",
      implications: "You might find that bending forward, stretching, quick movements, or prolonged sitting increase your thigh discomfort.",
      relief: "Progressive Hamstring Work May Provide Relief",
    },
    ITB_FRICTION_SYNDROME: {
      headline: "Your Pain May Be Related to IT Band Tightness",
      clinicalTerm: "ITB Friction Syndrome",
      implications: "You might notice pain on the outer side of your thigh or knee, especially with repetitive activities like running or cycling.",
      relief: "IT Band Stretching and Hip Strengthening May Provide Relief",
    },
    // KNEE
    KNEE_VALGUS_COLLAPSE: {
      headline: "Your Knee Pain May Be Related to Inward Knee Motion",
      clinicalTerm: "Knee Valgus Collapse",
      implications: "You might notice your knee diving inward during squats, stairs, or landing from jumps.",
      relief: "Hip Strengthening to Control Knee Position May Provide Relief",
    },
    PATELLOFEMORAL_PAIN_WITH_MALTRACKING: {
      headline: "Your Knee Pain May Be Related to Kneecap Tracking",
      clinicalTerm: "Patellofemoral Pain with Maltracking",
      implications: "You might notice grinding, clicking, or pain under your kneecap, especially after prolonged sitting.",
      relief: "Quadriceps Control and Patellar Tracking Exercises May Provide Relief",
    },
    LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: {
      headline: "Your Knee May Need Better Mobility",
      clinicalTerm: "Limited Knee Extension/Flexion Mobility",
      implications: "You might find it difficult to fully straighten or bend your knee, with stiffness throughout the range.",
      relief: "Gentle Range of Motion Exercises May Provide Relief",
    },
    TIBIAL_TRANSLATION_INSTABILITY: {
      headline: "Your Knee May Feel Unstable",
      clinicalTerm: "Tibial Translation (Instability)",
      implications: "You might feel like your knee could buckle, shift, or give way during activities.",
      relief: "Controlled Strengthening to Improve Stability May Provide Relief",
    },
  },

  // Subtype-keyed human-readable display names.
  // (Moved from CONSTANTS.SUBTYPE_DISPLAY_NAMES.)
  subtypeDisplayNames: {
    // LBP
    FLEXION_INTOLERANT: "Flexion-Sensitive",
    EXTENSION_INTOLERANT: "Extension-Sensitive",
    NEUTRAL_SENSITIVE: "Neutral Zone Sensitivity",
    LIMITED_LUMBAR_MOBILITY: "Limited Lumbar Mobility",
    ROTATIONAL_INTOLERANT: "Rotation-Sensitive",
    // HIP
    ANTERIOR_CHAIN_MOBILITY_DEFICIT: "Anterior Chain Mobility Deficit",
    POSTERIOR_CHAIN_MOBILITY_DEFICIT: "Posterior Chain Mobility Deficit",
    NEUROMUSCULAR_MOBILITY_DEFICIT: "Neuromuscular Mobility Deficit",
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "Adductor Overload/Control Deficit",
    HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: "Hip Abduction/External Rotation Control Deficit",
    PELVIC_CONTROL_IMPAIRMENT: "Pelvic Control Impairment",
    // THIGH
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "Quadriceps Loading/Impact Intolerance",
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: "Hamstring Loading/Lengthening Deficit",
    ITB_FRICTION_SYNDROME: "ITB Friction Syndrome",
    // KNEE
    KNEE_VALGUS_COLLAPSE: "Knee Valgus Collapse",
    PATELLOFEMORAL_PAIN_WITH_MALTRACKING: "Patellofemoral Pain with Maltracking",
    LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: "Limited Knee Extension/Flexion Mobility",
    TIBIAL_TRANSLATION_INSTABILITY: "Tibial Translation (Instability)",
  },

  // Subtype-keyed plan-focus tag (short phrase shown on plan screen).
  // (Moved from CONSTANTS.MESSAGING.planFocus.)
  planFocus: {
    // LBP
    FLEXION_INTOLERANT: "Reduce Sensitivity to Bending",
    EXTENSION_INTOLERANT: "Relieve Pressure from Arching",
    LIMITED_LUMBAR_MOBILITY: "Improve Flexibility",
    NEUTRAL_SENSITIVE: "Build Core Support",
    ROTATIONAL_INTOLERANT: "Reduce Sensitivity to Twisting",
    HIGH_FUNCTIONING: "Build Strength & Durability",
    // HIP
    ANTERIOR_CHAIN_MOBILITY_DEFICIT: "Improve Hip Flexor Flexibility",
    POSTERIOR_CHAIN_MOBILITY_DEFICIT: "Improve Hip Mobility",
    NEUROMUSCULAR_MOBILITY_DEFICIT: "Address Neural Tension",
    ADDUCTOR_OVERLOAD_CONTROL_DEFICIT: "Strengthen Inner Thigh Control",
    HIP_ABDUCTION_EXTERNAL_ROTATION_CONTROL_DEFICIT: "Strengthen Hip Abductors",
    PELVIC_CONTROL_IMPAIRMENT: "Improve Pelvic Stability",
    // THIGH
    QUADRICEPS_LOADING_IMPACT_INTOLERANCE: "Build Quadriceps Tolerance",
    HAMSTRING_LOADING_LENGTHENING_DEFICIT: "Improve Posterior Chain Flexibility",
    ITB_FRICTION_SYNDROME: "Release IT Band Tension",
    // KNEE
    KNEE_VALGUS_COLLAPSE: "Control Knee Alignment",
    PATELLOFEMORAL_PAIN_WITH_MALTRACKING: "Improve Kneecap Tracking",
    LIMITED_KNEE_EXTENSION_FLEXION_MOBILITY: "Restore Knee Range of Motion",
    TIBIAL_TRANSLATION_INSTABILITY: "Build Knee Stability",
  },

  // Recovery readiness tiers (canonical wording).
  // (Moved & deduped from CONSTANTS.MESSAGING.readinessTiers + CONSTANTS.READINESS_TIERS.
  // Canonical version uses MESSAGING.readinessTiers wording.)
  readinessTiers: {
    READY_TO_GO: {
      headline: "Ready to Go",
      description: "Your history and health factors show you're ready for a standard starting intensity.",
    },
    CAUTIOUS_START: {
      headline: "A Cautious Start",
      description: "We've slightly adjusted the intensity of your plan to ensure your body adapts well as you get started.",
    },
    GENTLE_APPROACH: {
      headline: "A Gentle Approach",
      description: "We've adjusted your plan to begin with a gentler intensity, giving your body extra time to respond and build momentum safely.",
    },
    STARTING_WITH_CARE: {
      headline: "Starting with Care",
      description: "To ensure the safest start, your plan begins with a very gentle intensity, focused on building a solid foundation.",
    },
  },

  // ── 21. PLAN SCREEN ──────────────────────────────────────────
  plan: {
    intro: "Here is your personalized Week 1 Recovery Plan! Thanks for answering my questions and completing those movements with me. Using everything you shared about how your body responds, I've built a safe starting point to help you feel better and move forward in your recovery.", // batch: 1 (pageLoad)
    qaEncouragement: "Have questions about your plan? Ask me anything—I’m here to help!",
    signupCta: {
      line1: "Continue my recovery with Myra",
      line2: "Sign up — free, no credit card",
    },

    // ── Plan-screen quick-ask canned answers (moved from CONSTANTS.PLAN_QA_ANSWERS) ─
    // Keyed by quick-ask intent (why / often / hurts).
    qaAnswers: {
      why: "Each exercise was chosen based on your pain pattern, movement results, and subtype classification. They target the specific areas that need strengthening or mobility work for your recovery.",
      often: "Your plan is designed for 3 sessions per week. Each session takes about 15–20 minutes. Consistency matters more than intensity — even 2 sessions is better than none.",
      hurts: "Mild discomfort during exercises can be normal, but sharp or increasing pain is not. If an exercise causes pain above a 4 out of 10 or gets worse as you go, stop and let me know. I can swap it for an alternative.",
    },
  },

  // ── CROSS-CUTTING: SHELL ─────────────────────────────────────
  // Shell-level voice messages that aren't tied to a single screen.
  // Currently covers the global AUTO_PAUSE inactivity command.
  shell: {
    // Spoken when AUTO_PAUSE fires while the user is in voice mode.
    // (In non-voice modes the InactivityModal is shown instead.)
    autoPause: "I've paused. Take your time...",
    myraName: "Myra",
    myraRole: "Recovery Coach",
    modeChatLabel: "Chat",
    modeVoiceLabel: "Voice",
    exitAssessmentTitle: "Exit assessment",
    inputPlaceholderDefault: "Ask me anything or share what's going on",
    inputPlaceholderReady: "Ask a question or type 'ready'…",
    inputPlaceholderAskMyra: "Ask Myra a question…",
  },

  // ── CROSS-CUTTING: SCREEN HEADINGS ───────────────────────────
  // User-facing shell headings used by SelfAssessment.jsx STEP_CONFIG.
  // Keys match the router step names.
  screenHeadings: {
    intro: "Getting Started",
    problem_area: "Where Did Your Pain Start?",
    pain_location: "Where Are You Feeling Pain?",
    safety_screening: "Quick Safety Check",
    cpg_questions: "Pain Character",
    subtype_questioning: "Pain Pattern",
    self_assessment: "Pain Pattern",
    nprs_symptoms: "What Is Your Pain Level?",
    test1_demo: "Movement Check 1 - Demo Video",
    camera_permission: "Camera Or Self-Report",
    mode_selection: "Chat Or Talk?",
    movement_test_1: "Movement Check 1",
    movement_q1: "Movement Check Feedback",
    movement_q2: "Rep Consistency",
    self_report_rom: "Self-Report Rep Comparison",
    self_report_tempo: "Self-Report Rep Pace",
    rpe: "How Challenging Was It?",
    feel_affect: "How Did It Make You Feel?",
    test2_demo: "Movement Check 2 - Demo Video",
    movement_test_2: "Movement Check 2",
    feedback2: "Movement Comparison",
    personalize: "Personalize Your Plan",
    recovery_score: "Recovery Compass",
    plan: "Your Recovery Plan",
    hard_stop: "Safety Recommendation",
  },

  // ── CROSS-CUTTING: STEP LABELS ───────────────────────────────
  // Progress-bar / breadcrumb labels per flow step.
  // (Moved from CONSTANTS.STEP_LABELS.) Keyed by the internal step name
  // used by the flow router. Step keys are NOT renamed — they stay tied
  // to router code; only the user-facing display labels live here.
  stepLabels: {
    intro: "Getting Started",
    problem_area: "Problem Area",
    pain_location: "Pinpoint Your Pain",
    safety: "Safety Check",
    safety_followup: "Safety Check",
    cpg: "Pain Character",
    subtype_q1: "Pain Pattern",
    q2: "Pain Pattern",
    pre_nprs: "Pain Level",
    demo_video_1: "Movement Check 1",
    demo_video_2: "Movement Check 2",
    camera: "Camera Setup",
    movement_1: "Movement Check 1",
    movement_2: "Movement Check 2",
    self_report_movement_1: "Movement Check 1",
    self_report_movement_2: "Movement Check 2",
    self_report_rom: "Self-Report",
    self_report_tempo: "Self-Report",
    rpe: "How Challenging",
    feel_affect: "Symptom Response",
    feedback2: "Movement Comparison",
    personalize: "Personalize Your Plan",
    recovery_score: "Recovery Compass",
    plan: "Your Recovery Plan",
    hard_stop: "Safety Recommendation",
  },

  // ── CROSS-CUTTING: MYRA AGENT (mode switching, welcome) ──────
  // Spoken by MyraAgent.jsx when the user switches between voice / chat /
  // button mode, or when the agent first picks a mode at session start.
  // These messages reach `doSay` (voice) on mobile/Android — the desktop /
  // chat-mode paths route through onMyraMessage instead, but the strings
  // are kept together here so siblings stay in one place.
  myraAgent: {
    // First-time welcome when the user picks a mode for the first time.
    // returningWelcome runs on subsequent picks (already-onboarded user).
    pickVoice: {
      firstTimeWelcome: "Hi, I'm Myra — your recovery coach. I'm here to guide you through your assessment. You can talk to me anytime, or just use the buttons on screen",
      returningWelcome: "I'm listening — go ahead.",
    },
    pickChat: {
      firstTimeWelcome: "Hi, I'm Myra — your recovery coach. I'm here to guide you through your assessment. Ask me anything, or use the buttons on screen.",
      returningWelcome: "I'm here — what can I help with?",
    },

    // Spoken when the user toggles modes mid-session.
    switchMode: {
      voiceToChat: "I'm here in chat now. What can I help with?",
      buttonToChat: "I'm here — what can I help with?",
      // ⚑ FLAG: chat→voice and button→voice currently use identical copy;
      // kept as separate keys so they can diverge later.
      chatToVoice: "I'm listening — go ahead.",
      buttonToVoice: "I'm listening — go ahead.",
      // Default fallback when onGrant fires with no msg set yet.
      voiceFallback: "I'm listening — go ahead.",
    },

    // Spoken when the user denies the mic permission and we fall back to chat.
    micDenied: "No problem! We can chat instead. I'm here if you need anything — just type or use the buttons on screen.",
  },

  // ── PHASE-1 STEP FLOW (CAMERA PATH) ──────────────────────────
  // Camera-on exercise stage runs a 5-step pre-exercise flow (GSA only;
  // non-GSA users go through the legacy `exerciseIntro.cameraInApp.*`
  // announcements above). Source: check_start_stage.dart.
  //
  // Steps:
  //   1. Intro            — "Thanks for continuing..."
  //   2. Device placement — "place your device at knee height"
  //   3. Controls handoff — "you can talk to me anytime / use chat"
  //   4. Get visible      — "stand/sit about X away from your device"
  //   5. Starting position — "to get into the starting position..."
  //   6. Countdown        — uses shared countdown.startExercise
  //
  // (Named `cameraStage` to avoid collision with the existing `camera`
  // key which holds camera-permission copy.)
  cameraStage: {

    // Step 1 — exercise intro for GSA camera path.
    // Vars: {displayName}, {totalReps}, {holdText}
    // Source: check_start_stage.dart line 1088.
    step1Intro: {
      announce: {
        app: {
          // ⚑ FLAG: kept for schema symmetry; non-GSA users hit
          // exerciseIntro.cameraInApp.* instead, not this key.
          voice: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps {holdText} — no rush, I'll follow your pace.",
          chat: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps {holdText} — no rush, I'll follow your pace.",
        },
        gsa: {
          test1: {
            voice: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps {holdText} — no rush, I'll follow your pace.",
            chat: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps {holdText} — no rush, I'll follow your pace.",
          },
          test2: {
            voice: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps {holdText} — no rush, I'll follow your pace.",
            chat: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps {holdText} — no rush, I'll follow your pace.",
          },
        },
      },
    },

    // Step 2 — device placement guidance + Ready confirmation.
    // Source: check_start_stage.dart lines 1221-1223 (announce, voice/chat split)
    //         and 1237/1249/1261 (helpOffer, single string in source).
    devicePlacement: {
      announce: {
        app: {
          voice: 'For the best camera position, place your device at about knee height. Once it\'s set, just say "ready" or press the "Ready" button, and we\'ll get started!',
          chat: "For the best camera position, place your device at about knee height. Once it's set, press Continue.",
        },
        gsa: {
          test1: {
            voice: 'For the best camera position, place your device at about knee height. Once it\'s set, just say "ready" or press the "Ready" button, and we\'ll get started!',
            chat: "For the best camera position, place your device at about knee height. Once it's set, press Continue.",
          },
          test2: {
            voice: 'For the best camera position, place your device at about knee height. Once it\'s set, just say "ready" or press the "Ready" button, and we\'ll get started!',
            chat: "For the best camera position, place your device at about knee height. Once it's set, press Continue.",
          },
        },
      },
      helpOffer: {
        // ⚑ FLAG: source has a single string for both voice and chat.
        // Voice/chat split present here per doctrine; chat copy will diverge.
        app: {
          voice: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Device Ready button.",
          chat: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Device Ready button.",
        },
        gsa: {
          test1: {
            voice: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Device Ready button.",
            chat: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Device Ready button.",
          },
          test2: {
            voice: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Device Ready button.",
            chat: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Device Ready button.",
          },
        },
      },
    },

    // Step 3 — controls handoff. Source has voice/chat split.
    // Source: check_start_stage.dart lines 1298-1299.
    controlsHandoff: {
      announce: {
        app: {
          voice: "Great! If you need to pause or have a question, just say pause or ask the question. Once I see you are in position, I'll start the exercise.",
          chat: "Great! Need a pause or have a question? Tap the Myra icon at the bottom left to chat. Once I see you are in position, I'll start the exercise.",
        },
        gsa: {
          test1: {
            voice: "Great! If you need to pause or have a question, just say pause or ask the question. Once I see you are in position, I'll start the exercise.",
            chat: "Great! Need a pause or have a question? Tap the Myra icon at the bottom left to chat. Once I see you are in position, I'll start the exercise.",
          },
          test2: {
            voice: "Great! If you need to pause or have a question, just say pause or ask the question. Once I see you are in position, I'll start the exercise.",
            chat: "Great! Need a pause or have a question? Tap the Myra icon at the bottom left to chat. Once I see you are in position, I'll start the exercise.",
          },
        },
      },
    },

    // Step 4 — get visible (only when CV doesn't see user yet).
    // Vars: {standOrSit}, {distance}, {justModifier}, {anchor}, {feetNotVisible}
    // Source: check_start_stage.dart lines 1476-1478 (announce)
    //         and 1486 (helpOffer; uses {anchor}).
    getVisible: {
      announce: {
        app: {
          voice: "{standOrSit} about {distance} away from your device, as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}.",
          chat: "{standOrSit} about {distance} away from your device, as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}.",
        },
        gsa: {
          test1: {
            voice: "{standOrSit} about {distance} away from your device, as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}.",
            chat: "{standOrSit} about {distance} away from your device, as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}.",
          },
          test2: {
            voice: "{standOrSit} about {distance} away from your device, as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}.",
            chat: "{standOrSit} about {distance} away from your device, as I {justModifier}need to see you from your {anchor} to your shoulders{feetNotVisible}.",
          },
        },
      },
      helpOffer: {
        // Vars: {anchor}
        // ⚑ FLAG: source has a single string for both modes.
        app: {
          voice: "I'm having trouble seeing you. Try adjusting your position so I can see you from your {anchor} to your shoulders.",
          chat: "I'm having trouble seeing you. Try adjusting your position so I can see you from your {anchor} to your shoulders.",
        },
        gsa: {
          test1: {
            voice: "I'm having trouble seeing you. Try adjusting your position so I can see you from your {anchor} to your shoulders.",
            chat: "I'm having trouble seeing you. Try adjusting your position so I can see you from your {anchor} to your shoulders.",
          },
          test2: {
            voice: "I'm having trouble seeing you. Try adjusting your position so I can see you from your {anchor} to your shoulders.",
            chat: "I'm having trouble seeing you. Try adjusting your position so I can see you from your {anchor} to your shoulders.",
          },
        },
      },
    },

    // Step 5 — starting position guidance.
    // Vars: {screenInstructionsWithPunct}
    // Source: check_start_stage.dart lines 1517-1519 (announce)
    //         and 1526 (helpOffer; no vars).
    startingPosition: {
      announce: {
        app: {
          voice: "To get into the starting position, {screenInstructionsWithPunct} You'll see an outline on your screen — when it turns green, you're in position, and I'll count you down and say Start Exercise.",
          chat: "To get into the starting position, {screenInstructionsWithPunct} You'll see an outline on your screen — when it turns green, you're in position, and I'll count you down and say Start Exercise.",
        },
        gsa: {
          test1: {
            voice: "To get into the starting position, {screenInstructionsWithPunct} You'll see an outline on your screen — when it turns green, you're in position, and I'll count you down and say Start Exercise.",
            chat: "To get into the starting position, {screenInstructionsWithPunct} You'll see an outline on your screen — when it turns green, you're in position, and I'll count you down and say Start Exercise.",
          },
          test2: {
            voice: "To get into the starting position, {screenInstructionsWithPunct} You'll see an outline on your screen — when it turns green, you're in position, and I'll count you down and say Start Exercise.",
            chat: "To get into the starting position, {screenInstructionsWithPunct} You'll see an outline on your screen — when it turns green, you're in position, and I'll count you down and say Start Exercise.",
          },
        },
      },
      helpOffer: {
        // ⚑ FLAG: source has a single string for both modes.
        app: {
          voice: "Try to match the outline on your screen. When it turns green, I'll count you down.",
          chat: "Try to match the outline on your screen. When it turns green, I'll count you down.",
        },
        gsa: {
          test1: {
            voice: "Try to match the outline on your screen. When it turns green, I'll count you down.",
            chat: "Try to match the outline on your screen. When it turns green, I'll count you down.",
          },
          test2: {
            voice: "Try to match the outline on your screen. When it turns green, I'll count you down.",
            chat: "Try to match the outline on your screen. When it turns green, I'll count you down.",
          },
        },
      },
    },
    overlay: {
      // Skip-exercise button label, shown while reps are in progress.
      skipExercise: "Skip Exercise",
      // End-exercise prompt rendered when widget.showEndExercise == true.
      endExercise: "Press End Exercise to go to next exercise",
    },
    popups: {
      // Device-Ready confirmation popup (shown during Step 2).
      deviceReady: {
        title: "My Device is in place",
        buttonLabel: "Continue",
      },

      switchToNoCamera: {
        title: "Continue without camera",
        subtitle: "Tap here to switch back",
      },

      // Auto-pause popup shown when user is out of frame too long.
      notVisiblePause: {
        title: {
          app: {
            voice: "I'll wait here. Press the Resume button or say Myra Resume to continue.",
            chat: "I'll wait here. Press the Resume button to continue.",
          },
          gsa: {
            test1: {
              voice: "I'll wait here. Press the Resume button or say Myra Resume to continue.",
              chat: "I'll wait here. Press the Resume button to continue.",
            },
            test2: {
              voice: "I'll wait here. Press the Resume button or say Myra Resume to continue.",
              chat: "I'll wait here. Press the Resume button to continue.",
            },
          },
        },
        buttonLabel: "Resume",
      },
    },
    // Said when MediaPipe returns no landmarks at all. Two variants
    // selected via the hasEverSeenLandmarks signal in check_start_stage:
    //   firstTime → we've never detected the user since this stage started
    //               (likely a setup problem: camera angle, lighting, room).
    //   regressed → we had landmarks earlier and lost them
    //               (user walked out of frame; positioning problem).
    // Gated by a 10s cooldown and a type-change check, so these fire
    // at most once per distinct episode. Source: check_start_stage.dart
    // line ~2846 (hint dispatch).
    hint: {
      out_of_view: {
        firstTime: "I can't see you just yet. When you're ready, step into the frame so I can find you.",
        regressed: "I've lost sight of you. Step back into the frame whenever you're ready.",
      },
    },
  },

  // ── PHASE-1 STEP FLOW (NO-CAMERA PATH) ───────────────────────
  // No-camera exercise stage runs a 3-step pre-exercise flow.
  // Step 1 has its own intro key (step1Intro below) — copy matches
  // cameraStage.step1Intro today but is kept separate per doctrine.
  // Source: no_camera_exercise_stage.dart.
  noCamera: {
    // Step 1 — exercise intro for GSA no-camera path.
    // Vars: {displayName}, {totalReps}, {holdText}
    step1Intro: {
      announce: {
        app: {
          voice: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps{holdText} — no rush, I'll follow your pace.",
          chat: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps{holdText} — no rush, I'll follow your pace.",
        },
        gsa: {
          test1: {
            voice: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps{holdText} — no rush, I'll follow your pace.",
            chat: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps{holdText} — no rush, I'll follow your pace.",
          },
          test2: {
            voice: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps{holdText} — no rush, I'll follow your pace.",
            chat: "Thanks for sticking with it! Let's get started with the {displayName}. You'll do {totalReps} reps{holdText} — no rush, I'll follow your pace.",
          },
        },
      },
    },
    // Step 2 — device placement.
    devicePlacement: {

      //// Switch to Camera is disabled for Live. If we re-enable it, we'll need to re-add the "track your form with the camera" language to these messages.////
      // announce: {
      //   app: {
      //     voice: "Go ahead and place your device somewhere you can easily see it. Once it's set, just say 'start' or press the Start button, and I'll walk you through what's next. If you'd like, you can also track your form with the camera. Tap to learn more.",
      //     chat: "Go ahead and place your device somewhere you can easily see it. Once it's set, press Start and I'll walk you through what's next. If you'd like, you can also track your form with the camera. Tap to learn more.",
      //   },
      //   gsa: {
      //     test1: {
      //       voice: "Go ahead and place your device somewhere you can easily see it. Once it's set, just say 'start' or press the Start button, and I'll walk you through what's next. If you'd like, you can also track your form with the camera. Tap to learn more.",
      //       chat: "Go ahead and place your device somewhere you can easily see it. Once it's set, press Start and I'll walk you through what's next. If you'd like, you can also track your form with the camera. Tap to learn more.",
      //     },
      //     test2: {
      //       voice: "Go ahead and place your device somewhere you can easily see it. Once it's set, just say 'start' or press the Start button, and I'll walk you through what's next. If you'd like, you can also track your form with the camera. Tap to learn more.",
      //       chat: "Go ahead and place your device somewhere you can easily see it. Once it's set, press Start and I'll walk you through what's next. If you'd like, you can also track your form with the camera. Tap to learn more.",
      //     },
      //   },
      // },


      announce: {
        app: {
          voice: "Go ahead and place your device somewhere you can easily see it. Once it's set, just say 'start' or press the Start button, and I'll walk you through what's next.",
          chat: "Go ahead and place your device somewhere you can easily see it. Once it's set, press Start and I'll walk you through what's next.",
        },
        gsa: {
          test1: {
            voice: "Go ahead and place your device somewhere you can easily see it. Once it's set, just say 'start' or press the Start button, and I'll walk you through what's next.",
            chat: "Go ahead and place your device somewhere you can easily see it. Once it's set, press Start and I'll walk you through what's next.",
          },
          test2: {
            voice: "Go ahead and place your device somewhere you can easily see it. Once it's set, just say 'start' or press the Start button, and I'll walk you through what's next.",
            chat: "Go ahead and place your device somewhere you can easily see it. Once it's set, press Start and I'll walk you through what's next.",
          }
        }
      },
      helpOffer: {
        // ⚑ FLAG: source has a single string for both modes.
        app: {
          voice: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Ready button.",
          chat: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Ready button.",
        },
        gsa: {
          test1: {
            voice: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Ready button.",
            chat: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Ready button.",
          },
          test2: {
            voice: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Ready button.",
            chat: "It looks like you might still be setting up. Take your time — when your device is placed, just press the Ready button.",
          },
        },
      },
    },

    // Step 3 — controls handoff.

    controlsHandoff: {
      announce: {
        app: {
          voice: "Great! If you need to pause or have a question, just say pause or ask the question. When you're ready to begin, say 'ready' and there's a short countdown and a video to follow.",
          chat: "Great! Need a pause or have a question? Tap the Myra icon at the bottom left to chat. When you press Start, there's a short countdown and a video to follow.",
        },
        gsa: {
          test1: {
            voice: "Great! If you need to pause or have a question, just say pause or ask the question. When you're ready to begin, say 'ready' and there's a short countdown and a video to follow.",
            chat: "Great! Need a pause or have a question? Tap the Myra icon at the bottom left to chat. When you press Start, there's a short countdown and a video to follow.",
          },
          test2: {
            voice: "Great! If you need to pause or have a question, just say pause or ask the question. When you're ready to begin, say 'ready' and there's a short countdown and a video to follow.",
            chat: "Great! Need a pause or have a question? Tap the Myra icon at the bottom left to chat. When you press Start, there's a short countdown and a video to follow.",
          },
        },
      },
      helpOffer: {
        // ⚑ FLAG: source has a single string for both modes.
        app: {
          voice: "No rush — when you're ready to begin, just press the Ready to Start button.",
          chat: "No rush — when you're ready to begin, just press the Ready to Start button.",
        },
        gsa: {
          test1: {
            voice: "No rush — when you're ready to begin, just press the Ready to Start button.",
            chat: "No rush — when you're ready to begin, just press the Ready to Start button.",
          },
          test2: {
            voice: "No rush — when you're ready to begin, just press the Ready to Start button.",
            chat: "No rush — when you're ready to begin, just press the Ready to Start button.",
          },
        },
      },
    },
    popups: {
      // Step 3 — Ready-to-Start popup shown after controlsHandoff TTS completes.
      // Source: no_camera_exercise_stage.dart line 2103.
      readyToStart: {
        title: "Ready when you are.",
        buttonLabel: "Ready to Start",
      },

      // Legacy idle popup — non-GSA fallback path, same title as readyToStart
      // but a shorter button label. Both popups can be visible in the same
      // session (different _NoCameraStep states), so they're kept as separate
      // keys rather than collapsed.
      readyLegacy: {
        title: "Ready when you are.",
        buttonLabel: "Ready",
      },

      // Camera-denied bottom sheet — shown when the user taps "Track my form"
      // on the FormChip but denies the camera permission prompt.
      cameraDenied: {
        body: "No problem — we'll keep going without the camera. You can enable it any time from your device settings.",
        buttonLabel: "Got it",
      },

      // Step 2 — DeviceReadyBar shown bottom-anchored while waiting for user
      // to confirm device placement. The widget itself lives in
      // movement_test_widgets.dart (class DeviceReadyBar); both _wideLayout
      // and _stackedLayout render the same title + button strings.
      // ⚑ NAMING: sibling to cameraStage.popups.deviceReady, which is a
      // different popup with different copy ("My Device is in place" /
      // "Continue"). Resolver paths don't collide, but reader take note.
      deviceReady: {
        title: "Is your device placed safely?",
        buttonLabel: "Yes, start",
      },
    },

    // FormChip — affordance shown on Steps 2 and 3 of the no-camera flow
    // offering the user the option to switch to the camera-tracked path.
    // Tapping the chip opens the cameraInfoSheet (below). The chip's first
    // line has a short variant for narrow viewports.
    formChip: {
      labelLong: "Track my form with camera",
      labelShort: "Track my form",
      secondLine: "Tap here to find out more",
    },

    // Camera-info bottom sheet — opened when the user taps the FormChip.
    // Explains what camera tracking enables, then offers Use camera / Cancel.
    cameraInfoSheet: {
      body: "With your camera on, I can track your form — your movement patterns, speed, and range — during the test. Your video stays on your device. Nothing is recorded or sent anywhere. And it saves you two questions afterward.",
      confirmLabel: "Use camera",
      cancelLabel: "Cancel",
    },
  },

  // ── COUNTDOWN ────────────────────────────────────────────────
  // Used by every flow right before the user starts moving.
  // Single canonical string; both camera and no-camera paths use this.
  // Source: no_camera_exercise_stage.dart line 624,
  //         check_start_stage.dart lines 1575 and 2798 (now consolidated).
  countdown: {
    startExerciseAnnouncement: {
      app: {
        voice: "Three... Two... One...",
        chat: "Three... Two... One...",
      },
      gsa: {
        test1: {
          voice: "Three..., two..., one..., ...Start Exercise.",
          chat: "Three..., two..., one..., ...Start Exercise.",
        },
        test2: {
          voice: "Three..., two..., one..., ...Start Exercise.",
          chat: "Three..., two..., one..., ...Start Exercise.",
        },
      },
    },
    startExercise: "Three..., two..., one..., ...Start Exercise.",

    // Short trailing announcement fired at the moment rep-counting
    // actually begins. In-app (non-GSA) only — on the GSA path,
    // `countdown.startExercise` already ends with "...Start Exercise.",
    // so saying it again would be redundant.
    // Source: virtual_stage.dart line 1882.
    // Caller gates this on isSelfAssessment != "1" — that gate stays
    // in dart rather than being expressed as flow split here, since
    // it's flow-specific suppression rather than a copy variant.
    startExerciseShort: "Start Exercise!",
  },

  // ── VISIBILITY (mid-exercise) ────────────────────────────────
  // Camera-path mid-exercise feedback when user goes out of frame
  // and returns. Source: check_start_stage.dart.
  visibility: {
    // Said when user becomes visible again after losing visibility.
    // Source: line 2221.
    regained: "I can see you now.",

    // Said when user has been out of frame long enough to auto-pause.
    // Source: line 2455-2456.
    pauseUntilVisible: "I'm going to pause. When you're ready, let me know and I'll resume.",
  },

  // ── POSITION HELP (mid-exercise) ─────────────────────────────
  // Periodic check-ins when the user is having trouble getting into
  // the starting position. Two cycles, escalating from "I can help"
  // to "you can also skip". Source: check_start_stage.dart line 3592+.
  positionHelp: {
    checkInFirst:
      "Are you having trouble getting into position? I can help, " +
      "or if you're already in the starting position, just let me know " +
      "and I'll start the exercise. You can also ask me to pause if you " +
      "need a moment.",
    checkInSecond:
      "Still having trouble? I can try to help again, or you can skip " +
      "this exercise and we'll move to the next one. If you're already " +
      "in position, just let me know. You can also pause if you need a moment.",
    checkInFirstRegression: {
      app: {
        chat: "Looks like you've drifted out of position. Take a moment to get back to where you were — You can also pause if you need a moment.",
        voice: "Looks like you've drifted out of position. Take a moment to get back to where you were — you can ask me to pause if you need to.",
      },
      gsa: {
        chat: "Looks like you've drifted out of position. Take a moment to get back to where you were — You can also pause if you need a moment.",
        voice: "Looks like you've drifted out of position. Take a moment to get back to where you were — you can ask me to pause if you need to.",
      },
    },
    checkInSecondRegression: {
      app: {
        voice: "Still drifting? Try resetting to your starting position. If you're already in position, just let me know. You can also pause if you need a moment.",
        chat: "Still drifting? Try resetting to your starting position. If you're already in position, just let me know. You can also pause if you need a moment.",
      },
      gsa: {
        chat: "Still drifting? Try resetting to your starting position. If you're already in position, just let me know. You can also pause if you need a moment.",
        voice: "Still drifting? Try resetting to your starting position. If you're already in position, just let me know. You can also pause if you need a moment.",
      },
    },
    movedFromPosition: "Looks like you moved. Take your time getting back into position."
  },

  betweenReps: {
    lateSet: "You're nearly done. Take a breath and go when you're ready.",
    restedBrief: "No rush — go when you're ready."
  },

  // ── POST-EXERCISE QUESTIONS (in-app only) ────────────────────
  // After a user completes a movement, the in-app flow opens an RPE
  // dialog and then a symptom dialog. The RPE prompt is spoken by
  // the React-side `flow_rpe` voice-assembly handler (no dart-side
  // string to consolidate). The symptom prompt below IS spoken from
  // dart — fired between the user submitting RPE and the symptom
  // dialog opening.
  //
  // Source: post_questions_stage.dart line 362 (inside RpeScreen.onContinue).
  // Conceptually overlaps with `feelAffect.prompt` (the GSA-flow
  // feel-affect question), but kept separate per direction — the
  // two beats are owned by different code paths and may diverge.
  postQuestions: {
    // Vars: none.
    // Spoken immediately after the user submits an RPE value, just
    // before `_showSymptom1Dialog` opens.
    symptomPrompt: {
      app: {
        voice: "How did that movement check affect your pain? Would you say it felt better, felt the same, or felt worse?",
        // ⚑ FLAG: chat copy authored during migration — review.
        // The dart source has a single string used in both modes; this
        // chat version was tightened for an on-screen dialog where the
        // option recap is redundant (the dialog buttons are visible).
        chat: "How did that movement check affect your pain?",
      },
      gsa: {
        // ⚑ FLAG: post_questions_stage is in-app only by design; gsa
        // slots exist for schema symmetry. Resolver will only reach
        // these if flow=gsa, which shouldn't happen for this key —
        // pattern matches `exerciseIntro.cameraInApp.firstExercise`.
        test1: {
          voice: "How did that movement check affect your pain? Would you say it felt better, felt the same, or felt worse?",
          chat: "How did that movement check affect your pain?",
        },
        test2: {
          voice: "How did that movement check affect your pain? Would you say it felt better, felt the same, or felt worse?",
          chat: "How did that movement check affect your pain?",
        },
      },
    },
  },

  // ── EXIT HANDLER ─────────────────────────────────────────────
  exit: {
    // Deterministic first messages per reason: see `reasons` below
    // (moved here from CONSTANTS.EXIT_MYRA_MESSAGES)
    // Per-context screen copy + per-tile copy: see `byContext` below
    // (moved here from CONSTANTS.EXIT_CONFIGS)
    confirmExitGoodbye: "Thanks for giving the assessment a try. If you ever want to come back, I'll be here. Take care.", // batch: 1 (pageLoad)

    // ── Per-reason Myra replies (moved from CONSTANTS.EXIT_MYRA_MESSAGES) ─
    // Keyed by exit-tile reason. Spoken/shown when the user picks a tile
    // on the exit modal. Reasons match the `reason` field on EXIT_CONFIGS tiles.
    reasons: {
      confused: "No worries — what part is unclear? I'm happy to walk you through it.",
      technical: "Sorry about that. Can you tell me what's happening — is the screen frozen, or is something not responding?",
      why_info: "Great question. Your age, weight, and other details help me choose exercises at the right intensity and make sure they're safe. What specifically are you wondering about?",
      tracking: "I see you're having trouble with the tracking. Are the targets not showing up, or is the camera not picking up your body?",
    },

    // ── Per-context screen copy + per-tile copy ─────────────────
    //   (moved from CONSTANTS.EXIT_CONFIGS)
    // Pattern A lookup: components iterate the tile array from CONSTANTS
    // (carries reason routing key) and look up display copy here:
    //   exit.byContext[ctx].heading
    //   exit.byContext[ctx].subheading
    //   exit.byContext[ctx].tilesByReason[reason] => { title, desc }
    byContext: {
      gsa: {
        heading: "Having Trouble or Thinking About Leaving?",
        subheading: "Let us know if you need help or want to exit — we're here to support you.",
        tilesByReason: {
          confused: { title: "I'm not sure how to answer", desc: "Not sure what fits your situation? Myra can help with any questions you have." },
          technical: { title: "Are you having technical difficulties?", desc: "If something isn't working, Myra can help troubleshoot the issue." },
          leave: { title: "I want to leave the assessment", desc: "If you decide to leave, we'd love to hear how we can improve." },
        },
      },
      personalize: {
        heading: "We Can Help or Want to Exit?",
        subheading: "This information helps us create your personalized recovery plan.",
        tilesByReason: {
          why_info: { title: "Why do you need this information?", desc: "Curious why we're asking for age, weight, or other details? Myra can explain everything." },
          confused: { title: "I'm not sure how to answer", desc: "Not sure what to put? Myra can guide you through any question or field." },
          technical: { title: "Something isn't working", desc: "If the screen or buttons aren't responding, Myra can help troubleshoot the issue." },
          leave: { title: "I want to leave the assessment", desc: "If you decide to stop, we'd love to hear how we can improve your experience." },
        },
      },
      movement_test: {
        heading: "Tracking Trouble or Want to Exit?",
        subheading: "Let us know if you need help or want to exit — we're here to support you.",
        tilesByReason: {
          tracking: { title: "Tracking isn't working correctly", desc: "Having trouble with the camera or body tracking? Myra can help troubleshoot." },
          confused: { title: "I'm not sure how to answer", desc: "Not sure what fits your situation? Myra can help with any questions you have." },
          technical: { title: "Something isn't working", desc: "If the screen or buttons aren't responding, Myra can help troubleshoot the issue." },
          leave: { title: "I want to leave the assessment", desc: "If you decide to stop, we'd love to hear how we can improve your experience." },
        },
      },
    },
  },

  // ── FEEDBACK ─────────────────────────────────────────────────
  // Short acknowledgments Myra speaks back to the user.
  // Source: onDemandOverlayContainerNew.dart.
  feedback: {
    // Spoken when the user returns to correct device orientation
    // (after web-only orientation prompt). Fires from two paths:
    // setDeviceOrientation (line 1736) and isCorrectOrientation
    // (line 3367) — both speak the same string. Reaches both flows.
    acknowledge: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Thanks!",
        chat: "Thanks!",
      },
      gsa: {
        test1: {
          voice: "Thanks!",
          chat: "Thanks!",
        },
        test2: {
          voice: "Thanks!",
          chat: "Thanks!",
        },
      },
    },
  },

  // ── SKIP EXERCISE ────────────────────────────────────────────
  // Announcements and dialog prompts when the user skips or ends
  // an exercise. Used in both flows (GSA and in-app), though the
  // current dart call sites differ:
  //   - `skipExercise.noReps` / `someReps` / `endOrContinueAck` —
  //     reachable in-app only today (skipEndExercise returns early
  //     on isSelfAssessment == "1"), but kept flow-symmetric so
  //     future GSA skip behavior can use the same keys.
  //   - `skipExercise.skipFirstTest` / `skipSubsequent` —
  //     GSA-only voice-command responses, gated on _currentActivity.
  //
  // Source: onDemandOverlayContainerNew.dart.
  skipExercise: {
    // Announcement when user skips an exercise without completing
    // any reps. Source: line 2861 (variant when currentRepNo == 0).
    // No vars.
    noReps: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Looks like you skipped this exercise",
        chat: "Looks like you skipped this exercise",
      },
      gsa: {
        test1: {
          voice: "Looks like you skipped this exercise",
          chat: "Looks like you skipped this exercise",
        },
        test2: {
          voice: "Looks like you skipped this exercise",
          chat: "Looks like you skipped this exercise",
        },
      },
    },

    // Announcement when user ended/skipped after completing some
    // reps (currentRepNo > 0). Source: line 2861 (variant).
    // No vars.
    someReps: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Great effort! You did some reps for this exercise",
        chat: "Great effort! You did some reps for this exercise",
      },
      gsa: {
        test1: {
          voice: "Great effort! You did some reps for this exercise",
          chat: "Great effort! You did some reps for this exercise",
        },
        test2: {
          voice: "Great effort! You did some reps for this exercise",
          chat: "Great effort! You did some reps for this exercise",
        },
      },
    },

    // "End the session, or continue with the next exercise?" prompt.
    // Two source call sites consolidate to one key with a real
    // voice/chat split (the source strings genuinely differ):
    //   - voice (line 5102): voice-command response to LATER —
    //     starts with "Got it!" acknowledgment because it's a reply
    //     to a spoken command. Currently invoked via
    //     `tn.callNative('doSay', [...])` directly; migration routes
    //     it through audioManager.myraAnnounce.
    //   - chat (line 2898): fresh dialog prompt fired from
    //     showSkipExercisePopup.onEndSessionDialog when
    //     isMyraActive == false. The isMyraActive gate stays in
    //     dart (orthogonal to mode); the voice/chat split is what
    //     the resolver dispatches on.
    // No vars.
    endOrContinueAck: {
      app: {
        voice: "Got it! Would you like to end the session now or continue with the next exercise?",
        chat: "Do you want to end the session now? or continue with the next exercise?",
      },
      gsa: {
        // ⚑ FLAG: in-app only by design today; gsa slots filled for
        // schema symmetry per `postQuestions.symptomPrompt` precedent.
        test1: {
          voice: "Got it! Would you like to end the session now or continue with the next exercise?",
          chat: "Do you want to end the session now? or continue with the next exercise?",
        },
        test2: {
          voice: "Got it! Would you like to end the session now or continue with the next exercise?",
          chat: "Do you want to end the session now? or continue with the next exercise?",
        },
      },
    },

    // GSA voice-command response when the user skips during the
    // FIRST movement check (_currentActivity == 0). Source: line 5025.
    //
    // ⚑ FLAG: source string was incomplete ("...do you want to 'leave
    // the assessment " — trailing space, no closing quote, no period).
    // Reconstructed during migration — review.
    //
    // The voice copy mentions speaking the phrases 'something not
    // working' and 'leave the assessment' verbatim, which is voice-
    // mode specific. The chat variant is rephrased for tap-driven
    // interaction.
    // No vars.
    skipFirstTest: {
      // ⚑ FLAG: GSA-only by design; app slot filled for schema symmetry
      // per `exerciseIntro.cameraInApp.*` precedent.
      app: {
        voice: "Got it! Let me know how I can help — is it 'something not working,' or do you want to 'leave the assessment'?",
        // ⚑ FLAG: chat copy authored during migration — review.
        chat: "Got it! Let me know how I can help. Is something not working, or do you want to leave the assessment?",
      },
      gsa: {
        test1: {
          voice: "Got it! Let me know how I can help — is it 'something not working,' or do you want to 'leave the assessment'?",
          // ⚑ FLAG: chat copy authored during migration — review.
          chat: "Got it! Let me know how I can help. Is something not working, or do you want to leave the assessment?",
        },
        test2: {
          // ⚑ FLAG: caller gates on _currentActivity == 0, so test2
          // shouldn't reach this key in practice. Filled for schema
          // symmetry; falls through to test1 anyway via the resolver.
          voice: "",
          chat: "",
        },
      },
    },

    // GSA voice-command response when the user skips on a
    // SUBSEQUENT movement check (_currentActivity != 0).
    // Source: line 5027. No vars.
    skipSubsequent: {
      // ⚑ FLAG: GSA-only by design; app slot filled for schema symmetry.
      app: {
        voice: "Got it! Let's move on to the next step",
        chat: "Got it! Let's move on to the next step",
      },
      gsa: {
        test1: {
          // ⚑ FLAG: caller gates on _currentActivity != 0, so test1
          // (first activity) shouldn't reach this key. Filled for
          // schema symmetry.
          voice: "Got it! Let's move on to the next step",
          chat: "Got it! Let's move on to the next step",
        },
        test2: {
          voice: "Got it! Let's move on to the next step",
          chat: "Got it! Let's move on to the next step",
        },
      },
    },
  },

  // ── CONTROLS ─────────────────────────────────────────────────
  // Voice-command lifecycle responses — Myra acknowledging a
  // pause/resume command. Currently in-app behavior; gsa slots
  // filled for schema symmetry per `postQuestions.symptomPrompt`
  // precedent. Source: onDemandOverlayContainerNew.dart.
  controls: {
    // Spoken on RESUME_EXERCISE response. Source: line 5047.
    // No vars.
    resume: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Okay, resuming the exercise",
        chat: "Okay, resuming the exercise",
      },
      gsa: {
        test1: {
          voice: "Okay, resuming the exercise",
          chat: "Okay, resuming the exercise",
        },
        test2: {
          voice: "Okay, resuming the exercise",
          chat: "Okay, resuming the exercise",
        },
      },
    },

    // Spoken on START_PAUSE / PAUSE_EXERCISE response.
    // Source: line 5066. No vars.
    //
    // The voice copy includes a verbatim spoken phrase ("just say
    // 'Myra continue the exercise.'") that is voice-mode specific.
    // The chat variant directs the user to the resume button instead.
    pause: {
      app: {
        voice: "Okay, I’ve paused. When you’re ready, just say ‘Myra continue the exercise.‘",
        // ⚑ FLAG: chat copy authored during migration — review.
        chat: "Okay, I’ve paused. Press resume when you’re ready.",
      },
      gsa: {
        test1: {
          voice: "Okay, I’ve paused. When you’re ready, just say ‘Myra continue the exercise.‘",
          // ⚑ FLAG: chat copy authored during migration — review.
          chat: "Okay, I’ve paused. Press resume when you’re ready.",
        },
        test2: {
          voice: "Okay, I’ve paused. When you’re ready, just say ‘Myra continue the exercise.‘",
          // ⚑ FLAG: chat copy authored during migration — review.
          chat: "Okay, I’ve paused. Press resume when you’re ready.",
        },
      },
    },
  },

  // ── RPE RESPONSE ─────────────────────────────────────────────
  // Myra's spoken acknowledgment after the user submits an RPE
  // answer in the in-app dialog flow. Five variants keyed by RPE
  // integer in source (1/3/5/7/9); semantic names used here.
  // Caller maps int → key:
  //   1 → easy, 3 → kindaEasy, 5 → justRight, 7 → kindaHard, 9 → hard
  //
  // In-app only (called from didReceivedMyraResponse in
  // VirtualState.exerciseCompletedFirstTime); gsa slots filled for
  // schema symmetry per `postQuestions.symptomPrompt` precedent.
  //
  // Source: onDemandOverlayContainerNew.dart line 4603-4609.
  // Source punctuation/dash style preserved verbatim — some variants
  // end with "." and some don't.
  // No vars.
  rpeResponse: {
    easy: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Easy—great to hear! Let's move on to the next step.",
        chat: "Easy—great to hear! Let's move on to the next step.",
      },
      gsa: {
        test1: {
          voice: "Easy—great to hear! Let's move on to the next step.",
          chat: "Easy—great to hear! Let's move on to the next step.",
        },
        test2: {
          voice: "Easy—great to hear! Let's move on to the next step.",
          chat: "Easy—great to hear! Let's move on to the next step.",
        },
      },
    },
    kindaEasy: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Kinda Easy—great to hear! Let's move on to the next step",
        chat: "Kinda Easy—great to hear! Let's move on to the next step",
      },
      gsa: {
        test1: {
          voice: "Kinda Easy—great to hear! Let's move on to the next step",
          chat: "Kinda Easy—great to hear! Let's move on to the next step",
        },
        test2: {
          voice: "Kinda Easy—great to hear! Let's move on to the next step",
          chat: "Kinda Easy—great to hear! Let's move on to the next step",
        },
      },
    },
    justRight: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Just right—great! Let's move on to the next step",
        chat: "Just right—great! Let's move on to the next step",
      },
      gsa: {
        test1: {
          voice: "Just right—great! Let's move on to the next step",
          chat: "Just right—great! Let's move on to the next step",
        },
        test2: {
          voice: "Just right—great! Let's move on to the next step",
          chat: "Just right—great! Let's move on to the next step",
        },
      },
    },
    kindaHard: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Kinda Hard—thanks for letting me know. Let's move on to the next step.",
        chat: "Kinda Hard—thanks for letting me know. Let's move on to the next step.",
      },
      gsa: {
        test1: {
          voice: "Kinda Hard—thanks for letting me know. Let's move on to the next step.",
          chat: "Kinda Hard—thanks for letting me know. Let's move on to the next step.",
        },
        test2: {
          voice: "Kinda Hard—thanks for letting me know. Let's move on to the next step.",
          chat: "Kinda Hard—thanks for letting me know. Let's move on to the next step.",
        },
      },
    },
    hard: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Hard—thanks for letting me know. Let's move on to the next step",
        chat: "Hard—thanks for letting me know. Let's move on to the next step",
      },
      gsa: {
        test1: {
          voice: "Hard—thanks for letting me know. Let's move on to the next step",
          chat: "Hard—thanks for letting me know. Let's move on to the next step",
        },
        test2: {
          voice: "Hard—thanks for letting me know. Let's move on to the next step",
          chat: "Hard—thanks for letting me know. Let's move on to the next step",
        },
      },
    },
  },

  // ── SYMPTOM RESPONSE ─────────────────────────────────────────
  // Myra's spoken acknowledgment after the user submits a symptom
  // answer in the in-app dialog flow. Four variants keyed by
  // symptom code in source (IMPROVED / TOLERABLE / SAME / WORSENED);
  // semantic names used here. Caller maps code → key:
  //   IMPROVED → improved, TOLERABLE → tolerable,
  //   SAME → same, WORSENED → worsened
  //
  // Note: both `tolerable` and `same` open with "Same-" in the
  // source — that's intentional; both response codes correspond to
  // the user feeling "Same" but with different acknowledgment text.
  //
  // In-app only; gsa slots filled for schema symmetry.
  //
  // Source: onDemandOverlayContainerNew.dart line 4625-4633.
  // No vars.
  symptomResponse: {
    improved: {
      // ⚑ FLAG: source had a typo `We''ll` (double apostrophe);
      // corrected to `We'll` during migration.
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Better-That's great to hear. We'll keep moving forward with your recovery.",
        chat: "Better-That's great to hear. We'll keep moving forward with your recovery.",
      },
      gsa: {
        test1: {
          voice: "Better-That's great to hear. We'll keep moving forward with your recovery.",
          chat: "Better-That's great to hear. We'll keep moving forward with your recovery.",
        },
        test2: {
          voice: "Better-That's great to hear. We'll keep moving forward with your recovery.",
          chat: "Better-That's great to hear. We'll keep moving forward with your recovery.",
        },
      },
    },
    tolerable: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Same-Thanks for letting me know. We'll continue at a pace that feels manageable.",
        chat: "Same-Thanks for letting me know. We'll continue at a pace that feels manageable.",
      },
      gsa: {
        test1: {
          voice: "Same-Thanks for letting me know. We'll continue at a pace that feels manageable.",
          chat: "Same-Thanks for letting me know. We'll continue at a pace that feels manageable.",
        },
        test2: {
          voice: "Same-Thanks for letting me know. We'll continue at a pace that feels manageable.",
          chat: "Same-Thanks for letting me know. We'll continue at a pace that feels manageable.",
        },
      },
    },
    same: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Same-Got it. We'll stay consistent and keep monitoring how you feel.",
        chat: "Same-Got it. We'll stay consistent and keep monitoring how you feel.",
      },
      gsa: {
        test1: {
          voice: "Same-Got it. We'll stay consistent and keep monitoring how you feel.",
          chat: "Same-Got it. We'll stay consistent and keep monitoring how you feel.",
        },
        test2: {
          voice: "Same-Got it. We'll stay consistent and keep monitoring how you feel.",
          chat: "Same-Got it. We'll stay consistent and keep monitoring how you feel.",
        },
      },
    },
    worsened: {
      // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
      app: {
        voice: "Worse-Thanks for telling me. We'll take that into account as we move forward.",
        chat: "Worse-Thanks for telling me. We'll take that into account as we move forward.",
      },
      gsa: {
        test1: {
          voice: "Worse-Thanks for telling me. We'll take that into account as we move forward.",
          chat: "Worse-Thanks for telling me. We'll take that into account as we move forward.",
        },
        test2: {
          voice: "Worse-Thanks for telling me. We'll take that into account as we move forward.",
          chat: "Worse-Thanks for telling me. We'll take that into account as we move forward.",
        },
      },
    },
  },

  // ── PROGRESSION / REGRESSION ─────────────────────────────────
  // Loader and recommendation announcements during the in-app
  // exercise-swap flow (when RPE/symptom answers trigger a
  // progression or regression recommendation).
  //
  // ⚑ NOTE: identical strings appear (commented-out) in
  // post_questions_stage.dart — see Part 4 §4.2 of this migration
  // guide. The live version of the feature lives in
  // onDemandOverlayContainerNew.dart, not post_questions_stage.dart.
  //
  // In-app only; gsa slots filled for schema symmetry.
  // Source: onDemandOverlayContainerNew.dart.
  progressionRegression: {
    // Loader announcements (spoken while the recommendation API
    // call is in flight). Source: line 4738-4742.
    loading: {
      // Spoken when result == "progression". No vars.
      // Note: `…` is U+2026 (ellipsis character), preserved verbatim.
      progression: {
        // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
        app: {
          voice: "Great job! Finding your next challenge…",
          chat: "Great job! Finding your next challenge…",
        },
        gsa: {
          test1: {
            voice: "Great job! Finding your next challenge…",
            chat: "Great job! Finding your next challenge…",
          },
          test2: {
            voice: "Great job! Finding your next challenge…",
            chat: "Great job! Finding your next challenge…",
          },
        },
      },

      // Spoken when result is "regression" (or anything other than
      // "progression"). No vars.
      regression: {
        // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
        app: {
          voice: "Thanks for letting me know. I'll look for a better fit",
          chat: "Thanks for letting me know. I'll look for a better fit",
        },
        gsa: {
          test1: {
            voice: "Thanks for letting me know. I'll look for a better fit",
            chat: "Thanks for letting me know. I'll look for a better fit",
          },
          test2: {
            voice: "Thanks for letting me know. I'll look for a better fit",
            chat: "Thanks for letting me know. I'll look for a better fit",
          },
        },
      },
    },

    // Recommendation announcements (spoken once the API returns
    // the rank-1 replacement exercise). Source: line 4816, 4818.
    // Vars: {rankOneExercise}
    recommendation: {
      // Spoken when result == "progression" (the default branch
      // in source — line 4816 sets sayText, then 4818 overrides
      // only when result == "regression").
      progression: {
        // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
        app: {
          voice: "Got it! I've added {rankOneExercise} as a replacement for your next session",
          chat: "Got it! I've added {rankOneExercise} as a replacement for your next session",
        },
        gsa: {
          test1: {
            voice: "Got it! I've added {rankOneExercise} as a replacement for your next session",
            chat: "Got it! I've added {rankOneExercise} as a replacement for your next session",
          },
          test2: {
            voice: "Got it! I've added {rankOneExercise} as a replacement for your next session",
            chat: "Got it! I've added {rankOneExercise} as a replacement for your next session",
          },
        },
      },

      // Spoken when result == "regression". Vars: {rankOneExercise}
      regression: {
        // ⚑ FLAG: source has a single string; voice/chat split here per doctrine.
        app: {
          voice: "Done. I found {rankOneExercise} that should fit better and added it to your next session",
          chat: "Done. I found {rankOneExercise} that should fit better and added it to your next session",
        },
        gsa: {
          test1: {
            voice: "Done. I found {rankOneExercise} that should fit better and added it to your next session",
            chat: "Done. I found {rankOneExercise} that should fit better and added it to your next session",
          },
          test2: {
            voice: "Done. I found {rankOneExercise} that should fit better and added it to your next session",
            chat: "Done. I found {rankOneExercise} that should fit better and added it to your next session",
          },
        },
      },
    },
  },

  // ── REFERENCE DATA: EXERCISES ────────────────────────────────
  // Per-exercise audio + on-screen instruction text, keyed by exercise
  // name (the display name from the exercises table).
  //
  //   id     — exercises table primary key (stable, unique, never reused).
  //            Useful for analytics / API calls that still want the id.
  //   audio  — what Myra speaks at the start of the exercise (TTS).
  //            null when the exercise has no spoken instructions.
  //   screen — short on-screen instruction shown alongside the video.
  //
  // Lookup helper: getExerciseAudio(name) — defined alongside
  // resolveMessage at the bottom of this file. Prefer the helper over
  // reaching into this object directly so absent-name handling stays
  // in one place.
  //
  // Order: alphabetical by name for easy human scanning.
  exercises: {
    "Active Straight Leg Raise with Quad Set": {
      id: 152,
      audio: "Tighten your thigh, lift your straight leg off the ground, hold briefly, then lower slowly.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Alternating Lunges": {
      id: 140,
      audio: "Stand tall with feet hip-width apart and core engaged. Step one foot forward and lower your body until both knees are bent at about 90 degrees, keeping your front knee over your ankle and your torso upright. Push through the front heel to return to the starting position, then step forward with the opposite leg. Continue alternating sides in a smooth, controlled rhythm. Keep your back straight and avoid leaning forward.",
      screen: "Stand tall with feet hip-width apart, arms at your sides or hands on hips for balance. Maintain an upright posture and engage your core.",
    },
    "Alternating Punch with Resistance": {
      id: 172,
      audio: "Hold resistance bands at chest level. Punch one arm forward, return, then switch sides in a controlled motion.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Assisted Seated Trunk Rotation": {
      id: 139,
      audio: "Sit upright with hands on thighs. Gently rotate upper body to one side, pause, return to center, and repeat to the other side. Avoid twisting hips or leaning.",
      screen: "Sit upright with both hands on thighs.",
    },
    "Beginner Leg Single Prone Raise": {
      id: 256,
      audio: null,
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Bird Dog": {
      id: 62,
      audio: "Lift one arm and the opposite leg, keeping your back flat. Lower, then switch sides.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Bird Dog Hold": {
      id: 61,
      audio: "Lift one arm and the opposite leg, keeping your back flat. Lower, then switch sides.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Cat Cow Pose": {
      id: 91,
      audio: "Start on your hands and knees. As you inhale, arch your back and lift your head and tailbone. As you exhale, round your spine and tuck your chin toward your chest. Keep moving with your breath.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Child pose": {
      id: 74,
      audio: "From hands and knees, sit your hips back toward your heels and reach your arms forward. Lower your chest and hold.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Circular Shoulder Pendulum with Table Support": {
      id: 247,
      audio: "With your left arm hanging off the table, move it in small circles in both directions.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Clam Shell with Band": {
      id: 176,
      audio: null,
      screen: "Lie on your right side, facing the camera.",
    },
    "Clam Shells": {
      id: 68,
      audio: "Keeping your feet together, lift your top knee as high as you can without rolling your hips. Then lower and repeat.",
      screen: "Lie on your right side, facing the camera.",
    },
    "Corner Pec Major Stretch": {
      id: 212,
      audio: "Stand in a corner with hands on the walls, step forward slightly to stretch your chest. Hold and release.",
      screen: "Stand tall, facing the camera.",
    },
    "Crossover Lunge": {
      id: 141,
      audio: "Stand upright and step one leg diagonally behind and across the other leg, lowering into a lunge. Push through the front heel to return to start, then alternate sides. Keep chest up and movement controlled.",
      screen: "Stand tall, feet hip-width apart, hands on hips or arms forward for balance.",
    },
    "Curl Ups": {
      id: 81,
      audio: "Lift your head and upper shoulders off the ground to touch the target, then lower back down with control.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Dead Bug": {
      id: 54,
      audio: "Extend one arm and the opposite leg while keeping your back flat. Return to center, then switch sides and repeat.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Dead Bug with Heel Taps": {
      id: 94,
      audio: "Lower one arm to your side and tap the opposite heel to the floor. Return to start, then switch sides and repeat.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Doorway Pec Stretch at 120 Degrees Abduction": {
      id: 173,
      audio: "Place your hands on the doorway above your head. Step forward until you feel a stretch in your upper chest. Hold and release.",
      screen: "Stand tall, facing the camera.",
    },
    "Doorway Pec Stretch at 60 Elevation": {
      id: 175,
      audio: "Place your hands on the doorway slightly below shoulder height. Step forward until you feel a stretch in your lower chest. Hold and release.",
      screen: "Stand tall, facing the camera.",
    },
    "Doorway Pec Stretch at 90 Degrees Abduction": {
      id: 171,
      audio: "Place your hands on a doorway at shoulder height, step forward slightly, and feel the stretch in your chest. Hold and release.",
      screen: "Stand tall, facing the camera.",
    },
    "Doorway Rhomboid Stretch": {
      id: 222,
      audio: "Hold the doorway with both hands, step back slightly, and lean to stretch between your shoulder blades.",
      screen: "Stand tall, facing the camera.",
    },
    "Downward Dog": {
      id: 168,
      audio: "Start on hands and knees. Lift your hips up and back, straightening your legs as you press your heels toward the ground.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Flexion-Extension Shoulder Pendulum with Table Support": {
      id: 246,
      audio: "With your left arm hanging off the table, gently swing it forward and back in a controlled motion.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Gastroc Stretch on Wall": {
      id: 185,
      audio: "Place one foot back, press the heel into the ground, and lean forward slightly to stretch the calf. Hold and switch.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Goblet Squat with Kettlebell": {
      id: 253,
      audio: "Stand tall holding kettlebell at chest level. Lower into a squat, keeping elbows close to knees. Push through heels to return to standing. Maintain upright posture and engage core throughout.",
      screen: "Stand tall, feet shoulder-width apart, holding kettlebell close to chest with both hands.",
    },
    "Heel Raise on Step": {
      id: 218,
      audio: "Stand tall with the balls of your feet on the edge of a step, heels hanging off. Slowly lift your heels as high as possible, rising onto your toes, then lower your heels below the step level for a full stretch. Keep knees straight but not locked, and move in a slow, controlled rhythm. Engage your calves throughout the motion.",
      screen: "Stand tall on a step or sturdy platform with the balls of your feet on the edge and heels hanging slightly off.",
    },
    "Heel Slide": {
      id: 264,
      audio: "Lie on your back with your legs straight. Slide your heel slowly toward your hip, bending your knee upward. Pause briefly, then straighten your leg back down with control.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Hooklying Single Leg Bent Knee Fallouts with Resistance": {
      id: 231,
      audio: "Lie on your back with knees bent and a band around your thighs. Let your left knee drop outward, then return.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Horizontal Shoulder Pendulum with Table Support": {
      id: 245,
      audio: "With your left arm hanging off the table, gently swing it side to side in a relaxed motion.",
      screen: "Stand tall, facing the camera.",
    },
    "Inchworm": {
      id: 267,
      audio: "Place your hands on your hips, press them forward, and gently lean your upper body back. Then return to standing.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Isometric Shoulder Abduction at Wall": {
      id: 242,
      audio: "With your left arm at your side, press outward against the wall without moving. Hold, then release.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Isometric Shoulder Extension at Wall": {
      id: 200,
      audio: "Stand with your hands against the wall. Push backward into the wall without moving your arms. Hold and release.",
      screen: "Stand tall, facing the camera.",
    },
    "Isometric Shoulder Flexion at Wall": {
      id: 207,
      audio: "Press your hand into the wall in front of you as if lifting your arm. Hold and release.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "ITB Stretch at Wall": {
      id: 232,
      audio: "Face the camera, stand sideways near a wall, cross your left leg behind, and lean toward the wall. Hold and switch sides.",
      screen: "Stand tall, facing the camera.",
    },
    "Lateral Lunge": {
      id: 53,
      audio: "Shift weight to one side, bend that knee while keeping the other leg straight; push back to start. Return to center and repeat to the other side. Keep chest lifted and hips back.",
      screen: "Stand tall, feet wider than shoulder width, hands on hips (or crossed on chest).",
    },
    "Lateral Single Leg Lunge Jumps": {
      id: 153,
      audio: "Start upright with knees slightly bent. Jump laterally to land on one leg, bending the knee to absorb impact. Push off to jump back to the starting side. Maintain balance and soft landings.",
      screen: "Stand tall, feet hip-width apart, knees slightly bent, arms at sides.",
    },
    "Mountain Climbers": {
      id: 143,
      audio: null,
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Nerve Glide": {
      id: 265,
      audio: "Lie on your back and hold your leg behind the thigh. Keep your knee bent. Slowly raise your toes upward, then bring them back down. Move gently and stay relaxed.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "New Exercise": {
      id: 268,
      audio: "Bend your knees and hips to lower into a squat, keeping your chest up and weight in your heels. Then stand back up tall.",
      screen: "Stand tall, facing the camera.",
    },
    "Overhead Reach": {
      id: 258,
      audio: null,
      screen: "Stand tall, facing the camera.",
    },
    "Plank Against the Wall": {
      id: 88,
      audio: "Stand facing wall with hands flat at shoulder height. Walk feet back until body forms a straight line from head to heels. Engage core and hold position. Keep shoulders down, neck neutral, and body aligned.",
      screen: "Stand facing the wall, feet hip-width apart, hands pressed against wall at shoulder height.",
    },
    "Plank Arms Extended": {
      id: 71,
      audio: "From hands and knees, step your feet back to straighten your legs into a full plank. Keep your core tight and hold.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Plank Arms Extended On Knees": {
      id: 100,
      audio: "From your plank position on knees, walk your hands forward into the targets while keeping your core tight and body aligned.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Plank On Elbows": {
      id: 70,
      audio: "Lift your body off the ground, keeping a straight line from head to heels. Engage your core and hold for the full time-unless you feel any pain.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Plank On Elbows On Knee": {
      id: 99,
      audio: "With your elbows under your shoulders and knees on the floor, lift your hips and hold your body in a straight line from head to knees.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Alternating Arm and Leg Lifts": {
      id: 149,
      audio: null,
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Leg Extension": {
      id: 93,
      audio: "While lying face down, lift one leg off the ground while keeping it straight. Lower with control, then switch sides.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Press Up": {
      id: 85,
      audio: "Press through your hands to lift your chest while keeping your hips on the ground. Pause briefly, then lower back down.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Quadriceps Stretch with Strap": {
      id: 192,
      audio: "Lie on your stomach, loop a strap around your ankle, and gently pull your heel toward your glutes. Hold and release.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Scapular Retraction": {
      id: 208,
      audio: "Lie face down, squeeze your shoulder blades together while keeping your arms at your sides, then relax.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Scapular Retraction Arms at Side": {
      id: 155,
      audio: "Lie on your stomach, squeeze your shoulder blades together, and lift your arms slightly. Hold and release.",
      screen: "Lie flat on your stomach, with your left side closest to the camera..",
    },
    "Prone Scapular Retraction in Abduction": {
      id: 156,
      audio: "Lie face down, extend your arms outward, then squeeze your shoulder blades together. Hold and release.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Shoulder Horizontal Abduction": {
      id: 157,
      audio: "Lie on your stomach with arms out to the sides. Lift them slightly, hold, then lower.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Prone Single Arm Shoulder Y": {
      id: 158,
      audio: "Lie on a table or bed with one arm hanging off the side. Lift your arm forward into a Y shape, hold briefly, then lower with control.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Quadratus Lumborum Stretch": {
      id: 132,
      audio: "Reach one arm overhead and lean to the side, feeling the stretch in your lower back. Hold and switch sides.",
      screen: "Stand tall, facing the camera.",
    },
    "Quadruped Bent Leg Hip Extension": {
      id: 145,
      audio: null,
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Quadruped Fire Hydrant": {
      id: 252,
      audio: "From hands and knees, lift your left knee outward without twisting your body, then lower.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Quadruped Leg Lifts": {
      id: 146,
      audio: null,
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Renegade Row": {
      id: 266,
      audio: "Lift one hand off the ground and pull your elbow back. Place it down and switch sides.",
      screen: "Get on your hands and knees, with your left side closest to the camera.",
    },
    "Reverse Fly with Anchored Resistance": {
      id: 167,
      audio: "Hold the band with both hands in front of you. Pull your arms outward to shoulder height, then return slowly.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Scapular Retraction with Resistance": {
      id: 209,
      audio: "Hold a band, pull your shoulder blades together while keeping your arms straight. Return slowly.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Seated Bicycle Crunch": {
      id: 110,
      audio: "Sit tall with feet flat and hands gently behind head. Engage core before movement and perform controlled alternating leg lifts; avoid rounding the spine.",
      screen: "Sit tall on a sturdy chair, feet flat, hands gently behind head.",
    },
    "Seated Bird Dog": {
      id: 114,
      audio: "Sit near the edge, hands on thighs, chest lifted. Engage core and extend one arm and opposite leg forward, maintaining balance and steady posture.",
      screen: "Sit near edge of chair, feet hip-width apart, hands on thighs.",
    },
    "Seated Calf Towel Stretch": {
      id: 213,
      audio: "Sit with legs extended, loop a towel around your foot, and gently pull toward you. Hold and switch sides.",
      screen: "Sit sideways on the floor with your legs in front of you, with your right shoulder closest to the camera",
    },
    "Seated Crunch": {
      id: 107,
      audio: "Sit upright with feet flat and hands behind head. Engage core and flex slightly forward from ribs while keeping spine neutral and shoulders relaxed.",
      screen: "Sit tall, feet flat, hands gently behind head.",
    },
    "Seated Dead Bug": {
      id: 113,
      audio: "Sit tall near front of chair, feet flat, hands on thighs. Engage core, lift one knee at a time without leaning back; keep torso stable and controlled.",
      screen: "Sit upright near front of chair, knees 90°, hands resting on thighs.",
    },
    "Seated Glute Stretch": {
      id: 112,
      audio: "Sit tall, feet flat on the floor, hands on thighs. Keep chest lifted, spine straight, and apply gentle downward pressure for stretch — no forcing.",
      screen: "Sit tall, feet flat, hands resting on thighs.",
    },
    "Seated Hamstring Stretch": {
      id: 137,
      audio: "From the starting position with both knees at 90°, extend one leg forward reaching toward the ankle while keeping spine straight. Return the foot to floor and alternate to the other side. Movement comes from a gentle hip hinge, not the back.",
      screen: "Sit tall at edge of chair facing left, both knees bent at 90°.",
    },
    "Seated Hip Abduction": {
      id: 124,
      audio: "Sit upright, feet and knees together, hands resting on thighs. Engage glutes, move knees outward without leaning; control motion throughout.",
      screen: "Sit upright, feet and knees together, hands on thighs or chair.",
    },
    "Seated Hip Adduction Isometrics with Ball": {
      id: 182,
      audio: "Sit upright with ball or pillow between knees, hands on thighs. Gently squeeze inward using inner thighs; maintain upright spine and steady breathing.",
      screen: "Sit tall, feet flat, small ball/pillow between knees, hands resting on thighs.",
    },
    "Seated Hip External and Internal Rotation": {
      id: 262,
      audio: "Sit tall, knees bent 90 degrees, hands on sides or thighs. Rotate lower legs outward (external) and inward (internal) from the hips only; keep thighs still and motion smooth.",
      screen: "Sit tall, knees 90 degrees, feet flat, hands on sides or on thighs for balance.",
    },
    "Seated Hip External and Internal Rotation Right": {
      id: 261,
      audio: null,
      screen: "Sit on a chair, facing the camera.",
    },
    "Seated Hip Hinge": {
      id: 109,
      audio: "Sit back in chair, hands behind head, feet flat. Hinge forward from hips keeping spine neutral and back straight; avoid rounding or collapsing chest.",
      screen: "Sit back in chair, feet flat, hands gently behind head.",
    },
    "Seated Hip Internal Rotation": {
      id: 138,
      audio: "Sit tall with hands on sides or thighs. Rotate lower leg outward from the knee (to create internal rotation at the hip); keep movement slow and symmetrical.",
      screen: "Sit tall, knees 90°, feet flat, hands on sides or on thighs for balance .",
    },
    "Seated Isometric Hip Abduction": {
      id: 181,
      audio: null,
      screen: "Sit on a chair, facing the camera.",
    },
    "Seated Isometric Knee Extension": {
      id: 233,
      audio: "Sit tall, press your foot into the floor as if trying to straighten your knee. Hold, then release.",
      screen: "Sit on a chair, turn your chair sideways, with your shoulder closest to the camera.",
    },
    "Seated Lateral Muscles Stretch": {
      id: 136,
      audio: "Reach one arm overhead and bend to the side, stretching your torso. Hold and switch sides.",
      screen: "Sit on a chair, facing the camera.",
    },
    "Seated Long Arc Quad": {
      id: 199,
      audio: "Sit tall and extend one leg straight, fully straightening the knee. Lower with control and switch sides.",
      screen: "Sit on a chair, turn your chair sideways to your left, with your right shoulder closest to the camera.",
    },
    "Seated Low to High Torso Rotation": {
      id: 134,
      audio: "Sit tall with feet flat and hands on thighs. Begin rotating the torso to one side while rising the arms and chest slightly in a low?to?high arc; return to center and alternate sides. Keep the motion smooth and controlled through the core.",
      screen: "Sit tall on a chair, feet flat, hands on thighs.",
    },
    "Seated Lumbar Flexion": {
      id: 111,
      audio: "Sit tall on a chair, feet flat, hands on thighs. Slowly bend forward from the hips, reaching toward the floor, keeping movement gentle and controlled.",
      screen: "Sit tall on a chair, feet flat, hands resting on thighs.",
    },
    "Seated Single Knee To Chest": {
      id: 125,
      audio: "Sit tall with feet flat and hands on thighs. Lift one knee toward your chest, guiding it gently upward with your hands. Lower to starting position and repeat on the other side. Keep your back upright and movement slow.",
      screen: "Sit tall, feet flat, hands on thighs.",
    },
    "Seated Spine Rotation Stretch": {
      id: 133,
      audio: "Sit tall with feet flat and hands on thighs. Rotate your upper body gently to one side, hold briefly, then return to center and repeat to the other side. Keep hips facing forward and movement slow.",
      screen: "Sit tall, feet flat, hands resting on thighs.",
    },
    "Seated Squat": {
      id: 120,
      audio: "Stand tall, arms forward for balance. Engage your core, lower slowly with control to the seat, then press through heels to lift slightly off the seat. Maintain upright posture throughout.",
      screen: "Stand tall, feet shoulder-width apart, arms forward for balance.",
    },
    "Seated Straight Leg Raise": {
      id: 121,
      audio: "Sit tall near the front of the chair, facing left, with hands on thighs. Straighten and lift one leg slowly, keeping the knee extended and upper body steady. Return to starting position and alternate sides.",
      screen: "Sit tall on a chair facing left, right shoulder toward camera, hands resting on thighs.",
    },
    "Seated Toe Tap": {
      id: 108,
      audio: "Sit tall with feet flat and hands on thighs. Alternate tapping toes up and down, returning to flat between each tap while maintaining upright posture and steady core.",
      screen: "Sit tall, feet flat, hands on thighs.",
    },
    "Seated Trunk Rotation": {
      id: 122,
      audio: "Sit upright with feet flat and arms crossed over your chest. Rotate torso gently to one side, return to center, and repeat to the other side. Keep hips still and motion controlled.",
      screen: "Sit tall with feet flat on the floor.",
    },
    "Seated Trunk Side Bending": {
      id: 135,
      audio: "Sit tall with feet flat and arms hanging by your sides. Gently bend your torso to one side, reaching your hand toward the floor; return to center and repeat to the other side. Keep spine long and movement smooth.",
      screen: "Sit tall, feet flat, arms hanging by your sides.",
    },
    "Shoulder Abduction - Palms Down": {
      id: 161,
      audio: "Raise your arms out to the sides with palms facing down until shoulder height, then lower with control.",
      screen: "Stand tall, facing the camera.",
    },
    "Shoulder Adduction with Anchored Resistance": {
      id: 221,
      audio: "Hold a band at your side with your left hand and pull your arm inward toward your body, then return.",
      screen: "Stand tall, facing the camera.",
    },
    "Shoulder Extension with Resistance - Neutral": {
      id: 163,
      audio: "Hold the band, arms at your sides. Pull straight back, keeping elbows straight, then return slowly.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Shoulder External Rotation with Anchored Resistance with Towel Under Elbow": {
      id: 220,
      audio: "Hold a band with your left hand, elbow bent at 90 degrees, and rotate your arm outward while keeping a towel under your elbow.",
      screen: "Stand tall, facing the camera.",
    },
    "Shoulder Horizontal Abduction - Thumbs Up": {
      id: 162,
      audio: "Extend your arms forward at shoulder height, then move them outward with thumbs up. Return and repeat.",
      screen: "Stand tall, facing the camera.",
    },
    "Shoulder Internal Rotation with Resistance": {
      id: 249,
      audio: "With your left elbow at your side, pull the band inward toward your stomach, then return slowly.",
      screen: "Stand tall, facing the camera.",
    },
    "Side Plank Against the Wall": {
      id: 89,
      audio: "Stand sideways to wall, forearm on wall, elbow bent 90°. Step feet slightly away from wall to create a side-plank angle. Engage core and glutes to hold. Keep body straight from head to heels.",
      screen: "Stand sideways to wall, feet shoulder-width apart, forearm pressed lightly against wall at shoulder height.",
    },
    "Side Plank On Elbow": {
      id: 72,
      audio: "Lift your hips off the ground into a straight line from shoulders to feet. Keep your core tight and hold for the full time-unless you feel any pain.",
      screen: "Lie on your right side, facing the camera.",
    },
    "Side Plank On Elbow On Knees": {
      id: 101,
      audio: "Lift your hips off the ground and hold a straight line from your head to your knees. Keep your core tight and stay steady.",
      screen: "Lie on your right side, facing the camera.",
    },
    "Side Plank with Clam": {
      id: 250,
      audio: "In a side plank on your left forearm, open your top knee while keeping feet together, then close.",
      screen: "Lie on your right side, facing the camera.",
    },
    "Side Reach": {
      id: 259,
      audio: null,
      screen: "Stand tall, facing the camera.",
    },
    "Side Stepping with Resistance at Thighs": {
      id: 189,
      audio: "Maintain slight knee bend and core engagement. Step laterally to one side keeping band tension, bring feet back to start, then repeat to the other side. Avoid knees collapsing inward.",
      screen: "Stand tall, band looped above knees, feet hip-width, hands on hips (or chair for balance).",
    },
    "Side to Side Bend": {
      id: 116,
      audio: "Stand tall with arms relaxed at your sides. Slide one hand down the same-side thigh as you bend gently, return to center, then repeat to the other side. Keep core engaged and movement smooth.",
      screen: "Stand tall, feet shoulder-width apart, arms at sides.",
    },
    "Sidelying Hip Abduction": {
      id: 237,
      audio: "Lie on your left side with legs straight. Lift your right leg upward, then lower with control.",
      screen: "Lie on your right side, facing the camera.",
    },
    "Sidelying Hip Abduction with Resistance at Thighs": {
      id: 238,
      audio: "Lie on your left side with a band around your thighs. Lift your right leg upward against resistance, then lower.",
      screen: "Lie on your right side, facing the camera.",
    },
    "Single Arm Doorway Pec Stretch at 90 Degrees Abduction": {
      id: 224,
      audio: "Place your left hand on a doorway at shoulder height, step forward slightly, and feel the stretch in your chest.",
      screen: "Stand tall, facing the camera.",
    },
    "Single Arm Shoulder Extension with Resistance": {
      id: 219,
      audio: "Hold a resistance band with your left hand. Pull your arm straight back, keeping it close to your body, then return.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Single Leg Bridge": {
      id: 148,
      audio: null,
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Sit to Stand": {
      id: 119,
      audio: "Sit down with feet flat and arms forward. Engage core, press through heels to stand up, then lower back down to the chair with control. Maintain upright posture and avoid using momentum.",
      screen: "Sit down and near the edge of a chair, feet hip-width apart, arms forward for balance.",
    },
    "Sitting Knee Extension with Resistance": {
      id: 197,
      audio: "Sit tall with a resistance band around your ankle. Straighten your leg, then return slowly.",
      screen: "Sit on a chair, turn your chair sideways to your left, with your right shoulder closest to the camera.",
    },
    "Soleus Stretch on Wall": {
      id: 186,
      audio: "Step one foot back, bend both knees slightly, and press your heel down to stretch the lower calf. Hold and switch.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Squat": {
      id: 52,
      audio: "Stand tall with feet shoulder-width apart, arms extended forward. Lower hips back and down into a squat until thighs are parallel to floor. Push through heels to return to standing. Keep chest up and knees tracking over toes.",
      screen: "Stand tall, feet shoulder-width apart, arms forward for balance.",
    },
    "Squat to Heel Raise": {
      id: 188,
      audio: "Perform a controlled squat; as you rise, lift heels into a heel raise. Lower heels, reset to start, and repeat. Keep torso upright and knees tracking over toes.",
      screen: "Stand tall, feet hip-width, arms forward for balance.",
    },
    "Standing Anti-Rotation Press with Anchored Resistance": {
      id: 159,
      audio: "Hold the band at chest level, press straight forward, resisting rotation. Bring it back and repeat.",
      screen: "Stand tall, facing the camera.",
    },
    "Standing Calf Stretch Against the Wall": {
      id: 78,
      audio: "Stand facing the wall, step one foot back keeping the heel flat and toes forward. Lean gently toward the wall until you feel a stretch in the back calf. Hold briefly, then return to start. Keep both feet pointing forward and back straight.",
      screen: "Stand sideways to camera, facing a wall for support, one foot forward and one back.",
    },
    "Standing Hip Abduction": {
      id: 227,
      audio: "Stand tall with core engaged and hands for balance. Shift weight to your support leg and lift your working leg outward to the side without leaning. Return to start and repeat for the same leg. Keep toes forward and motion controlled.",
      screen: "Stand tall, feet hip-width apart, hands on hips or lightly on a chair for support.",
    },
    "Standing Hip Abduction with Anchored Resistance": {
      id: 226,
      audio: "Stand upright with resistance band secured to your working leg. Keep torso steady and move the leg outward against the band, then return to start and repeat. Maintain controlled motion and steady tension throughout.",
      screen: "Stand tall facing camera, resistance band anchored at ankle level, band around working ankle, holding chair or wall for balance.",
    },
    "Standing Hip Extension": {
      id: 128,
      audio: "Stand tall with core engaged and hands for support. Shift weight to your support leg and extend your working leg straight back without leaning forward. Return to start and repeat for the same leg. Squeeze glute at the top, keeping back neutral.",
      screen: "Stand sideways to camera, feet hip-width apart, hands on hips or holding a chair lightly for support.",
    },
    "Standing Hip Extension with Chair": {
      id: 177,
      audio: "Stand upright holding chair for balance. Shift weight to your support leg and extend the working leg straight back while keeping your torso upright. Return to start and repeat for the same leg. Keep movement slow and avoid arching your back.",
      screen: "Stand sideways to camera, one hand on chair for balance, feet hip-width apart.",
    },
    "Standing Hip External Rotation": {
      id: 131,
      audio: "Lift one foot slightly and rotate the leg outward from the hip; return to neutral and repeat. Keep knees softly bent, pelvis level, and motion controlled.",
      screen: "Stand tall, feet hip-width, hands on hips or light chair support.",
    },
    "Standing Hip Flexion with Anchored Resistance and Chair Support": {
      id: 183,
      audio: "Stand tall holding a chair for support, resistance band anchored behind you. Lift your leg forward from the hip against the band’s resistance, keeping torso upright and knee slightly bent. Return to start and repeat for the same leg. Maintain smooth, controlled motion.",
      screen: "Stand sideways to camera, holding a chair for balance. Resistance band anchored behind you at ankle level to the working leg.",
    },
    "Standing Hip Flexor Stretch": {
      id: 76,
      audio: "Start with feet together. Step one leg forward into a half-lunge position, keeping back leg straight and torso upright. Gently shift hips forward to stretch the front of the rear hip. Return to starting position and repeat on the other side.",
      screen: "Stand tall, feet together, hands on hips or resting lightly on thighs. Make sure your camera is between 2 and 3 feet off the floor for this exercise to detect your movement properly.",
    },
    "Standing Hip Internal Rotation": {
      id: 130,
      audio: "Stand tall on one leg with light support if needed. Lift your knee to hip height, then rotate your hip inward while turning the lower leg outward. Return to start and repeat for the same leg. Keep torso upright and motion controlled.",
      screen: "Stand tall, feet hip-width apart, hands on hips or light chair support.",
    },
    "Standing Hip Lateral Raise": {
      id: 127,
      audio: "Stand tall with core engaged and hands for support. Lift one leg out to the side while keeping torso upright, then return to start. Repeat on the other side, alternating sides each time. Keep toes facing forward and avoid leaning.",
      screen: "Stand tall, feet hip-width apart, hands on hips or chair for balance.",
    },
    "Standing Isometric Shoulder External Rotation with Doorway": {
      id: 203,
      audio: "Stand next to a doorway, press your forearm outward against the frame. Hold and release.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Standing Isometric Shoulder Internal Rotation at Doorway": {
      id: 244,
      audio: "With your left forearm against the doorway at 90 degrees, press inward without moving. Hold, then release.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Standing ITB Stretch - Side Bend": {
      id: 239,
      audio: "Stand tall with feet together. Step one foot forward and slightly across the other leg so the legs are crossed at the ankles. Raise the arm on the same side as the back leg overhead and gently bend your torso toward that side, feeling a stretch along the outer hip and thigh. Return to standing and repeat for the same side for the full set. Keep torso facing forward and movement slow and controlled.",
      screen: "Stand tall with feet together, arms relaxed at your sides, and posture upright",
    },
    "Standing ITB Stretch - Toe Touch": {
      id: 154,
      audio: "Stand tall with feet together. Step one foot forward and slightly across the other so the legs are crossed at the ankles. Hinge forward from the hips and reach toward your toes while keeping both knees straight. Hold briefly, feeling a stretch along the outer hip and thigh of the back leg. Return to standing and repeat for the same side for the full set. Keep your back flat, torso facing forward, and movement slow and controlled.",
      screen: "Stand tall with feet together, arms relaxed at your sides, and posture upright",
    },
    "Standing Lumbar Extension": {
      id: 86,
      audio: "Stand tall facing left with feet shoulder-width apart, hands on hips. Gently arch backward from the lower spine, keeping hips stable. Return to neutral and repeat. Keep motion slow and controlled.",
      screen: "Stand tall facing left, feet shoulder-width apart, hands on hips.",
    },
    "Standing Passive Hip Flexion with Chair": {
      id: 129,
      audio: "Stand tall, lift one knee toward your chest using your hands to support and gently pull the knee closer to your body. Lower your leg back to starting position and repeat on the other side. Keep torso upright and avoid leaning forward.",
      screen: "Stand tall, feet hip-width apart, hands on hips.",
    },
    "Standing Quadratus Lumborum Stretch with Doorway": {
      id: 225,
      audio: "Face the camera, grab the doorway with both hands, and lean back to stretch your lower back.",
      screen: "Stand tall, facing the camera.",
    },
    "Standing Quadriceps Stretch with Chair": {
      id: 75,
      audio: "Stand upright holding a chair or wall for balance. Lift one leg back, bend the knee, grab your ankle, and gently pull your foot toward your upper leg until you feel a stretch in the front of the thigh. Keep posture tall and knees close together.",
      screen: "Stand tall, holding a chair or wall for balance.",
    },
    "Standing Scapular Retraction": {
      id: 205,
      audio: "Stand tall, pull your shoulder blades back and down. Hold briefly, then release.",
      screen: "Stand tall, facing the camera.",
    },
    "Standing Scapular Retraction in Abduction": {
      id: 206,
      audio: "Hold your arms slightly out to the sides, pull your shoulder blades back, then release.",
      screen: "Stand tall, facing the camera.",
    },
    "Standing Shoulder Abduction with Self-Anchored Resistance": {
      id: 211,
      audio: "Step on a band, lift your arm sideways to shoulder height, then lower with control.",
      screen: "Stand tall, facing the camera.",
    },
    "Standing Shoulder External Rotation Stretch in Doorway": {
      id: 223,
      audio: "Stand next to a doorway, place your forearm on the frame at 90 degrees, and rotate your body outward to stretch your shoulder.",
      screen: "Stand tall, facing the camera.",
    },
    "Standing Shoulder Single Arm Flexion with Anchored Resistance": {
      id: 210,
      audio: "Hold a resistance band, raise one arm forward to shoulder height, then lower slowly.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Standing Single Leg Circles with Chair": {
      id: 263,
      audio: "Stand tall holding a chair for support. Lift one leg slightly off the floor and draw small controlled circles outward and inward from the hip. Maintain upright posture and steady core engagement.",
      screen: "Stand tall, holding a chair or wall for balance, one foot lifted slightly off the floor.",
    },
    "Standing Terminal Knee Extension at Wall with Ball": {
      id: 191,
      audio: "Stand with a ball behind your knee against the wall. Straighten your leg, pressing into the ball, then release.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Standing Terminal Knee Extension with Resistance": {
      id: 234,
      audio: "Anchor the band behind your knee. Straighten your leg against the resistance, then return slowly.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Standing Wall Ball Circles with Mini Swiss Ball": {
      id: 204,
      audio: "Hold a mini Swiss ball against the wall. Move it in small circles while keeping your shoulder stable.",
      screen: "Stand tall, turn sideways to your left, with your right shoulder closest to the camera.",
    },
    "Superman": {
      id: 65,
      audio: "Lift your arms, chest, and legs off the floor at the same time. Hold briefly, then lower with control.",
      screen: "Lie flat on your stomach, with your left side closest to the camera.",
    },
    "Supine Bridge": {
      id: 64,
      audio: "Press through your feet to lift your hips off the floor until your body forms a straight line from shoulders to knees. Then lower and repeat.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Bridge with Leg Extension": {
      id: 147,
      audio: null,
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Bridge with Mini Swiss Ball Between Knees": {
      id: 180,
      audio: null,
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine double knee to chest": {
      id: 92,
      audio: "Bring both knees up toward your chest, using your hands to gently hold your legs in place. Hold the stretch and breathe.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Double Knee with Knees Bent": {
      id: 96,
      audio: "With your knees bent and feet on the floor, bring both knees toward your chest. Use your hands to hold the position and breathe.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Figure 4 Stretch": {
      id: 126,
      audio: "Cross one ankle over the opposite knee to form a figure 4. Then gently pull the leg that's still on the floor toward your chest and hold the stretch.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Glute Stretch": {
      id: 83,
      audio: "Bring one knee up toward your chest, then cross it over the opposite leg so your ankle rests just above the knee. Hold this position and stretch.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Hamstring Stretch": {
      id: 82,
      audio: "Raise your leg to touch the target, keeping your knee straight. Use your hands to gently hold behind your thigh and stretch.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Hamstring Stretch with Knees Bent": {
      id: 97,
      audio: "With one knee bent and foot on the floor, raise your other leg and gently hold behind the thigh to stretch. Keep the lifted leg as straight as you can.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Hamstring Stretch with Strap": {
      id: 178,
      audio: null,
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine ITB Stretch": {
      id: 240,
      audio: "Lie on your back, cross one leg over the other, and gently pull your knee toward the opposite shoulder. Hold and switch.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Knee Extension Strengthening": {
      id: 235,
      audio: "Lie on your back, tighten your left thigh, and lift your straight leg slightly. Hold briefly, then lower with control.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Knee Flexion": {
      id: 260,
      audio: null,
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Short Arc Quad": {
      id: 241,
      audio: "Lie on your back with a small cushion under your knee. Straighten your leg, lifting your heel, then lower with control.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine single knee to chest": {
      id: 80,
      audio: "Gently pull one knee toward your chest while keeping the other leg straight on the floor. Hold, then switch sides.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Supine Single Knee with Knees Bent": {
      id: 95,
      audio: "With both knees bent and feet on the floor, bring one knee toward your chest using your hands. Hold, then switch sides.",
      screen: "Lie on your back, with your right side closest to the camera.",
    },
    "Toe Touch": {
      id: 115,
      audio: "Stand tall with feet together. Hinge forward from hips and reach toward toes while keeping legs straight. Return to upright position. Avoid rounding back; keep motion smooth and controlled.",
      screen: "Stand tall facing left, feet together, arms at sides.",
    },
    "V Sit Hip Adductor Hamstring Stretch": {
      id: 79,
      audio: "Sit tall with your legs in a V, then walk your hands forward between your legs as far as comfortable. Hold the stretch.",
      screen: "Sit on the floor with your legs in front of you, facing the camera.",
    },
    "Virtual Home Exercise Tutorial": {
      id: 60,
      audio: null,
      screen: "Sit on a chair, facing the camera.",
    },
    "Wall Angels": {
      id: 160,
      audio: "Stand against the wall with elbows bent at 90 degrees, fingers pointing up. Slide your arms up and down while keeping elbows and wrists in contact with the wall.",
      screen: "Stand tall, facing the camera.",
    },
    "Wall Squat": {
      id: 123,
      audio: "Stand upright with back flat against wall, feet slightly forward. Slide down into a squat until thighs are parallel to floor, hold, then press through heels to return to standing. Keep knees over ankles and back flat against wall.",
      screen: "Stand with back against wall, feet shoulder-width apart, feet slightly forward.",
    },
    "Wall Squat Hold with Ball": {
      id: 179,
      audio: "Stand upright with a stability ball behind your back and feet slightly forward. Slowly slide down the wall into a squat position, using the ball for support. Hold briefly, then press through heels to return to standing. Keep core engaged and back straight against the ball.",
      screen: "Stand with your back against the wall, a stability ball placed between your back and the wall.",
    },
    "Wall Squat with Heel Raises": {
      id: 187,
      audio: "Stand with back flat against wall, feet slightly forward. Lower into a squat, hold, then lift both heels off the floor into a heel raise. Lower heels, maintain squat hold, and repeat. Keep back against wall and core engaged.",
      screen: "Stand with back against wall, feet shoulder-width apart, feet slightly forward.",
    },
    "Warrior": {
      id: 142,
      audio: "Begin in an inverted 'U' position. Bring one foot forward between your hands, rotating the back foot outward. Raise your torso and arms overhead into Warrior stance, keeping legs wide and stable. Lower arms back down beside the front foot, step the front leg back to the starting position, and repeat. Keep movements smooth and controlled, maintaining balance and alignment throughout.",
      screen: "Start on hands and feet in an inverted 'U' shape (similar to Downward Dog), hips lifted high, heels pressing gently toward floor.",
    },
  },

  // ── REFERENCE DATA: MYRA_TEXT ────────────────────────────────
  // Verbatim copy of the myra_text DB table — used by the runtime
  // voice agent for both TTS lines and LLM system prompts.
  //
  // Two categories share this block (separated only by name prefix):
  //
  //   audio_*   — user-facing strings spoken by Myra (TTS).
  //                Often contain {name} for personalization.
  //                Newlines (\n) are intentional pacing hints for the
  //                voice synth — preserve them verbatim.
  //
  //   prompt_*  — LLM system prompts that tell the model how to
  //                interpret the user's voice reply at each step.
  //                NEVER pass these to TTS — they are internal agent
  //                instructions and contain JSON schemas, persona
  //                rules, etc. Most pair with an audio_* of the same
  //                stem (e.g. audio_rpe + prompt_rpe).
  //
  // Each entry preserves its DB id for traceability. Whitespace and
  // line endings (mix of \n and \r\n) are kept exactly as in the
  // source — changing them can subtly alter LLM prompt behavior.
  //
  // Lookup helper: getMyraText(name) — defined alongside resolveMessage.
  //
  // Order: alphabetical by text_name, which naturally groups each
  // audio_X with its paired prompt_X.
  myraText: {
    "audio_after_exercise": {
      id: 52,
      value: "Was it because you want to do it later, don't like it, want something different or it felt too hard?",
    },
    "audio_after_exercise_partial": {
      id: 53,
      value: "Nice work! Every set count towards feeling better, come back any time to finish the rest. If you would like to complete the rest another time just say \"Yes, I'll complete it later\" or if you are done with this session say \"No, I am done\".",
    },
    "audio_camera_permission": {
      id: 31,
      value: "{name}, | place your device on a flat, stable surface. When you're ready, just say, 'Myra, start.' If you'd like to see your camera view full screen, just say, 'Myra, expand the camera view.'",
    },
    "audio_Compass_Score": {
      id: 11,
      value: "{name}. | We've got everything we need—thank you for your effort, especially while in pain. Now it's our turn to do the work! I am analyzing your information to calculate your Recovery Compass Score and create your personalized recovery plan. Hang tight—we'll be ready in just a moment.",
    },
    "audio_device": {
      id: 29,
      value: "To get the best results, make sure to place your device on a flat, stable surface. If possible, position it a few feet off the ground for the best results—but don't worry, it's not required. If you're curious about why placement matters, just ask! When you're ready to move on, let me know.",
    },
    "audio_endSessionFlow": {
      id: 48,
      value: "Nice work! Every set counts toward feeling better. Come back anytime to finish the rest. If you'd like to complete the rest another time, just say \"Yes, I'll complete it later\" or if you're done with this session, say \"No, I am done\".",
    },
    "audio_ExerciseCompletedFirstTimeFlow": {
      id: 43,
      value: "How challenging did {exerciseName} feel for you? You can say it was easy, just right, or hard—or even 'kinda' easy or hard if it felt somewhere in between.",
    },
    "audio_first_demo": {
      id: 30,
      value: "Next, I’ll show a demo video of your first movement check. If you want to pause the video, replay the video, skip this video, or see the video full screen, just say something like Myra, pause the video or Myra, replay the video. I’ll take it from there. Let’s get started!",
    },
    "audio_first_test": {
      id: 9,
      value: "Hi there! I'm Myra, your virtual recovery coach. It's great to meet you! I see you've already shared some details about how you're feeling, thank you for that. Together, we'll figure out what's going on and work toward helping you feel better. If you'd like, you can tell me your name so I can make this experience more personal, or feel free to skip if you'd prefer. No pressure at all! And remember, if you have any questions along the way, I'm here for you—just ask.",
    },
    "audio_first_test_have_name": {
      id: 26,
      value: "{name}, | based on what you've shared so far, I've picked a simple movement check to help us understand how your body responds. I'll show you a quick demo video to guide you through it. If you need to pause, replay, skip, or watch it full screen, just say something like, 'Myra, pause the video,' or 'Myra, replay the video.' I'll take care of it. Let's get started!",
    },
    "audio_first_test_have_name_chat": {
      id: 54,
      value: "{name}, | based on what you've shared so far, I've picked a simple movement check to help us understand how your body responds. I'll show you a quick demo video to guide you through it. You can use the video control buttons to pause, replay, or watch it full screen, or press the \"Start Movement Check\" button to skip the video. Let's get started!",
    },
    "audio_first_test_no_name": {
      id: 23,
      value: "I see you've already shared some details about how you're feeling — thank you for that. Together, we'll figure out what's going on and work toward helping you feel better. If you'd like, you can tell me your name so I can make this experience more personal, or feel free to skip if you'd prefer. No pressure at all. And remember, if you have any questions along the way, I'm here for you, just ask.",
    },
    "audio_first_video_play_name": {
      id: 19,
      value: "Thanks, | {name}, | Based on what you've shared so far, I've picked a simple movement check to help us understand how your body responds. I'll show you a quick demo video to guide you through it. If you need to pause, replay, skip, or watch it full screen, just say something like, 'Myra, pause the video,' or 'Myra, replay the video.' I'll take care of it. Let's get started!",
    },
    "audio_first_video_play_skip_name": {
      id: 22,
      value: "No problem, we'll skip the name for now. Based on what you've shared so far, I've picked a simple movement check to help us understand how your body responds. I'll show you a quick demo video to guide you through it. If you need to pause, replay, skip, or watch it full screen, just say something like, 'Myra, pause the video,' or 'Myra, replay the video.' I'll take care of it. Let's get started!",
    },
    "audio_lighting": {
      id: 28,
      value: "It's important to ensure your lighting is just right—think about how you'd set up for a great photo. Avoid sunlight shining directly at the camera for the best results. If you're curious about why lighting matters, just ask! When you're ready to move on, let me know.",
    },
    "audio_notvisible": {
      id: 46,
      value: "It looks like you're still not visible. Take your time to adjust — if you’d like help, just say ‘help.’ Or if you need to pause, just say ‘pause.’ I’ll be right here when you’re ready.",
    },
    "audio_personalized": {
      id: 10,
      value: "Let’s make this all about you! To tailor your plan, I need a little more information about you. Please enter your details, and if you’ve had any prior surgeries or injuries to your spine or hips, or any conditions related to lower back pain, let me know. If there’s anything else you think is important for me to know, feel free to share that too. When you’re done, just say, ‘Myra, I’m ready to continue.’ And if you have any questions about why I’m asking for something, I’m here for you!",
    },
    "audio_popup_step_2": {
      id: 17,
      value: "It's important to ensure your lighting is just right—think about how you’d set up for a great photo. Avoid sunlight shining directly at the camera for the best results. If you’re curious about why lighting matters, just ask! When you’re ready to move on, let me know.",
    },
    "audio_recovery_score": {
      id: 12,
      value: "Here's your Recovery Compass Score, it's your personal guide, uniquely tailored to your journey. It reflects how your movements and responses come together to shape your recovery. Let me know when you're ready, I'll take you through your personalized recovery plan!",
    },
    "audio_rpe": {
      id: 1,
      value: "{name}. | You’re past the first \nmovement check. Now tell me, \nhow did that feel for you? You \ncan say it was easy, just right, or \nhard—or even 'kinda' easy or \nhard if it felt somewhere in \nbetween.",
    },
    "audio_rpe_exercise": {
      id: 50,
      value: "How challenging did exercise feel for you? You can say it was easy, just right, or hard—or even 'kinda' easy or hard if it felt somewhere in between.",
    },
    "audio_second_test": {
      id: 4,
      value: "{name}, | based on how that first movement went, I want to check one more thing. This test looks at movement in a different direction — it helps me confirm what I'm seeing about your pattern. Watch the video, then we'll do it together.",
    },
    "audio_skippedExerciseFlow": {
      id: 39,
      value: "Was it because you want to do it later, don't like it and want something different, or it felt too hard?",
    },
    "audio_symptom_exercise": {
      id: 51,
      value: "How did that movement check affect your pain? Would you say it felt better, felt same or felt worse?",
    },
    "audio_symptom1": {
      id: 2,
      value: "How did that movement check affect your pain? Would you say it felt better, felt same or felt worse?",
    },
    "audio_symptom2": {
      id: 3,
      value: "How did the second movement check feel compared to the first? Did it feel worse or better?",
    },
    "audio_visible": {
      id: 27,
      value: "{name}. | The key is to make sure you can see yourself clearly on the screen—that means we can see you too! Don't worry, if we can't see you properly during the movement check, I'll let you know. If you're curious about how this works, just ask! When you're ready to move on, let me know.",
    },
    "audio_Week_1_Recovery_Plan": {
      id: 13,
      value: "",
    },
    "audio_without_camera_permission": {
      id: 32,
      value: "{name}, | we'll need access to your camera so we can see you and guide you through the movement check. Don't worry — we don't record anything, and all video is processed securely on your device. Once you've granted permission, place your device on a flat, stable surface. When you're ready, just say, 'Myra, start.' If you'd like to see your camera view full screen, just say, 'Myra, expand the camera view.'",
    },
    "prompt_first_test": {
      id: 14,
      value: "User may either provide their name or say they want to skip.\n      - If they say something like \"My name is Ali\", \"I'm Sarah\", \"This is John\", \"It's Mary\", respond ONLY as: {\"action\":\"GIVE_NAME\",\"value\":\"<NAME>\"} where <NAME> is the extracted name.\n      - If they say \"skip\", \"I'll skip\", \"skip it\", \"no name\", \"don't want to\", respond ONLY as: {\"action\":\"SKIP_NAME\",\"value\":true}.\n      - If they say some other text return only {\"action\":\"SKIP_NAME\",\"value\": false}\n      - IMPORTANT: Do not include extra text. Respond with compact JSON only.",
    },
    "prompt_first_test_have_name": {
      id: 25,
      value: "Detect if user wants to skip, pause or replay video. Look for phrases like \"skip\", \"skip video\", \"skip the video\", \"myra skip\", \"skip demo\".\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\n\nAlso look for phrases \"replay\", \"replay video\", \"replay demo\" or \"myra replay\".\nRespond ONLY as {\"action\":\"REPLAY_VIDEO\",\"value\":true} if they want to replay\n\nAlso look for phrases \"pause\", \"pause video\", \"pause demo\" or \"myra pause\".\nRespond ONLY as {\"action\":\"PAUSE_VIDEO\",\"value\":true} if they want to pause\n\nAlso look for phrases \"resume\", \"resume video\", \"continue\", \"continue video\", \"play\", \"play video\".\nRespond ONLY as {\"action\":\"RESUME_VIDEO\",\"value\":true} if they want to resume\n\nAlso look for phrases \"expand\", \"expand video\", \"bigger\", \"bigger video\".\nRespond ONLY as {\"action\":\"EXPAND_VIDEO\",\"value\":true} if they want to resume.\n\nIn not skipping and not replying then answer ONLY as {\"action\":\"CONTINUE_VIDEO\", \"value\": true}.\n\nAlso look for skip phrases like \"next\", \"next video\", \"fast forward\", \"next step\", \"go ahead\", \"forward video\", \"continue without demo\", \"go next\", \"leave demo\".\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\n\nAlso look for start phrases like \"start\", \"start test\" or \"start movement check\" that means video should be skipped:\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to start",
    },
    "prompt_first_test_no_name": {
      id: 24,
      value: "User may either provide their name or say they want to skip.\r\n      - If they say something like \"My name is Ali\", \"I'm Sarah\", \"This is John\", \"It's Mary\", respond ONLY as: {\"action\":\"GIVE_NAME\",\"value\":\"<NAME>\"} where <NAME> is the extracted name.\r\n      - If they say \"skip\", \"I'll skip\", \"skip it\", \"no name\", \"don't want to\", respond ONLY as: {\"action\":\"SKIP_NAME\",\"value\":true}.\r\n      - If they say some other text return only {\"action\":\"SKIP_NAME\",\"value\": false}\r\n      - IMPORTANT: Do not include extra text. Respond with compact JSON only.",
    },
    "prompt_first_video_play_name": {
      id: 20,
      value: "Detect if user wants to skip, pause or replay video. Look for phrases like \"skip\", \"skip video\", \"skip the video\", \"myra skip\", \"skip demo\".\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\n\nAlso look for phrases \"replay\", \"replay video\", \"replay demo\" or \"myra replay\".\nRespond ONLY as {\"action\":\"REPLAY_VIDEO\",\"value\":true} if they want to replay\n\nAlso look for phrases \"pause\", \"pause video\", \"pause demo\" or \"myra pause\".\nRespond ONLY as {\"action\":\"PAUSE_VIDEO\",\"value\":true} if they want to pause\n\nAlso look for phrases \"resume\", \"resume video\", \"continue\", \"continue video\", \"play\", \"play video\".\nRespond ONLY as {\"action\":\"RESUME_VIDEO\",\"value\":true} if they want to resume\n\nAlso look for phrases \"expand\", \"expand video\", \"bigger\", \"bigger video\".\nRespond ONLY as {\"action\":\"EXPAND_VIDEO\",\"value\":true} if they want to resume.\n\nIn not skipping and not replying then answer ONLY as {\"action\":\"CONTINUE_VIDEO\", \"value\": true}.\n\nAlso look for skip phrases like \"next\", \"next video\", \"fast forward\", \"next step\", \"go ahead\", \"forward video\", \"continue without demo\", \"go next\", \"leave demo\".\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip",
    },
    "prompt_first_video_play_skip_name": {
      id: 21,
      value: "Detect if user wants to skip, pause or replay video. Look for phrases like \"skip\", \"skip video\", \"skip the video\", \"myra skip\", \"skip demo\".\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\n\nAlso look for phrases \"replay\", \"replay video\", \"replay demo\" or \"myra replay\".\nRespond ONLY as {\"action\":\"REPLAY_VIDEO\",\"value\":true} if they want to replay\n\nAlso look for phrases \"pause\", \"pause video\", \"pause demo\" or \"myra pause\".\nRespond ONLY as {\"action\":\"PAUSE_VIDEO\",\"value\":true} if they want to pause\n\nAlso look for phrases \"resume\", \"resume video\", \"continue\", \"continue video\", \"play\", \"play video\".\nRespond ONLY as {\"action\":\"RESUME_VIDEO\",\"value\":true} if they want to resume\n\nAlso look for phrases \"expand\", \"expand video\", \"bigger\", \"bigger video\".\nRespond ONLY as {\"action\":\"EXPAND_VIDEO\",\"value\":true} if they want to resume.\n\nIn not skipping and not replying then answer ONLY as {\"action\":\"CONTINUE_VIDEO\", \"value\": true}.\n\nAlso look for skip phrases like \"next\", \"next video\", \"fast forward\", \"next step\", \"go ahead\", \"forward video\", \"continue without demo\", \"go next\", \"leave demo\".\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip",
    },
    "prompt_notvisible": {
      id: 47,
      value: "Detect if user wants help, if user is saying \"help\" or \"Myra help\" or \"I need help\" or similar.\r\nRespond ONLY as {\"action\":\"START_HELP\",\"value\":true} if they want help\r\n\r\nAlso look for phrases \"pause\", \"hold on\", \"wait\", \"stop\", \"Myra pause\" or similar words that indicate user wants to pause.\r\nRespond ONLY as {\"action\":\"START_PAUSE\",\"value\":true} if they want pause",
    },
    "prompt_popup_step_2": {
      id: 18,
      value: "User may say MOVE ON, in which case you will respond ONLY as: {\"action\":\"MOVE_ON\",\"value\": 1}\r\n\r\nOr user may ask general question, in which case you use following description:\r\n\r\n# PERSONA & ROLE\r\nYou are Myra, a supportive, clear, and friendly AI recovery guide. Your tone is always simple, reassuring, and non-technical. Your goal is to help a user understand the best practices for setting up their camera for their exercises.\r\n\r\n# TASK\r\nYour task is to answer user questions about the computer vision setup. You MUST keep your answers short (1-2 sentences) and directly address the user's question using the knowledge provided below. Your answer should be relevant to the user's current screen.\r\n\r\n# CURRENT CONTEXT\r\nYou are currently helping the user on a screen with the following topic: **${screenContext}**. You should prioritize answers from this section of the knowledge base, but can answer about other topics if the user asks directly.\r\n\r\n# KNOWLEDGE BASE\r\n\r\n* **For questions about 'visibility' (the user being visible):** \"Being fully visible on screen allows me to see your movements clearly. This helps me give you the best feedback and ensure you're performing the exercises safely and effectively.\"\r\n* **For questions about 'lighting':** \"Good, even lighting is important so the camera can see you clearly. Avoiding bright lights shining into the camera or directly behind you helps me track your movements much more accurately.\"\r\n* **For questions about 'stability' (device placement):** \"Placing your device on a stable, flat surface ensures the camera's view doesn't shake. A steady view is key for me to track your exercises without any interruptions.\"\r\n* **For general questions (\"Why does this matter?\"):** \"A good setup helps me see you clearly, which means I can give you better feedback on your form and help you get the most out of every exercise.\"\r\n* **If you don't know the answer:** \"That's a great question. The most important thing is to follow these steps so I can see you clearly and help you through your exercises safely.\"\r\n\r\n# INSTRUCTIONS\r\n1.  You will be given the full conversation history. Use it to understand the context of the user's most recent question.\r\n2.  Your answer must be based **only** on the Knowledge Base above.\r\n3.  Keep your responses concise and conversational.\r\n\r\nin this case respon ONLY in following format: {\"action\":\"ANSWER_QUESTION\",\"value\": \"Placing your device...\"}\r\nWhere value is actual response to their question.\r\nMake sure quotes are well adapted to JSON standard.",
    },
    "prompt_recovery_score": {
      id: 15,
      value: "Detect if the user indicates readiness to proceed to recovery plan view.\r\n      Look for phrases like: \"ok\", \"got it\", \"move on\", \"next\", \"next step\", \"view\", \"view my recovery plan\", \"proceed\", \"continue\", or similar expressions.\r\n      Respond ONLY as: {\"action\":\"READY_TO_PROCEED\",\"value\":true}\r\n      If the user is not ready, respond as: {\"action\":\"NOT_READY\",\"value\":false}",
    },
    "prompt_rpe": {
      id: 5,
      value: "User may either say a numeric rating (0-10) or use descriptive words.\r\n      Map words to numbers as follows:\r\n        - \"easy\" => 1\r\n        - \"somewhere between easy and just right\" (or similar) => 3\r\n        - \"just right\" => 5\r\n        - \"hard\" => 8\r\n      If the user gives a number (e.g. \"7\", \"seven\", \"it was an 8\"), use that number.\r\n      Respond ONLY as: {\"action\":\"SELECT_RPE\",\"value\":NUMBER} where NUMBER is an integer between 0 and 10.",
    },
    "prompt_second_test": {
      id: 8,
      value: "Detect if user wants to skip, pause or replay video. Look for phrases like \"skip\", \"skip video\", \"skip the video\", \"myra skip\", \"skip demo\".\r\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\r\n\r\nAlso look for phrases \"replay\", \"replay video\", \"replay demo\" or \"myra replay\".\r\nRespond ONLY as {\"action\":\"REPLAY_VIDEO\",\"value\":true} if they want to replay\r\n\r\nAlso look for phrases \"pause\", \"pause video\", \"pause demo\" or \"myra pause\".\r\nRespond ONLY as {\"action\":\"PAUSE_VIDEO\",\"value\":true} if they want to pause\r\n\r\nAlso look for phrases \"resume\", \"resume video\", \"continue\", \"continue video\", \"play\", \"play video\".\r\nRespond ONLY as {\"action\":\"RESUME_VIDEO\",\"value\":true} if they want to resume\r\n\r\nAlso look for phrases \"expand\", \"expand video\", \"bigger\", \"bigger video\".\r\nRespond ONLY as {\"action\":\"EXPAND_VIDEO\",\"value\":true} if they want to resume.\r\n\r\nIn not skipping and not replying then answer ONLY as {\"action\":\"CONTINUE_VIDEO\", \"value\": true}.\r\n\r\nAlso look for skip phrases like \"next\", \"next video\", \"fast forward\", \"next step\", \"go ahead\", \"forward video\", \"continue without demo\", \"go next\", \"leave demo\":\r\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\r\n\r\nAlso look for start phrases like \"start\", \"start test\" or \"start movement check\" that means video should be skipped:\r\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to start",
    },
    "prompt_skippedExerciseFlow": {
      id: 40,
      value: "Options: LATER, DONT_LIKE, HARD.\r\n\r\nUser might say \"Do it later\" or \"Later do\" or \"Later\" - Respond ONLY as {\"action\":\"SELECT_ANSWER\",\"value\":\"LATER\"}.\r\n\r\nUser might say \"Don't like\", \"Not good\", \"Bad\" - - Respond ONLY as {\"action\":\"SELECT_ANSWER\",\"value\":\"DONT_LIKE\"}.\r\n\r\nUser might say \"Too Hard\", \"very Hard\", \"Hard\" - - Respond ONLY as {\"action\":\"SELECT_ANSWER\",\"value\":\"HARD\"}.",
    },
    "prompt_start_silent_demo": {
      id: 42,
      value: "Detect if user wants to skip, pause or replay video. Look for phrases like \"skip\", \"skip video\", \"skip the video\", \"myra skip\", \"skip demo\".\r\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\r\n\r\nAlso look for phrases \"replay\", \"replay video\", \"replay demo\" or \"myra replay\".\r\nRespond ONLY as {\"action\":\"REPLAY_VIDEO\",\"value\":true} if they want to replay\r\n\r\nAlso look for phrases \"pause\", \"pause video\", \"pause demo\" or \"myra pause\".\r\nRespond ONLY as {\"action\":\"PAUSE_VIDEO\",\"value\":true} if they want to pause\r\n\r\nAlso look for phrases \"resume\", \"resume video\", \"continue\", \"continue video\", \"play\", \"play video\".\r\nRespond ONLY as {\"action\":\"RESUME_VIDEO\",\"value\":true} if they want to resume\r\n\r\nAlso look for phrases \"expand\", \"expand video\", \"bigger\", \"bigger video\".\r\nRespond ONLY as {\"action\":\"EXPAND_VIDEO\",\"value\":true} if they want to resume.\r\n\r\nIn not skipping and not replying then answer ONLY as {\"action\":\"CONTINUE_VIDEO\", \"value\": true}.\r\n\r\nAlso look for skip phrases like \"next\", \"next video\", \"fast forward\", \"next step\", \"go ahead\", \"forward video\", \"continue without demo\", \"go next\", \"leave demo\".\r\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to skip\r\n\r\nAlso look for start phrases like \"start\", \"start test\" or \"start movement check\" that means video should be skipped:\r\nRespond ONLY as {\"action\":\"SKIP_VIDEO\",\"value\":true} if they want to start",
    },
    "prompt_symptom1": {
      id: 6,
      value: "User may say \"better\", \"felt the same\", \"same\", \"no change\", or \"worse\".\r\n      Map as: better ? IMPROVED; same/no change ? TOLERABLE; worse ? WORSENED.\r\n      Respond ONLY as {\"action\":\"SELECT_SYMPTOM\",\"value\":\"IMPROVED\"|\"TOLERABLE\"|\"WORSENED\"}.",
    },
    "prompt_symptom2": {
      id: 7,
      value: "Options: BETTER, WORSENED. Respond ONLY as {\"action\":\"SELECT_SYMPTOM\",\"value\":\"CATEGORY\"}.",
    },
    "prompt_Week_1_Recovery_Plan": {
      id: 16,
      value: "Detect if the user indicates they want to sign up or start their recovery plan.\n      Look for phrases like: \"sign up\", \"let's sign up\", \"start plan\", \"start my recovery plan\", \"get started\", or similar.\n      Respond ONLY as: {\"action\":\"SIGN_UP\",\"value\":true}\n      If no signup/start intent is detected, respond ONLY as: {\"action\":\"NO_ACTION\",\"value\":false}",
    },
  },
};

export const INPUT_PLACEHOLDERS = {
  // Landing/Intro - user can ask anything or describe their pain
  intro: "Ask me anything or share what's going on",

  // Problem Area - user can type which body part hurts
  problem_area: "Type where it hurts (e.g., 'Lower Back', 'Hip', 'Thigh', 'Knee')...",

  // Pain Location - user describes specific zones or confirms selections
  pain_location: "Describe where you feel it...",

  // Safety Screening - user lists conditions or symptoms
  safety_screening: "Share conditions or symptoms, or reply none",

  // CPG Questions - user describes their pain character
  cpg_questions: "Describe how your pain feels...",

  // Subtype Questioning - user describes what makes pain worse
  subtype_questioning: "Describe what makes it worse...",

  // Self Assessment Q2 - yes/no answer about relief
  self_assessment: "Type 'yes' or 'no'..",

  recovery_plan_reveal: "Ask me anything, or tap to continue...",

  // NPRS Symptoms - numeric pain level
  nprs_symptoms: "Type a number from 0 to 10",

  // Test 1 Demo - user can ask questions about the movement
  test1_demo: "Ask me anything about the movement check, or tap to start...",

  // Mode Selection - user picks communication mode
  mode_selection: "Tap an option above to choose how to communicate...",

  // Camera Permission - user makes camera choice
  camera_permission: "Tap an option above to continue...",

  // Movement Check 1 - AR is active, limited input
  movement_test_1: "Follow Myra's voice instructions...",

  movement_q1: "Describe it however feels natural...",

  movement_q2: "Describe it however feels natural...",

  // Self Report ROM - user picks movement comparison
  self_report_rom: "Tap an option above to describe your movements...",

  // Self Report Tempo - user picks speed comparison
  self_report_tempo: "Tap an option above to describe your speed...",

  // RPE - user rates difficulty
  rpe: "Tap how challenging it felt, or type easy , kinda easy, just right, kinda hard, or hard...",

  // Feel Affect - user describes symptom response
  feel_affect: "Tap how your symptoms felt, or describe the change...",

  // Test 2 Demo - second movement check intro
  test2_demo: "Ask me anything about this movement, or tap to start...",

  // Movement Check 2 - AR is active
  movement_test_2: "Follow Myra's voice instructions...",

  // Feedback 2 - comparison between tests
  feedback2: "Tap an option above to compare the movements...",

  // Personalize - user fills form or asks why
  personalize: "Fill in the fields above, or ask me anything...",

  // Recovery Score - user can ask about score
  recovery_score: "Ask me about your score, or tap to continue...",

  // Plan - user can ask about exercises
  plan: "Ask Myra anything, or type 'continue'...",

  // Hard Stop - user can ask questions
  hard_stop: "Ask me any questions about next steps...",

  // Default fallback
  default: "Ask me anything or share what's going on"
};

// ════════════════════════════════════════════════════════════════
// LEGACY TEMPLATE VARIABLE RESOLVER
// Kept for any non-Myra call sites that import it. New code should
// use getMessage() from resolver.js instead.
// ════════════════════════════════════════════════════════════════

export function resolveMessage(template, vars = {}) {
  return Object.entries(vars).reduce(
    (msg, [key, val]) => msg.replaceAll(`{${key}}`, val),
    template
  );
}


// ════════════════════════════════════════════════════════════════
// EXERCISE AUDIO LOOKUP
// Returns { id, audio, screen } for a given exercise name, or null if
// the name isn't in the table. Names must match exactly — they're
// sourced from the exercises table's display name column.
//
// Usage:
//   const ex = getExerciseAudio("Squat");
//   if (ex?.audio) doSay(ex.audio);
//   showOnScreen(ex?.screen);
// ════════════════════════════════════════════════════════════════

export function getExerciseAudio(name) {
  if (!name || typeof name !== "string") return null;
  return MYRA_MESSAGES.exercises[name] || null;
}


// ════════════════════════════════════════════════════════════════
// MYRA_TEXT LOOKUP
// Returns the text_value string for a given myra_text row name, or
// null if the name isn't in the table. Convenience over the wrapping
// { id, value } shape — most callers only want the string.
//
// Two flavors share the table; both are fetched the same way:
//   - audio_*  → user-facing TTS string (often with {name} template)
//   - prompt_* → LLM system prompt for parsing voice replies
//
// Usage:
//   const text = getMyraText("audio_rpe");
//   doSay(resolveMessage(text, { name: userName }));
//
//   const prompt = getMyraText("prompt_rpe");
//   sendToLLM(prompt, userTranscript);
// ════════════════════════════════════════════════════════════════

export function getMyraText(name) {
  if (!name || typeof name !== "string") return null;
  const entry = MYRA_MESSAGES.myraText[name];
  return entry ? entry.value : null;
}